// src/components/desk/LineageModal.jsx — the reshare lineage modal and its satellites:
// CapturedByToken, lineageFieldRow, FillFactPill, editUrlFor (+EDIT_URL_TPL/EDIT_ID_RE),
// QuestionBox, the LOCATION-card helpers (locDistM/locDistanceLabel/locName/getNearbyForPin,
// LOC_* radii) and NearbyLocationsCard, and ListingLineageModal itself — extracted VERBATIM
// from IntakeTab.jsx (DESK_MODE_REWRITE_PLAN §3 decomposition, behavior-preserving).
// locDistM and NEAR_SEARCH_RADIUS_M are also consumed by IntakeTab's near-search filtering,
// so they are exported alongside the modal.

import { useEffect, useMemo, useRef, useState } from 'react'
import { t, tLog, getLanguage } from '../../lib/i18n.js'
import { Icon, IconText } from '../Icon.jsx'
import { card, btn, Badge, BtnLabel, COUNTER, Money, ROW, ROW_LEFT, ROW_VAL, SECTION_LABEL, pill, useMoney, useCanHover, Modal, timeAgo } from './primitives.jsx'
// locDistM + NEAR_SEARCH_RADIUS_M now live in the pure feed module (../../lib/deskFeed.js) so
// `node --test` can run the feed pipeline that uses them; this file can't be loaded by Node.
// Still exported from here (bottom export block) — no import site changed.
import { locDistM, LOC_RADIUS_BY_TYPE, NEAR_SEARCH_RADIUS_M, LOC_TYPE_ORDER, LOC_TYPE_ICON, LOC_MAX_PER_TYPE, LOC_FETCH_RADIUS_M, pickNearbyChips } from '../../lib/deskFeed.js'
import { comparableAmount, CONVERTIBLE } from '../../lib/money.js'
import { ListingGallery, addressForDisplay, osmTileUrl } from './ListingGallery.jsx'
import { ImageSetTabs, ImageSetTotal, usePropertyImageSets } from './ImageSetTabs.jsx'
import { intentLabel, kindLabel } from '../../lib/labels.js'
import { AXIS, axisConflicts, axisOf, isExclusiveAxis } from '../../lib/featureAxes.js'
import { attrLabel, sourceKindLabel } from '../../lib/labels.js'
import AiMark, { aiMarkFor } from './AiMark.jsx'
import CadastralCard from './CadastralCard.jsx'
import AreaCard from './AreaCard.jsx'
import { ContactPill } from './ContactPill.jsx'
import { CooperationList, ShareChip, shareableLocation, ShelfToggles, VIEWING_DOT, coopIntentOf, coopMeetingSeed, representingSeg, viewingSchedAt, viewingSchedText } from './CooperationChips.jsx'
import { DiscussWithTeamButton } from './ChatInbox.jsx'
import { DealEditor } from './DealEditor.jsx'
import { DealClosePanel } from './DealCloseModal.jsx'
import { MarketReportPanel } from './MarketReportPanel.jsx'
import { AdDescriptionCard, AddressDiffLine, FactsDiffLine, PlatformPill, adDescriptionOf, formatListingSummary, listingAgeBadge } from './HistoryCards.jsx'
import { evLabel, evTime, usePropertyEvents } from './PropertyTimeline.jsx'
// The hover tooltips on this card print full instants (last seen, listed at, lifecycle
// observed). They were `new Date(x).toLocaleString()` — the DEVICE's zone — while the
// timeline column beside them is Tbilisi, so a tooltip and the row it belongs to could
// name different hours for the same fact. ⚠️ Only the ZERO-ARG form was converted: this
// file also uses `toLocaleString('en-US')` for MONEY, which is a number format and has
// nothing to do with time zones.
import { fmtTbilisi } from '../../lib/tbilisiTime.js'
import { buildEpisodes, isVisible } from '../../lib/propertyEpisodes.js'
import { stabilizeEvents } from '../../lib/stableChanges.js'
import { myVersionOf, ownValuesOf, hasMyVersion, myVersionImageSet, ownFactSummary } from '../../lib/myVersion.js'
import { now } from '../../lib/clock.js'
// CopyListingLink dropped from this import 2026-07-25 — the modal's share moved to the
// footer and now renders the shared ShareMenu (client link + agent link) instead.
import { AccessRequestPanel, ContactReveal, listingCompleteness } from './MatchSuggestions.jsx'
import { MatchMatrix } from './MatchMatrix.jsx'
import { ShareMenu } from './ShareMenu.jsx'
import { AgentPicker } from './AgentPicker.jsx'
import { NegotiationsPanel } from './NegotiationModal.jsx'
import { APT_CLASS, FeatureDefPanel, InfoDot, PROP_STATE, featureDefOf } from './ManualClientModal.jsx'
import { AvailabilityFacet, AvailabilityLine, PrivateNotesPanel, PublicPageFacet, RentalTermsByAgent, termsModeFor } from './ManualPropertyModal.jsx'
import { ContactOptOutPanel } from './ContactOptOutPanel.jsx'
import { AdRemoverPanel } from './AdRemoverPanel.jsx'
import { AdminCommentsPanel } from './AdminCommentsPanel.jsx'
import { PLATFORM_LABEL, platformKey } from '../ListingsTab.jsx'
import {
  getFbShareStats, getFeatures, getListingLineage, getMyPropertyVersion, getPropertyComplex, getPropertyContacts, getPropertyShares,
  getPropertyHistorySummary, getPropertyViewings, listCooperations, logListingView,
  setPropertyFeature, voteDistrict, getAuthorRole,
  getClaimConflicts, resolveClaimConflict, getPropertyDistinctiveness, sharePropertyWith, getPropertyRepresentatives, getStreetQuestion, answerStreetQuestion, requestAdTakedown, getBlockedAds } from '../../db/core.js'
import { askRemovalReason } from './RemovalReason.jsx'
import DistinctNote, { pickDistinct } from './DistinctNote.jsx'

import { nearbyLocations, getPropertyQuestionVotes, castPropertyQuestionVote } from '../../db/locations.js'
import { keepInView } from '../../lib/keepInView.js'

// ─── Reshare lineage modal — a reshare's own facts, gaps filled from its origin ────────────
// Clicking a reshare (Listings pill) opens THIS instead of a bare window.open: the agent's own
// posting (price, status, etc.) always wins field-by-field; only what they didn't set falls
// back to the origin ad (find_reshare_origin, migration 0041). ✎ marks a field the agent
// actually overrode (differs from the origin), not just one that happens to match.
// 📥 who CAPTURED this posting into our DB (raw_listing.scraped_by, server-shipped
// 2026-07-13) — a different fact from the platform ad's author. Several agents may
// have scraped the same posting over time; all are listed. Old rows predate the
// stamp and carry nothing — the token just doesn't render (honest, never guessed).
function CapturedByToken({ row }) {
  const [open, setOpen] = useState(false)
  const names = row?.captured_by || []
  if (!names.length) return null
  if (names.length <= 2) return (
    <span className={`${COUNTER} ml-1`}
      title={t('desk_lineage_captured_by_hint')}>
      <Icon name="download" /> {names.join(', ')}
    </span>
  )
  // §11 (designer round 4): lists never live in tooltips — 3+ names collapse to a count
  // whose tap opens a real popover (the origin card's name-dump was the overflow complaint).
  return (
    <span className="relative ml-1 inline-flex align-middle">
      <button type="button" onClick={e => { e.stopPropagation(); setOpen(o => !o) }}
        className={`${COUNTER} hover:text-foreground cursor-pointer`}
        title={t('desk_lineage_captured_by_hint')}>
        <Icon name="download" /> {tLog('desk_lineage_captured_by_n', { n: names.length })}
      </button>
      {open && (
        <span ref={keepInView} className="absolute z-20 top-full left-0 mt-1 flex flex-col gap-0.5 p-2 rounded-md border border-border bg-popover shadow-lg w-max max-w-[14rem] text-xs text-foreground"
          onClick={e => e.stopPropagation()}>
          {names.map(n => <span key={n}>{n}</span>)}
        </span>
      )}
    </span>
  )
}
// §11: fact disagreements as ONE explicit sentence — "Facts differ (2) — rooms 1 → 2 ·
// area 30 → 34მ²" — instead of independently-lit pills (three colored tokens are weaker
// than one sentence). When nothing differs the plain facts line renders as before.
function FactsDifferSentence({ m, refM }) {
  if (!m || !refM) return <FactsDiffLine m={m} refM={refM} />
  const pairs = []
  const add = (label, own, ref, unit = '') => {
    if (own != null && ref != null && own !== ref) pairs.push(`${label} ${ref}${unit} → ${own}${unit}`)
  }
  add(t('desk_fact_rooms'), m.rooms, refM.rooms)
  add(t('desk_fact_area'), m.area_sqm, refM.area_sqm, 'მ²')
  add(t('desk_fact_floor'), m.floor, refM.floor)
  add(t('desk_fact_condition'), m.condition, refM.condition)
  if (!pairs.length) return <FactsDiffLine m={m} refM={refM} />
  return (
    <span className="block text-xs">
      <b className="text-amber-600 dark:text-amber-400 font-semibold">{tLog('desk_lineage_facts_differ', { n: pairs.length })}</b>
      <span className="text-muted-foreground"> — {pairs.join(' · ')}</span>
    </span>
  )
}
function lineageFieldRow(label, value, overridden, info = null) {
  if (value == null || value === '') return null
  return (
    <div className="flex items-center justify-between text-sm py-1 border-b border-border/40 last:border-0">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium flex items-center gap-1">
        {value}
        {info /* ⓘ definition dot (0162) — appended by call sites whose value has one */}
        {overridden && <span className="text-xs text-amber-600 dark:text-amber-400" title={t('desk_contact_changed_from_origin')}><IconText>{t('desk_contact_yours_badge')}</IconText></span>}
      </span>
    </div>
  )
}
// Absent-fact row (2026-07-21, owner request): where lineageFieldRow hides an empty value,
// a rep agent instead sees the label + a "? fill in" pill — same sky styling AND same
// answer-in-place popover as the MATCHING CLIENTS gap chips, because it IS the same idea:
// a knowable fact nobody recorded yet. Picking an option writes ONE feature flag through
// the same evented door the match-panel pills use (setPropertyFeature → commit_feature,
// B42) — never listing/save, which would mint a manual listing row per click.
// ⚠️ Same non-exclusivity as the property editor's ladder chips: picking class_4 after
// someone recorded class_3 ADDS a flag, it doesn't retract the old one — features are
// append-only observations. If that ever needs fixing, fix it in commit_feature for both
// doors at once, not here.
// CONFLICT affordance (designer board §9, 2026-07-24). Two scrapes disagreeing on a
// mutually-exclusive axis used to render as a JOIN — "ძველი აშენებული · ახალი აშენებული" — a
// confident-looking sentence that cannot be true. The row now shows ONE value (see
// axisConflicts for the rule) plus this amber flag; tapping it opens the two readings with
// their provenance and a one-tap override.
//
// ⚠️ Rows are labelled by source KIND, never by portal: property_feature is keyed
// (property, feature, source_kind) and keeps no link to the raw_listing it was projected
// from, so "which portal said it" is not answerable from stored data. The count is the number
// of disagreeing RECORDED values — it never claims to be every source that looked.
// ⚠️ The deferred pieces (portal chip, an "N sources said something we don't record" footer
// off unknownTags) are NOT missing by accident: unknownTags is built during ingest and never
// persisted, so the footer would have nothing to read. Board carries both as ⏳.
function ConflictFlag({ conflict, labelOf, propertyId, agentId, onResolved }) {
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const [failed, setFailed] = useState(false)
  useEffect(() => {
    if (!open) return
    const onKey = e => { if (e.key === 'Escape') { e.stopPropagation(); setOpen(false) } }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  }, [open])
  // One row per distinct VALUE (a value seen by two scrapes is still one reading), each
  // carrying its most recent sighting — that timestamp is what the agent judges freshness by.
  const readings = []
  for (const r of conflict.candidates) {
    const hit = readings.find(x => x.feature_key === r.feature_key)
    if (!hit) readings.push({ ...r })
    else if (new Date(r.last_seen_at || 0) > new Date(hit.last_seen_at || 0)) Object.assign(hit, r)
  }
  async function confirm(code) {
    setBusy(true); setFailed(false)
    try {
      // Same evented door as the fill-in pill (setPropertyFeature → commit_feature): the
      // override is an agent-authored OBSERVATION, not an edit. It cannot retract the losing
      // scrape — append-only — which is exactly why axisConflicts treats a human row as the
      // decision rather than counting values.
      await setPropertyFeature(propertyId, code, true, agentId)
      setOpen(false); onResolved(code)
    } catch (e) { console.error('[fact-conflict]', e.message); setFailed(true) }
    finally { setBusy(false) }
  }
  return (
    <span className="relative inline-flex">
      <button type="button" onClick={() => setOpen(o => !o)}
        title={t('desk_conflict_title')}
        className={pill('warn', { door: true })}>
        <IconText>{tLog('desk_conflict_flag', { n: readings.length })}</IconText>
        {failed && <span className="text-destructive"> ⚠</span>}
      </button>
      {open && (
        <span ref={keepInView} className="absolute z-20 top-full right-0 mt-1 w-max max-w-[20rem] rounded-md border border-border bg-popover shadow-lg p-2 text-left">
          <span className="block text-xs font-semibold mb-1.5">{t('desk_conflict_title')}</span>
          {readings.map(r => (
            <span key={r.feature_key} className="flex items-center gap-2 py-0.5 text-xs">
              {/* ✦ AiMark (designer spec 2026-08-19): in THIS popover the agent is choosing
                  between readings, so "who analyzed it + the verbatim evidence" is decision
                  fuel, not decoration. aiMarkFor silences itself when a human row exists. */}
              <AiMark mark={aiMarkFor(r, readings)} />
              <span className="text-xs text-muted-foreground shrink-0">{sourceKindLabel(r.source_kind)}</span>
              <span className="font-medium truncate">{labelOf(r.feature_key)}</span>
              <span className="text-xs text-muted-foreground shrink-0">{r.last_seen_at ? timeAgo(r.last_seen_at) : ''}</span>
              <button type="button" disabled={busy} onClick={() => confirm(r.feature_key)}
                className={pill('none', { extra: 'ml-auto bg-primary text-primary-foreground border-primary font-medium hover:bg-primary/90 disabled:opacity-50 cursor-pointer' })}>
                {t('desk_conflict_confirm')}
              </button>
            </span>
          ))}
        </span>
      )}
    </span>
  )
}
function FillFactPill({ label, options, propertyId, agentId, onRecorded }) {
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const [failed, setFailed] = useState(false)
  // §9 fill-in 3-step (designer board 2026-07-23): Esc cancels the inline picker — the
  // second step must be escapable without saving, same as any inline input.
  useEffect(() => {
    if (!open) return
    const onKey = e => { if (e.key === 'Escape') { e.stopPropagation(); setOpen(false) } }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  }, [open])
  async function pick(o) {
    setBusy(true); setFailed(false)
    try {
      await setPropertyFeature(propertyId, o.key, true, agentId)
      setOpen(false); onRecorded(o)
    } catch (e) { console.error('[fill-fact]', e.message); setFailed(true) }
    finally { setBusy(false) }
  }
  return (
    <div className="flex items-center justify-between text-sm py-1 border-b border-border/40 last:border-0">
      <span className="text-muted-foreground">{label}</span>
      <span className="relative inline-flex">
        <button type="button" onClick={() => setOpen(o => !o)}
          className={pill('info', { door: true })}>
          ? {t('desk_fact_fill_pill')}{failed && <span className="text-destructive"> ⚠</span>}
        </button>
        {open && (
          <span ref={keepInView} className="absolute z-20 top-full right-0 mt-1 flex flex-wrap items-center gap-1 p-1.5 rounded-md border border-border bg-popover shadow-lg w-max max-w-[18rem]">
            {options.map(o => (
              <button key={o.key} type="button" disabled={busy} onClick={() => pick(o)}
                className={pill('neutral', { door: true, extra: 'disabled:opacity-50' })}>
                {o.label}
              </button>
            ))}
          </span>
        )}
      </span>
    </div>
  )
}
// logContactAction / ROLE_LABEL_KEYS / ContactRow / ContactPill — moved to ./desk/ContactPill.jsx (plan §3 decomposition).

// The agent's own EDIT page for a posting they published, per platform. The listing id is the
// SAME one that appears in the public (Open) source_url, so we lift it from there rather than
// depending on source_listing_id being carried in every response shape. The ID-extraction
// regexes mirror server-new-libs/lib/url_patterns.json `extract` (the authoritative dedup
// patterns) — kept in sync by hand: the scheduler doesn't fetch that file (only the extension
// does), and three static templates aren't worth wiring a server round-trip for.
const EDIT_URL_TPL = {
  myhome: id => `https://statements.myhome.ge/ka/statement/edit/${id}?referrer=myhome`,
  ss:     id => `https://home.ss.ge/ka/udzravi-qoneba/edit/${id}`,
  korter: id => `https://korter.ge/property/published/edit/${id}`,
}
const EDIT_ID_RE = {
  myhome: /myhome\.ge\/(?:pr|udzravi-qoneba)\/(\d{5,})(?:[/?#]|$)/i,
  korter: /korter\.ge\/[^?#]*[-/](\d{4,})(?:[/?#]|$)/i,
  ss:     /ss\.ge\/[^?#]*[-/](\d{4,})(?:[/?#]|$)/i,
}
function editUrlFor(source, sourceUrl) {
  const key = platformKey(source)
  if (!key || !EDIT_URL_TPL[key] || !sourceUrl) return null
  const m = String(sourceUrl).match(EDIT_ID_RE[key])
  return m ? EDIT_URL_TPL[key](m[1]) : null
}

// PlatformPill — moved to ./desk/HistoryCards.jsx (plan §3 decomposition).

// ─── QuestionBox — the generic "ask the humans" pattern (2026-07-16) ───────────
// Systemizes the 0101 district-dispute box: same yellow chrome, kind-specific
// answers, plus an ALWAYS-present "don't know" skip (a forced binary makes agents
// guess just to clear the box — a skip hides the question for THIS agent only and
// keeps it live for the next viewer). Votes are append-only rows in
// property_question_votes (scheduler DB); verdicts are computed per kind by the
// consumer. The district box predates this component and keeps its own JSX for
// now — unify when it next needs touching, not before.
function QuestionBox({ title, question, options, myAnswer, tally, busy, onAnswer }) {
  return (
    <div className="rounded-lg border border-yellow-500/40 bg-yellow-500/10 px-3 py-2 text-sm space-y-2 mb-2">
      <p className="text-xs font-semibold"><Icon name="alert-triangle" /> {title}</p>
      <p>{question}</p>
      <div className="flex items-center gap-2 flex-wrap">
        {options.map(o => (
          <button key={o.value} disabled={busy} onClick={() => onAnswer(o.value)}
            className={`rounded-md border px-3 py-1 text-sm ${myAnswer === o.value ? 'border-primary bg-primary/20 font-semibold' : 'border-border hover:bg-muted'}`}>
            {o.label}
          </button>
        ))}
        <button disabled={busy} onClick={() => onAnswer('skip')}
          className="rounded-md px-2 py-1 text-xs text-muted-foreground hover:bg-muted">
          {t('desk_q_skip')}
        </button>
        {tally && <span className="text-xs text-muted-foreground">{tally}</span>}
      </div>
    </div>
  )
}

// ─── StreetQuestion — a street no witness forces (owner 2026-09-21, core 1723/1724) ─────────────
// "If we don't yet know, leave it ambiguous and make agents ask the user." The row lists the candidate
// streets; one tap links the street (a person who knows the flat outranks every automatic rule), and
// "don't know" hides it for this viewer only. It never renders unless a question is open, and it
// yields to the district dispute (one question at a time modal-wide).
function StreetQuestion({ propertyId, agentId, suppress }) {
  const [q, setQ] = useState(null)
  const [busy, setBusy] = useState(false)
  const [done, setDone] = useState(null)
  useEffect(() => {
    setQ(null); setDone(null)
    if (!propertyId) return
    const ac = new AbortController()
    let alive = true
    getStreetQuestion(propertyId, { signal: ac.signal })
      .then(r => { if (alive) setQ(r) })
      .catch(e => { if (alive && e?.name !== 'AbortError') console.error('[street-question]', e.message) })
    return () => { alive = false; ac.abort() }
  }, [propertyId])
  if (suppress || !q) return null
  if (done === 'answered') return <p className="text-xs text-muted-foreground mb-2">{t('street_q_thanks')}</p>
  if (done === 'skip') return null
  async function answer(choice) {
    setBusy(true)
    try {
      const r = await answerStreetQuestion(propertyId, choice, agentId)
      setDone(choice === 'skip' ? 'skip' : (r && r.ok ? 'answered' : null))
    } catch (e) { console.error('[street-question]', e.message) }
    setBusy(false)
  }
  return (
    <QuestionBox title={t('street_q_title')} question={q.question_ka}
      options={q.candidates.map(c => ({ value: c.id, label: c.district ? `${c.name_ka} (${c.district})` : c.name_ka }))}
      busy={busy} onAnswer={answer} />
  )
}

// ─── LOCATION card (2026-07-16 design, README ## Changelog has the spec trail) ──
// Nearby amenities with straight-line distances — phone-pitch material ("450m from
// Delisi metro"). Meters ONLY, never walking minutes: Tbilisi topography makes
// straight-line-to-minutes conversions a liability (a 300m line across the Mtkvari
// or up the Nutsubidze plateau is not a 4-minute walk); agents translate meters
// themselves. Chips are capped at 2 per type with NO "show more" — the modal is
// long enough, and the two nearest answer every buyer question.
// ⚠️ TWO TIERS OF PROXIMITY, deliberately different numbers (owner 2026-07-24) — do NOT
// "harmonize" this with NEAR_SEARCH_RADIUS_M below. The owner's vocabulary:
//   • NEAR   = bus-takable. THIS map — the context a card SHOWS. "metro 1.2km" is worth knowing:
//     you'd take a bus to it. Cutoff stays wide.
//   • NEARER = walkable. NEAR_SEARCH_RADIUS_M — what a FILTER means. Metro 300m ≈ 4 minutes.
//     Filtering at 1500m returned flats nobody would call metro-adjacent, so it meant nothing.
// Same POI, two truths: show it far, match it near.
// LOC_RADIUS_BY_TYPE moved to lib/deskFeed.js (B220) so the requirement near-points
// control shares the exact values without an import cycle. Imported back below.
// TWO maps, split by DESTINATION (2026-07-20 sweep): the icon map is for the screen;
// the emoji map is for OUTBOUND TEXT (copyPitch → WhatsApp/Viber), where an SVG has no
// meaning — a client's phone must receive real characters. Keep them key-aligned.
const LOC_TYPE_EMOJI = { metro: '🚇', park: '🌳', square: '⛲', mall: '🛍️', school: '🏫', landmark: '🏛️' }
const COMPLEX_ASK_RADIUS_M = 150         // membership question window ("in", never "near")
const COMPLEX_SURE_RADIUS_M = 40         // pin essentially ON the complex → stronger question phrasing

// Straight-line metres between two points — the search near-filter's distance check.
// Named locDistM, NOT haversineM: server.js once had two same-named haversine
// declarations and the last one silently won (see README 2026-06-28) — keep the
// name unique in this file.
// locDistM: definition moved to ../../lib/deskFeed.js (imported above).
// Search-filter reach per location type ("near X" typed in the box). Complex is
// practically "in it"; the rest mirror the LOCATION card radii above.
// ⚠️ metro: 300, NOT 1500 (owner ruling 2026-07-24). This map is the NEARER tier — walkable.
// 1500m is a ~20-minute walk, so the old radius returned flats nobody would call metro-adjacent
// and the filter meant nothing. 300m ≈ 4 minutes. The NEAR (bus-takable) tier lives in
// LOC_RADIUS_BY_TYPE above; see the note there before touching either.
// The other kinds keep their wider radii on purpose: you drive to a mall or an airport, and a
// park at 1km is still "near a park". If school/kindergarten ever draw the same complaint, they
// are the next walkable pair — parents walk children.
// NEAR_SEARCH_RADIUS_M: definition moved to ../../lib/deskFeed.js (imported above).

// t() renders the raw KEY for a string it does not have, so a status the vocabulary has not
// caught up with reaches the screen as `desk_cplx_status_<x>`. Show nothing instead: a
// missing adjective costs a reader nothing, a leaked key costs them trust.
function cplxStatusLabel(status) {
  if (!status) return null
  const key = `desk_cplx_status_${status}`
  const label = t(key)
  return label === key ? null : label
}
function locDistanceLabel(m) {
  return m < 1000 ? `${Math.round(m / 10) * 10}${t('desk_loc_unit_m')}` : `${(m / 1000).toFixed(1)}${t('desk_loc_unit_km')}`
}
function locName(row) {
  return (getLanguage() === 'ka' && row.name_ka) ? row.name_ka : row.name
}
// Promise cache per property (same pattern as approxLocationCache below): reopening
// a modal or re-anchoring on a sibling must not re-fire the two RPCs.
const nearbyCache = new Map()
// ⚠️ THE COMPLEX ARM NOW COMES FROM CORE, NOT THE SCHEDULER (0726, owner ruling 2026-08-26).
// This card used to ask the scheduler's own `complexes` table — a SEPARATE identity, 133 rows
// against core's 332, in a different database from the one the linker and the search filter
// use. The owner's report was "says complex linked but can't see it anywhere in the listing",
// and the measurement was worse than a missing display path: the banner below only ever drew
// on an agent `yes`, and `property_question_votes` held FIVE complex_membership rows in
// total — one no, four skip, zero yes. It had never rendered for any property, once, while
// 1,802 property rows carried a core link.
// The POI arm still belongs to the scheduler; those are metro/park/school rows and have
// nothing to do with this.
function getNearbyForPin(cacheKey, lat, lng, propertyId) {
  if (!nearbyCache.has(cacheKey)) {
    // ⚠️ The two arms are INDEPENDENT and must fail independently. They live in different
    // databases now — POIs in the scheduler, the complex in core — so a Promise.all would let
    // either one's outage delete the other from the card, and the failure a reader would see
    // is a missing banner, not an error. That is the shape this whole change exists to stop.
    nearbyCache.set(cacheKey, Promise.all([
      nearbyLocations(lat, lng, LOC_FETCH_RADIUS_M, null)
        .catch(e => { console.error('[location-card] poi arm:', e.message); return [] }),
      // No property id (a bare pin, no row behind it) means no link to speak of — resolve
      // empty rather than asking, so the POI chips still render.
      (propertyId ? getPropertyComplex(propertyId) : Promise.resolve(null))
        .catch(e => { console.error('[location-card] complex arm:', e.message); return null }),
    ]).then(([locs, cx]) => ({ locs: locs || [], linked: cx?.linked || [], candidates: cx?.candidates || [] })))
  }
  return nearbyCache.get(cacheKey)
}

// Exported for `src/harness.jsx` only (same reason as MyVersionCard/PortalFactsCard): this
// card is inside the login gate, and the 0726 banner cannot be verified by a build.
export function NearbyLocationsCard({ propertyId, lat, lng, address, agentId, canEdit, onSetPin, suppressQuestion }) {
  const hasPin = lat != null && lng != null   // precise 📍 only — merged lat/lng is never the approximate geocode
  const [nearby, setNearby] = useState(null)  // { locs, linked, candidates } | null while loading/unavailable
  const [votes, setVotes] = useState([])      // newest-first property_question_votes rows
  const [voteBusy, setVoteBusy] = useState(false)
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    if (!hasPin) return
    let cancelled = false
    getNearbyForPin(propertyId || `${lat},${lng}`, lat, lng, propertyId)
      .then(d => { if (!cancelled) setNearby(d) })
      .catch(e => console.error('[location-card] nearby fetch:', e.message))
    if (propertyId) {
      getPropertyQuestionVotes(propertyId, 'complex_membership')
        .then(v => { if (!cancelled) setVotes(v) })
        .catch(e => console.error('[location-card] votes fetch:', e.message))
    }
    return () => { cancelled = true }
  }, [propertyId, lat, lng])  // eslint-disable-line react-hooks/exhaustive-deps

  // ── No precise pin: greyed prompt for editors (their want of this pitch data is
  // the incentive that fills missing pins), nothing at all for everyone else.
  // District-centroid distances would be garbage an agent might quote — never shown.
  if (!hasPin) {
    if (!canEdit || !onSetPin) return null
    return (
      <div className={`${card} opacity-60`}>
        <p className={`${SECTION_LABEL} mb-2`}>{t('desk_loc_section')}</p>
        <button className="text-sm text-primary text-left" onClick={onSetPin}><Icon name="map-pin" /> {t('desk_loc_set_pin')}</button>
      </div>
    )
  }
  if (!nearby) return null   // loading or fetch failed — the card just appears when ready

  // ── Chips: group by type (fixed order — scannability beats raw distance sort),
  // per-type radius cutoff, 2 nearest each.
  const chips = pickNearbyChips(nearby.locs, LOC_RADIUS_BY_TYPE)

  // ── Complex membership verdict, derived from append-only votes: latest row per
  // agent per complex; latest non-skip answer wins overall (yes → confirmed banner,
  // no → denied, never re-asked). A 'skip' silences the question for that agent only.
  const latestByComplex = {}   // subject_id → agent_id → newest vote row
  for (const v of votes) {
    const perAgent = (latestByComplex[v.subject_id] = latestByComplex[v.subject_id] || {})
    if (!perAgent[v.agent_id]) perAgent[v.agent_id] = v   // votes arrive newest-first
  }
  const verdictFor = subjectId => {
    const real = Object.values(latestByComplex[subjectId] || {})
      .filter(v => v.answer !== 'skip')
      .sort((a, b) => (a.created_at < b.created_at ? 1 : -1))
    if (!real.length) return { state: 'open' }
    const yes = real.filter(v => v.answer === 'yes').length
    return { state: real[0].answer === 'yes' ? 'confirmed' : 'denied', yes, no: real.length - yes }
  }
  // ── What shows, and why (0726). TWO ways a flat comes to be in a development and they are
  // not alternatives: the linker or an admin ESTABLISHED it (core, `linked`, carrying the
  // basis that established it), or an agent standing in front of the building SAID so (a
  // `yes` vote here). Either is enough to draw the banner; a denial by vote overrides,
  // because the person who visited outranks a text match.
  // ⚠️ Do NOT restore "only draw on a yes vote" from an older reading of this file: that rule
  // is exactly what made 1,802 established links invisible.
  const established = nearby.linked || []
  const candidates = nearby.candidates || []   // nearest-first, undecided only
  const denied = c => verdictFor(String(c.complex_id)).state === 'denied'
  const confirmed = established.find(c => !denied(c))
    || candidates.find(c => verdictFor(String(c.complex_id)).state === 'confirmed')
  // Next open question: nearest un-answered complex this agent hasn't skipped.
  // One question at a time modal-wide: the district dispute outranks this one
  // (wrong district poisons more), so suppressQuestion hides it while that's live.
  const askTarget = (!confirmed && !suppressQuestion && agentId)
    ? candidates.find(c => {
        // ⚠️ The SQL arm returns everything inside the linker's published band
        // (complex_candidate_band_m(), 300 m) because that is the ONE definition of "close
        // enough to consider" and this card must not restate it. The QUESTION window is
        // tighter on purpose and always was: 150 m is "is this flat IN it", and asking about a
        // development 300 m away invites a "near enough" yes — the exact inference 0183 exists
        // to prevent. The band decides what is fetched; this decides what is worth asking.
        if (c.distance_m == null || c.distance_m > COMPLEX_ASK_RADIUS_M) return false
        const sid = String(c.complex_id)
        if (verdictFor(sid).state !== 'open') return null
        return latestByComplex[sid]?.[agentId]?.answer !== 'skip'
      })
    : null

  if (!chips.length && !confirmed && !askTarget) return null   // regional listing outside seed coverage — no apologetic empty state

  async function answerComplex(complexRow, answer) {
    if (voteBusy) return
    setVoteBusy(true)
    try {
      const row = await castPropertyQuestionVote({
        kind: 'complex_membership', propertyId, subjectId: complexRow.complex_id,
        agentId, answer,
        // Evidence snapshot: if the pin is later dragged away, this vote can be
        // invalidated instead of anchoring a stale membership.
        // ⚠️ Math.round(null) is 0, and a stored 0 would read as "the flat is ON the complex
        // pin" — the most confident possible claim, minted from an absence. Nine core
        // complexes have no pin at all, so this is reachable.
        metadata: {
          distance_at_vote_m: complexRow.distance_m == null ? null : Math.round(complexRow.distance_m),
          pin: [lat, lng],
        },
      })
      setVotes(v => [row, ...v])   // verdict derives from local state → the box flips to its payoff instantly
    } catch (e) { console.error('[location-card] vote:', e.message) }
    setVoteBusy(false)
  }

  // WhatsApp/Viber follow-up text — deterministic lines, no Georgian case-grammar templating.
  function copyPitch() {
    const lines = []
    if (address) lines.push(address)
    if (confirmed) lines.push(`🏢 ${confirmed.name}`)
    for (const r of chips) lines.push(`${LOC_TYPE_EMOJI[r.type] || '📍'} ${locName(r)} — ${locDistanceLabel(r.distance_m)}`)
    navigator.clipboard.writeText(lines.join('\n'))
      .then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500) })
      .catch(e => console.error('[location-card] copy:', e.message))
  }

  const confirmedVerdict = confirmed ? verdictFor(String(confirmed.complex_id)) : null
  return (
    <div className={card}>
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-semibold text-muted-foreground">{t('desk_loc_section')}</p>
        {(chips.length > 0 || confirmed) && (
          <button className="text-xs text-primary" onClick={copyPitch} title={t('desk_loc_copy_pitch')}>
            {copied ? t('desk_loc_copied') : <Icon name="clipboard" />}
          </button>
        )}
      </div>
      {/* Membership is a primary selling point, not a proximity perk — full-width
          banner (link-styled name reserved for the future complex page), never a chip. */}
      {confirmed && (
        <div className="rounded-lg border border-primary/40 bg-primary/10 px-3 py-2 text-sm flex items-center justify-between gap-2 flex-wrap mb-2">
          <span>
            <Icon name="building" /> <span className="font-medium">{confirmed.name}</span>
            {(confirmed.developer || confirmed.status) && (
              <span className="text-muted-foreground text-xs ml-1.5">
                ({[confirmed.developer, cplxStatusLabel(confirmed.status)].filter(Boolean).join(', ')})
              </span>
            )}
          </span>
          {/* Say WHAT established this, never just assert it. An agent deciding whether to
              repeat "it's in Archi Lilacs" to a client needs to know whether a colleague
              confirmed it, the advert said it, or a machine inferred it from a pin — and 127
              links are still over a kilometre from their complex, so 'from the location only'
              is the one a reader should weigh before quoting. */}
          <span className="text-xs text-muted-foreground shrink-0">
            {t(`desk_cplx_basis_${confirmed.basis || (verdictFor(String(confirmed.complex_id)).state === 'confirmed' ? 'agent_vote' : 'proximity')}`)}
            {confirmedVerdict?.no > 0 && ` · ${t('desk_district_dispute_votes')}: ${confirmedVerdict.yes} : ${confirmedVerdict.no}`}
          </span>
        </div>
      )}
      {askTarget && (
        <QuestionBox
          title={t('desk_loc_q_complex_title')}
          question={tLog(askTarget.distance_m <= COMPLEX_SURE_RADIUS_M ? 'desk_loc_q_complex_in' : 'desk_loc_q_complex_near', { name: askTarget.name })}
          options={[{ value: 'yes', label: t('desk_q_yes') }, { value: 'no', label: t('desk_q_no') }]}
          myAnswer={latestByComplex[String(askTarget.complex_id)]?.[agentId]?.answer || null}
          busy={voteBusy}
          onAnswer={a => answerComplex(askTarget, a)} />
      )}
      {chips.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {chips.map(r => (
            <span key={r.id} className={pill('none', { extra: 'bg-muted border-transparent text-foreground/80' })}>
              <Icon name={LOC_TYPE_ICON[r.type] || 'map-pin'} /> {locName(r)}
              <span className="text-muted-foreground tabular-nums">{locDistanceLabel(r.distance_m)}</span>
            </span>
          ))}
        </div>
      )}
    </div>
  )
}

// ─── PROPERTY HISTORY — the full event stream for this identity cluster ──────────────────
// ADMIN-ONLY for now (2026-07-27 ruling: "make it for admin first who will see everything,
// then we decide what to show to agents"). The gate is `privileged`, the same admin/manager
// test the rest of this modal uses — NOT a new one.
//
// Why the gate is load-bearing and not just a rollout choice: this modal spends real effort
// masking owner identity from non-representatives (listingLineagePayload nulls author_name,
// source_url and posted_address for owner ads). The raw event stream is BELOW that masking —
// listing.changed carries author/author_phone diffs, contact_link.declared carries names. So
// rendering it to a non-privileged viewer would hand back exactly what the payload withheld.
// Widening this to agents is a REDACTION task on core_events.js, not a prop change here.
//
// Keyed on the whole identity cluster, not listing.property_id: the four postings of one
// portal property are four `property` rows (joined retroactively by same_as), so the anchor
// row alone holds a fraction of the history. Same id set the cooperations fetch already uses.
//
// Collapsed by default — it is the deepest, least-scannable thing in the modal, and the
// sections above answer the common questions. Opening it is a deliberate act.
// PRESENTATION ONLY — what happened is decided in src/lib/propertyEpisodes.js and
// pinned by tests against real data. Nothing here re-judges an episode; it only
// draws one. Built from existing primitives on purpose (card/btn/PlatformPill/
// Money/Icon + the HSL tokens): a design pass is in flight, and inventing chrome
// now would be work thrown away twice.
//
// Author identity is THREE states, never two — a competing agency's broker is not
// bridged to our roster, so a boolean would draw them as the owner. Owner = solid,
// our agent = solid muted, external = HOLLOW ("we hold no record of this person",
// which must not read as "suspicious").
function ActorDot({ kind }) {
  const cls = kind === 'owner' ? 'bg-primary border-primary'
    : kind === 'our_agent' ? 'bg-muted-foreground/70 border-muted-foreground/70'
      : 'bg-transparent border-muted-foreground/60'
  return <span className={`inline-block w-2 h-2 rounded-full border shrink-0 ${cls}`} />
}

function actorLabel(actor) {
  if (!actor) return ''
  if (actor.name) return actor.name
  if (actor.kind === 'owner') return t('desk_ep_owner')
  if (actor.kind === 'unknown') return t('desk_ep_external')
  return ''
}

function episodeHeadline(ep) {
  const who = actorLabel(ep.actor)
  const portal = ep.sources[0] || ''
  switch (ep.kind) {
    case 'owner_post': return t('desk_ep_owner_post')
    case 'reshare': return ep.sources.length > 1
      ? tLog('desk_ep_reshare', { who, n: ep.sources.length })
      : tLog('desk_ep_reshare_one', { who, portal })
    case 'withdrawn': return t('desk_ep_withdrawn')
    case 'price_move': {
      const m = ep.price_move, cur = m.currency || ''
      return tLog(m.direction === 'up' ? 'desk_ep_price_up' : 'desk_ep_price_down',
        { from: `${m.from} ${cur}`.trim(), to: `${m.to} ${cur}`.trim() })
    }
    case 'correction': return t('desk_ep_correction')
    case 'merge': return t('desk_ep_merge')
    case 'agreement': return tLog('desk_ep_agreement', { what: (ep.events[0]?.verb || '').split('.')[1]?.replace(/_/g, ' ') || '' })
    case 'bookkeeping': return t('desk_ep_bookkeeping')
    case 'sold': return t('desk_ep_sold')
    // Unknown verbs keep the no-hiding contract: render the raw verb rather than
    // nothing, so a new backend event is visible the day it starts firing.
    case 'unknown': return `· ${ep.events[0]?.verb || ''}`
    default: return t('desk_ep_observed')
  }
}

function EpisodeRow({ ep, listings }) {
  const loud = ep.weight === 'loud'
  const quiet = ep.weight === 'quiet'
  // Indent by lineage depth — a repost OF a repost is a different fact from a
  // repost of the owner's ad. Measured: depth 1 = 98.3%, max observed 4, so 3
  // levels covers essentially everything; deeper renders at level 3.
  const indent = Math.min(ep.depth || 0, 3) * 12
  return (
    <li className={`py-1 ${quiet ? 'opacity-60' : ''}`} style={{ paddingLeft: indent }}>
      <div className="flex items-baseline gap-2 flex-wrap">
        <span className="text-xs text-muted-foreground tabular-nums shrink-0">{evTime(ep.at)}</span>
        <ActorDot kind={ep.actor?.kind} />
        <span className={loud ? 'font-medium' : ''}>{episodeHeadline(ep)}</span>
        {ep.sources.map(s => <PlatformPill key={s} source={s} compact />)}
        {ep.price && <span className="text-xs text-muted-foreground tabular-nums">{ep.price.value} {ep.price.currency}</span>}
        {ep.kind === 'withdrawn' && <Icon name="info" title={t('desk_ep_inferred_off')} />}
        {ep.unverifiable && <Icon name="help-circle" title={t('desk_history_partial')} />}
      </div>
      {/* The drawer: every event that fed this episode, including the ones the episode chose
          not to shout about.
          ⚠️ It said "lossless by contract" until 2026-09-02, and T34 made that FALSE rather than
          merely imprecise: edits that never settled (a typo and its correction) are folded out
          before episodes are built, so they feed nothing and appear nowhere here. The raw record
          is untouched in the event log — this is a rendering rule — but do not read this list as
          "everything that happened". Restoring true losslessness means keeping the unsettled
          events and marking them, not reverting the fold. */}
      {ep.events.length > 1 && (
        <details className="mt-0.5">
          <summary className="cursor-pointer text-xs text-muted-foreground">
            {tLog('desk_history_drawer', { n: ep.events.length })}
          </summary>
          <ul className="pl-4 mt-1 space-y-0.5 text-xs text-muted-foreground">
            {ep.events.map(e => (
              <li key={e.id}><span className="tabular-nums">{evTime(e.occurred_at)}</span> — {evLabel(e, listings)}</li>
            ))}
          </ul>
        </details>
      )}
    </li>
  )
}

// The pinned summary. Every number here comes from the SERVER aggregate over the
// whole cluster, never from the loaded page — a count derived from one page of
// 100 would describe the page and claim to describe the property.
function HistorySummary({ s }) {
  if (!s) return null
  const spread = s.price_min_usd != null && s.price_max_usd != null
  const disagree = spread && s.price_max_usd > s.price_min_usd
  return (
    // Docks BELOW the modal's own sticky title bar, not on top of it. That bar is
    // `sticky top-0 z-10` and ~57px tall in the same scroll container, so a second
    // top-0/z-10 element collides with it the moment you scroll (caught in the
    // browser 2026-07-27 — the summary covered the address). top-14 ≈ 56px clears
    // it; z-[9] means that if a long address wraps the title to two lines, this
    // slides UNDER the title rather than over it. Degrading out of sight beats
    // covering the thing that says which property you are looking at.
    <div className="sticky top-14 z-[9] bg-card border-b border-border pb-2 mb-2 -mt-1 pt-1">
      <div className="flex items-center gap-3 flex-wrap text-xs">
        {s.owner_price && (
          <span className="text-muted-foreground">
            {tLog('desk_history_owner_price', { price: `${s.owner_price.price} ${s.owner_price.currency}` })}
          </span>
        )}
        {spread && (
          <span className={disagree ? 'text-amber-600 dark:text-amber-500 font-medium' : 'text-muted-foreground'}>
            {s.price_min_usd === s.price_max_usd
              ? tLog('desk_history_price_one', { min: `$${Math.round(s.price_min_usd)}` })
              : tLog('desk_history_prices', { min: `$${Math.round(s.price_min_usd)}`, max: `$${Math.round(s.price_max_usd)}` })}
          </span>
        )}
        {s.agreements?.length > 0 && (
          <span className="text-muted-foreground">
            {s.agreements.map((a, i) => (
              <span key={i}>{i > 0 ? ' · ' : ''}
                {/* The FACT of an agreement is never hidden; only its numbers are. */}
                {a.floor_visible && a.floor != null
                  ? tLog('desk_history_floor', { price: `${a.floor} ${a.floor_currency || ''}`.trim() })
                  : t('desk_history_floor_hidden')}
              </span>
            ))}
          </span>
        )}
      </div>
    </div>
  )
}

function PropertyHistoryBand({ propertyIds, refreshKey }) {
  const feed = usePropertyEvents(propertyIds, refreshKey, { limit: 200 })
  const key = useMemo(() => [...new Set(propertyIds.filter(Boolean))].sort().join(','), [propertyIds])
  const [summary, setSummary] = useState(null)
  useEffect(() => {
    if (!key) return
    let gone = false
    getPropertyHistorySummary(key).then(d => { if (!gone) setSummary(d) }).catch(() => {})
    return () => { gone = true }
  }, [key, refreshKey])

  // T34 (owner ruling 2026-09-02): episodes are MARKET HISTORY — "has this owner been dropping
  // the price?" — so a typo and its correction must not appear as two price moves. The events are
  // settled first (per field, 15-minute sliding window, previous-stable → new-stable), which is
  // the same rule the property timeline applies, from the same module.
  // ⚠️ It runs HERE and not inside buildEpisodes because the episode builder is pure over the
  // events it is given; putting a clock inside it would make it untestable without one.
  const episodes = useMemo(() => buildEpisodes(
    feed.feed ? { ...feed.feed, events: stabilizeEvents(feed.feed.events, now().getTime()) } : feed.feed
  ), [feed.feed])
  const visible = useMemo(() => episodes.filter(isVisible), [episodes])
  // Low volume → the drawers are the document, not a distraction. Open them.
  const autoOpen = (feed.feed?.events?.length || 0) < 20
  if (!propertyIds.length) return null

  const s = summary
  const days = s?.first_event_at ? Math.max(1, Math.round((Date.parse(s.last_event_at || Date.now()) - Date.parse(s.first_event_at)) / 86400000)) : null
  // The collapsed line IS the feature for most viewers: live? how long? how hard
  // is the owner pushing? how many of ours are on it? do our prices disagree?
  const bits = []
  if (s) {
    bits.push(s.status === 'active' ? t('desk_history_status_active') : t('desk_history_status_off'))
    if (days) bits.push(tLog('desk_history_days', { n: days }))
    if (s.owner_posts) bits.push(s.owner_posts === 1 ? t('desk_history_owner_post_one') : tLog('desk_history_owner_posts', { n: s.owner_posts }))
    if (s.agent_count) bits.push(s.agent_count === 1 ? t('desk_history_agent_one') : tLog('desk_history_agents', { n: s.agent_count }))
    if (s.price_min_usd != null && s.price_max_usd != null && s.price_max_usd > s.price_min_usd) {
      bits.push(tLog('desk_history_prices', { min: `$${Math.round(s.price_min_usd)}`, max: `$${Math.round(s.price_max_usd)}` }))
    }
  }

  return (
    <details className={`${card} mt-4`} open={autoOpen}>
      <summary className="cursor-pointer flex items-center gap-2 flex-wrap">
        {/* rotate-ccw, not "history": Icon returns null silently for an unknown name,
            so a wrong guess ships as an invisible icon nobody notices. */}
        <Icon name="rotate-ccw" />
        <span className="font-medium">{t('desk_history_title')}</span>
        <span className="text-xs text-muted-foreground">
          {bits.length ? bits.join(' · ') : (feed.feed ? tLog('desk_timeline_events_count', { n: feed.feed.events.length, '+': feed.feed.has_more ? '+' : '' }) : '…')}
        </span>
      </summary>

      <HistorySummary s={s} />

      {feed.err && <p className="text-sm text-destructive"><Icon name="alert-triangle" /> {feed.err}</p>}
      {!feed.feed && !feed.err && <p className="text-xs text-muted-foreground">{t('desk_loading')}</p>}
      {feed.feed && !visible.length && <p className="text-xs text-muted-foreground">{t('desk_history_empty')}</p>}

      <ul className="text-sm">
        {visible.map(ep => <EpisodeRow key={ep.key} ep={ep} listings={feed.feed?.listings} />)}
      </ul>

      {feed.feed?.has_more && (
        <button className={`${btn('ghost')} mt-2`} disabled={feed.loadingOlder} onClick={feed.loadOlder}>
          {feed.loadingOlder ? t('desk_history_loading') : t('desk_history_older')}
        </button>
      )}
    </details>
  )
}

// ─── MY VERSION — the facts THIS agent ASSERTED ──────────────────────────────────────────
// Reads `state === 'own'` from agent_property_view. ⚠ marks an attribute where the canonical
// value disagrees, and the disagreeing PORTAL is named — a bare `10 ⚠ 9` states that two
// numbers exist and nothing about who claims which, which is unactionable (2026-08-06: an
// agent hit one, could not decode it, and it took ~20 DB queries to explain).
//
// ⚠️ "own" MEANS ASSERTED, and that is core 0439's whole point. Before it, `own` was any
// claim whose source_party_id matched — but that column records who RAN THE SCRAPER, so a
// scrape-back of the agent's own repost (carrying the portal's invented values) rendered
// under a heading saying the agent recorded it. 14,275 rows across 17 agents were shown that
// way; 6 were real. 0439 restricts `own` to asserting source kinds, so this card now renders
// ONLY deliberate edits, and shows nothing at all for a plain repost — which is correct and
// is why there is no empty state. Do not "fix" the empty card by loosening the filter.
//
// Extracted from an inline IIFE 2026-08-06 so it can be mounted in src/harness.jsx: the Desk
// is login-gated and `vite build` cannot see a Georgian typo or an overflowing value column.
export function MyVersionCard({ facts, onEdit, cardRef = null }) {
  const own = Object.entries(facts || {}).filter(([, f]) => f.state === 'own')
  if (!own.length) return null
  const fmt = v => v == null ? '—' : typeof v === 'object' ? JSON.stringify(v) : String(v)
  return (
    <div className={card} ref={cardRef}>
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-semibold text-muted-foreground">{t('desk_lineage_my_version_section')}</p>
        {onEdit && (
          <button className="text-xs text-primary" onClick={onEdit}>
            {t('desk_lineage_edit_property_button')}
          </button>
        )}
      </div>
      {own.map(([attr, f]) => (
        <div key={attr} className="flex justify-between text-sm py-0.5 gap-3">
          {/* The LABEL truncates, never the value (the StateRow rule, README 2026-08-04):
              clipping the value is what turns a three-portal conflict into a silent one. */}
          <span className="text-muted-foreground min-w-0 truncate">{attrLabel(attr)}</span>
          <span className="shrink-0 text-right">
            {fmt(f.value)}
            {f.drift && (
              <span className="text-amber-600 dark:text-amber-400 ml-1.5 whitespace-nowrap"
                title={f.canonical_portal
                  ? tLog('desk_my_version_drift_tooltip', { portal: f.canonical_portal, value: fmt(f.canonical) })
                  : tLog('desk_my_version_drift_tooltip_nosrc', { value: fmt(f.canonical) })}>
                <Icon name="alert-triangle" /> {fmt(f.canonical)}
                {/* canonical_portal (0439) resolves claim → generation → raw_listing. Null for
                    a claim with no observation behind it, hence the no-source tooltip form. */}
                {f.canonical_portal && (
                  <span className="text-xs opacity-70 ml-1">{f.canonical_portal}</span>
                )}
              </span>
            )}
          </span>
        </div>
      ))}
    </div>
  )
}

// ─── WHAT EACH SOURCE SAYS (core 0444, 2026-08-06) ───────────────────────────────────────
// The information-only half of the owner's 2026-08-06 ruling: a value an agent publishes to a
// portal "can't be carrying meaningful value change, but it must be recorded for information
// only". 0439 removed those values from MyVersionCard, where they were being presented as the
// agent's own assertions. This is where they are shown honestly — beside each other, so
// "floors_total 10 ⚠ 9" becomes "ss.ge says 9, korter.ge says 10" without leaving the modal.
//
// ⚠️ ROWS ARE SOURCES, NOT PORTALS, and the label says "source" for that reason.
// commit_claim supersedes by (source_party_id, source_kind) — not by listing, not by portal —
// so one agent reposting the same flat to three sites writes all three through ONE
// scrape_reshare channel, each overwriting the last. On the reported property irakli's ss.ge
// repost (9) was superseded by his korter repost (10) and is invisible here. Calling this
// "per portal" would therefore be a lie the data cannot back. See 0444's header.
//
// A repost is badged, because it copies an ad rather than observing the flat — without that,
// two rows reading `ss.ge 9 / korter.ge 10` look like two independent opinions when one is a
// marketing copy the agent may have altered deliberately.
//
// Disputed attributes sort first: agreement is reassurance, disagreement is the thing to act
// on. Renders nothing when no attribute has two sources (0444 filters those out server-side).
export function PortalFactsCard({ portalFacts }) {
  const rows = Object.entries(portalFacts || {})
  if (!rows.length) return null
  const fmt = v => v == null ? '—' : typeof v === 'object' ? JSON.stringify(v) : String(v)
  rows.sort((a, b) => (b[1].disputed ? 1 : 0) - (a[1].disputed ? 1 : 0) || a[0].localeCompare(b[0]))
  return (
    <div className={card}>
      <p className="text-xs font-semibold text-muted-foreground mb-2">{t('desk_portal_facts_section')}</p>
      {rows.map(([attr, f]) => (
        <div key={attr} className="py-1 border-b border-border/40 last:border-0">
          <div className="flex justify-between items-baseline gap-3">
            <span className="text-sm text-muted-foreground min-w-0 truncate">{attrLabel(attr)}</span>
            <span className={`text-xs shrink-0 ${f.disputed ? 'text-amber-600 dark:text-amber-400' : 'text-muted-foreground'}`}>
              {f.disputed && <Icon name="alert-triangle" />}{' '}
              {t(f.disputed ? 'desk_portal_facts_disputed' : 'desk_portal_facts_agree')}
            </span>
          </div>
          {(f.sources || []).map((s, i) => (
            <div key={`${s.portal || '?'}-${s.listing_id || i}`} className="flex justify-between gap-3 text-sm pl-3 py-0.5">
              <span className="min-w-0 truncate text-muted-foreground">
                {s.portal || '—'}
                {/* A reshare is a COPY. Badging it is what keeps two rows from reading as
                    two independent observations of the building. */}
                {s.source_kind === 'scrape_reshare' && (
                  <span className="ml-1 text-xs opacity-60" title={t('desk_portal_facts_reshare_tip')}>↻</span>
                )}
              </span>
              <span className={`shrink-0 ${s.canonical ? 'font-semibold' : ''}`}
                title={s.canonical ? t('desk_portal_facts_canonical_tip') : undefined}>
                {fmt(s.value)}{s.canonical && ' ✓'}
              </span>
            </div>
          ))}
        </div>
      ))}
      <p className="text-xs text-muted-foreground mt-2 opacity-70">{t('desk_portal_facts_note')}</p>
    </div>
  )
}

// F4 conflict gate (owner 2026-08-30: "new data contradicts old — ask which to keep; never silently
// replace price/bedrooms/pets"). Sits directly below PortalFactsCard: that card SHOWS the
// disagreement, this one RESOLVES it. The server lists ONLY attributes a machine source (scrape/ai)
// currently owns — anything a person already decided is settled and absent. Picking a value commits
// it as this viewer's decision (admin 0.95 / agent 0.50), which wins by confidence and clears the row.
// Renders nothing without a property or a conflict — no empty ceremony (same rule as MyVersionCard).
function ClaimConflictsPanel({ propertyId }) {
  const [data, setData] = useState(null)      // { conflicts } ; null = loading
  const [busy, setBusy] = useState(null)      // attribute currently resolving
  const [err, setErr] = useState(null)
  const load = () => { if (!propertyId) return; getClaimConflicts(propertyId).then(setData).catch(() => setData({ conflicts: [] })) }
  useEffect(() => { setData(null); load() }, [propertyId])
  const conflicts = data?.conflicts || []
  if (!propertyId || !conflicts.length) return null
  const pick = async (attribute, value) => {
    setBusy(attribute); setErr(null)
    try {
      const isNum = /^-?\d+(\.\d+)?$/.test(value)
      await resolveClaimConflict({ subjectId: propertyId, attribute, valueNum: isNum ? Number(value) : null, valueText: isNum ? null : value })
      load()
    } catch (e) { setErr(e.message || t('desk_conflicts_failed')) } finally { setBusy(null) }
  }
  return (
    <div className={card}>
      <p className="text-xs font-semibold text-amber-600 dark:text-amber-400 mb-2">
        <Icon name="alert-triangle" /> {t('desk_conflicts_section')} · {conflicts.length}
      </p>
      {conflicts.map(c => (
        <div key={c.attribute} className="py-1.5 border-b border-border/40 last:border-0">
          <div className="text-sm text-muted-foreground mb-1">{attrLabel(c.attribute)}</div>
          <div className="flex flex-wrap gap-1.5">
            {[c.current, ...c.competing].map((o, i) => (
              <button key={o.claim_id || i} type="button" disabled={busy === c.attribute}
                onClick={() => pick(c.attribute, o.value)} title={t('desk_conflicts_keep_tip')}
                className={`text-xs rounded-md border px-2 py-1 disabled:opacity-50 ${i === 0 ? 'border-amber-500/60 bg-amber-500/10' : 'border-border hover:bg-accent/60'}`}>
                <span className="font-semibold">{o.value}</span>
                <span className="opacity-60"> · {sourceKindLabel(o.source_kind)}{i === 0 ? ' ✓' : ''}</span>
              </button>
            ))}
          </div>
        </div>
      ))}
      {err && <p className="text-xs text-destructive mt-1">{err}</p>}
      <p className="text-xs text-muted-foreground mt-2 opacity-70">{t('desk_conflicts_note')}</p>
    </div>
  )
}

// ⚠️ `onShare` was PASSED by IntakeTab all along but never destructured here, so the modal
// silently had no client-share path — caught 2026-07-25 when the footer ShareMenu needed it.
// The agent's own recorded facts, named. Capped so a thorough agent's row cannot push the
// chain off the screen; the count says how many are not shown rather than hiding them silently.
// A cleared value prints as "—", because "I checked and it is not known" is an answer.
function MyFactsLine({ facts, max = 4 }) {
  const all = ownFactSummary(facts)
  if (!all.length) return null
  const shown = all.slice(0, max)
  return (
    <span className="block text-xs font-normal text-muted-foreground">
      {shown.map((f, i) => (
        <span key={f.key}>{i > 0 ? ', ' : ''}{attrLabel(f.key)} {f.cleared ? '—' : String(f.value)}</span>
      ))}
      {all.length > shown.length && <span> +{all.length - shown.length}</span>}
    </span>
  )
}

function ListingLineageModal({ listingId, isOwn, onClose, onAddMeeting, onShare, onOpenDemand, onEditProperty, agentId, agentName, privileged = false, isAdmin = false, seed = null, meetingsIndex = null, onOpenMeeting = null, sharesEpoch = 0, initialView = null, onViewChange = null, agents = [], favorites = null, archived = null, onToggleFavorite = null, onToggleArchive = null }) {
  const money = useMoney()
  // AD REMOVER (owner 2026-10-23): reposts a deletion was requested for from this card
  const [rmSent, setRmSent] = useState(() => new Set())
  // blocked adverts of ours with the portal's reason, by listing id (core 4030, owner 2026-10-29 "save blocked and the reason")
  const [blockedAds, setBlockedAds] = useState(null)
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState(null)
  const [data, setData] = useState(null)
  // ⚠️ RECORDING A FACT MUST REFETCH THE FACT (owner-caught 2026-07-30). Closing a deal wrote
  // the lifecycle correctly, but this modal never re-read the lineage — so the status card
  // still had lifecycle=null, fell back to "no problem recorded — considered active", and left
  // the "we closed it" door open. The agent's own successful act looked like it did nothing,
  // and inviting the click again is how the duplicate close happened in the first place.
  // Bump this after any write whose effect this payload carries.
  const [lineageSeq, setLineageSeq] = useState(0)
  // Image-set switcher (2026-08-17). ⚠️ MUST be a top-level hook: `merged` — the thing the hero
  // renders — only exists inside the big render-time IIFE below, and a hook in there would be
  // conditional on `data` having loaded. So the fetch keys off the payload's property id and the
  // derived values (heroImages/heroListing) are computed down there, where they are plain
  // expressions rather than hooks. Selection lives inside the hook for the same reason.
  const imgSets = usePropertyImageSets(data?.listing?.property_id || null)
  // The set pills sit ON the photo, so they are hidden until the pointer is over it (owner
  // 2026-08-17: "make it appear only on hover ... on hover on bottom part of image show that
  // pills"). ⚠️ useCanHover, not useHoverCapable: the question here is "can this person hover at
  // all", and `pointer: fine` describes the PRIMARY device — a touch laptop with a mouse plugged
  // in reports coarse and would lose the control entirely. Where hover is impossible the bar
  // stays visible, because there is no gesture that would reveal it.
  const canHover = useCanHover()
  // ⚠️ THE TRIGGER IS THE BOTTOM BAND OF THE PHOTO, NOT THE PHOTO (owner 2026-08-17: "hover on
  // the bottom of the image where that panel lives, not on the image at any other place"). A
  // `group`/`group-hover` pair cannot express that — it fires anywhere in the group — and the
  // obvious fix, an absolutely-positioned hover zone, is worse than it looks: a zone with
  // pointer-events:auto SWALLOWS the click that advances the carousel, and one with
  // pointer-events:none receives no hover at all, because CSS hover does not fire on it.
  // So the band is measured from the pointer instead. The pills are CHILDREN of the same
  // wrapper, so moving onto them keeps the pointer inside the band and they stay up.
  const [pillsOn, setPillsOn] = useState(false)
  const PILL_BAND = 0.7   // top of the band, as a fraction of the photo's height
  const loadedViewRef = useRef(null)
  const [coops, setCoops] = useState([])
  // Has the coops fetch ANSWERED at least once (with rows, empty, or an error)? The
  // URL-restore effect gates on this — an unsettled [] is indistinguishable from "no
  // cooperation exists", which is exactly the misread behind the blank-reopen bug.
  const [coopsSettled, setCoopsSettled] = useState(false)
  const [coopModal, setCoopModal] = useState(null) // { propertyId, propertyLabel?, contactPartyId?, contactName?, cooperationId? }
  // Combined deal editor (owner 2026-07-29): cooperation + owner-terms open TOGETHER as
  // two tabs of one editor — "when an agent fills one they usually fill the other too".
  // termsEdit = { listingId, mode }; dealTab = which tab fronts; both stay MOUNTED while
  // the editor is open so switching never loses half-typed input.
  const [termsEdit, setTermsEdit] = useState(null)
  const [dealTab, setDealTab] = useState('coop')
  // Repost (B244) opens the SAME editor on its third tab. It gets its own flag because it
  // is reachable from the ↗ ShareMenu, which has no cooperation or terms context to seed —
  // without this the editor would not render at all (its condition is coop || terms).
  const [repostOpen, setRepostOpen] = useState(false)
  // ქონების შეფასება (2026-09-12): the valuation tab's own render gate, for the same reason
  // repostOpen has one — its door (the button beside the Orpheus note) has no coop/terms
  // context to seed, and the editor's condition must still be true.
  const [valuationOpen, setValuationOpen] = useState(false)
  // ── THE EDITOR SURVIVES A REFRESH (2026-08-05) ──────────────────────────────────────────
  // Owner-reported: the repost tab talks to the extension, so "reload after the extension
  // updates" is a normal move mid-task — and it used to close this modal AND its editor.
  // `initialView` is what the URL says was open; `onViewChange` reports the tab back so the
  // URL keeps up. Both are optional: every other caller passes neither and behaves as before.
  //
  // The restore CANNOT run before `data` lands. Opening the editor means seeding its other
  // two tabs from the loaded payload (defaultCoopFor/defaultTermsFor), and skipping that is
  // the exact bug fixed twice already — an editor whose other tabs open empty on properties
  // that have both. So the door is captured into a ref during render (below, beside its
  // definition) and the effect waits for it.
  const openDealEditorRef = useRef(null)
  const [viewReady, setViewReady] = useState(!initialView)   // false only while a URL view is still pending
  useEffect(() => {
    if (viewReady || !data) return
    // ⚠️ Wait for the COOPS fetch too, not just the payload (2026-08-06): defaultCoopFor
    // reads `coops`, which loads async — restoring `view=coop` before it landed seeded a
    // blank NEW cooperation over an existing one, whose save open_cooperation then deduped
    // with the typed terms discarded (the irakli incident). coopsSettled flips on success
    // AND failure, so a dead endpoint degrades to the old behaviour, never a hang.
    if (data?.listing?.property_id && !coopsSettled) return
    const open = openDealEditorRef.current
    // No door (payload has no listing to edit) → nothing to restore into. Still mark ready,
    // so the reporter below can tell the URL the truth instead of leaving a stale `view=`.
    if (open) {
      if (initialView === 'share') { open('share'); setRepostOpen(true) }
      else if (initialView === 'coop' || initialView === 'terms') open(initialView)
    }
    setViewReady(true)
  }, [data, viewReady, coopsSettled])   // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!onViewChange || !viewReady) return
    onViewChange((coopModal || termsEdit || repostOpen || valuationOpen) ? dealTab : null)
  }, [viewReady, coopModal, termsEdit, repostOpen, valuationOpen, dealTab])   // eslint-disable-line react-hooks/exhaustive-deps
  // ── SHARING THIS FLAT WITH A COLLEAGUE (0983, owner 2026-09-08) ────────────────────────
  // `propReps` is null until asked and is fetched only when the picker opens: it is needed
  // solely to EXCLUDE people who already hold the flat, and paying for that read on every
  // modal open would be a fetch for a menu most opens never touch.
  const [sharePick, setSharePick] = useState(false)
  const [shareBusy, setShareBusy] = useState(false)
  const [shareErr, setShareErr] = useState(null)
  const [shareOk, setShareOk] = useState(null)
  const [propReps, setPropReps] = useState(null)
  const [termsRefresh, setTermsRefresh] = useState(0) // bumped on modal close → summary refetches
  const [viewingsSummary, setViewingsSummary] = useState(null) // null = loading/unavailable, [] = none
  const [pcontacts, setPcontacts] = useState(null)   // { contacts:[…], default_party_id } — ranked property contacts (resolution chain); null = unavailable → author fallback
  const [shares, setShares] = useState(null)         // client links sent for this property; null = NOT LOOKED YET (≠ [] "none sent") — see reloadShares
  const [myVersion, setMyVersion] = useState(null)   // getMyPropertyVersion result — which facts are THIS agent's own edits vs canonical
  const [fbStats, setFbStats] = useState(null)       // Map listing_id → {post_count, group_count}: one FB publication = many group posts (owner 2026-08-20)
  // §9 mosaic → hero jump: 'map' | photo URL | number. ⚠️ STARTS AT 0, NOT null (2026-08-17).
  // `null` meant "let ListingGallery self-drive", and while it self-drives it never calls
  // onIndexChange — so the modal did not know which photo was on screen, and the tile strip
  // could not ring it (owner asked for the selected image to be shown in the strip). Starting
  // controlled costs nothing: the gallery reports every advance, so the ring tracks clicks on
  // the hero as well as clicks on a tile.
  const [heroSlide, setHeroSlide] = useState(0)
  const [openPostings, setOpenPostings] = useState(() => new Set())  // §4i one-line rule: posting ids (and 'grp:*' keys) whose chevron detail is expanded
  // District dispute (0101): local copy of data.district_dispute so a vote updates the
  // tally/question in place without refetching the whole lineage payload.
  const [dispute, setDispute] = useState(null)
  const [voteBusy, setVoteBusy] = useState(false)
  // 0376 — "is this ad posted by an agent?": colleagues' reports + the portal's own
  // declaration + the scraped roster, for this one property. Its own small read rather
  // than a field on the lineage payload: the modal can be re-anchored between postings
  // of the same property, and this answer is the same for all of them.
  const [authorRoleMark, setAuthorRoleMark] = useState(null)
  const authorRolePid = data?.listing?.property_id || null
  useEffect(() => {
    if (!authorRolePid) return
    let gone = false
    // Soft: no answer just means the pill stays neutral and the popover asks the
    // question fresh — never "nobody reported this".
    getAuthorRole(authorRolePid).then(m => { if (!gone) setAuthorRoleMark(m) }).catch(() => {})
    return () => { gone = true }
  }, [authorRolePid])

  // Orpheus note (owner 2026-09-07: "add orpheus message here too inside a modal"): the same
  // card note the Leads feed shows (DistinctNote, property_distinctiveness.text_ka), fetched
  // per property because the modal opens from places with no feed page behind it (see the
  // shares comment below). Soft: no row / no text → the row simply does not render.
  const [distinctRow, setDistinctRow] = useState(null)
  const distinctIntent = data?.listing?.intent || seed?.intent || null
  useEffect(() => {
    if (!authorRolePid) return
    let gone = false
    getPropertyDistinctiveness([authorRolePid])
      .then(map => { if (!gone) setDistinctRow(pickDistinct(map?.[authorRolePid], distinctIntent) || null) })
      .catch(() => {})
    return () => { gone = true }
  }, [authorRolePid, distinctIntent])

  // Fill-in-place pills (2026-07-21): feature vocab for the project options (cached fetch,
  // shared promise in core.js) + a local echo of what the agent just recorded — a feature
  // write does NOT change merged.condition/.project (those are scrape claims), so without
  // the echo the row would still show "? fill in" right after a successful answer.
  // ⚠️ The reset-on-reanchor effect lives BELOW the `view` declaration — a dep of [view.id]
  // up here reads `view` before its useState runs (TDZ) and crashes the whole modal on open
  // (2026-07-21, user-caught: "modal doesn't open").
  const [featVocab, setFeatVocab] = useState([])
  const [factDefKey, setFactDefKey] = useState(null)   // ⓘ (0162): open definition in the scraped-facts table
  const [filledFacts, setFilledFacts] = useState({})
  // axis → the code the agent just confirmed via a conflict popover. Optimistic echo, same
  // shape as filledFacts: the write is append-only, so the server's next answer still carries
  // the losing value and axisConflicts would keep flagging until this render says otherwise.
  const [resolvedAxes, setResolvedAxes] = useState({})
  useEffect(() => { getFeatures().then(setFeatVocab).catch(() => {}) }, [])
  // 🔻 The modal's own agent-facts fetch was removed with its render (see the withdrawn band
  // further down). Keeping it would have cost two HTTP calls per modal open feeding nothing,
  // which is worse than the restore it was meant to make cheap — and the restore is a revert
  // of the commit that withdrew it, not a retyping.
  async function castDistrictVote(choice) {
    if (!dispute || voteBusy) return
    setVoteBusy(true)
    try {
      const r = await voteDistrict(dispute.property_id, choice, agentId)
      setDispute(d => ({ ...d, votes: r.votes, status: r.status, my_vote: r.my_vote }))
    } catch (e) { console.error('[district-vote]', e.message) }
    setVoteBusy(false)
  }
  // Which posting the modal is anchored on. Every POSTED ON row is clickable (2026-07-09):
  // each reshare is its OWN listing with its own captured data (posted address/price/photos),
  // so clicking a row re-anchors the whole modal on that posting — its facts render with the
  // same differs-from-origin highlighting the primary listing gets. Siblings are always our
  // agents' own reshares (derived_from edges only come from reposting), hence isOwn: true.
  const [view, setView] = useState({ id: listingId, isOwn })
  // Re-anchoring on a POSTED ON sibling is a different posting/property — the previous
  // pick must not bleed into its rows. (Must sit below `view`'s declaration; see the
  // TDZ note at the filledFacts state.)
  useEffect(() => { setFilledFacts({}); setResolvedAxes({}) }, [view.id])
  // Parent can retarget the open modal (e.g. AddEntryTile → another listing) — follow the prop.
  useEffect(() => { setView({ id: listingId, isOwn }) }, [listingId])  // eslint-disable-line react-hooks/exhaustive-deps

  // The whole FAMILY's cooperations, not just the opened card's property: a sale coop
  // lives on the sale property row (0046 identity = property+contact+intent), which is a
  // DIFFERENT property_id from the rent card the agent opened (0041: every posting has its
  // own property row) — fetching one id hid the other intent's agreement (2026-07-12,
  // შოთა/Pinar case: sale coop recorded fine, invisible from the rent-side modal).
  // ⚠️ Dedupe by REQUEST KEY, not by call site (2026-07-26 perf trace: opening one modal
  // fired contacts ×2 and cooperations ×2 for the same property, ~0.9s of pure waste).
  // Both are called twice by design — once at mount from the seed (the parallel fast path
  // below) and again when the lineage resolves — and usually with identical arguments, so
  // the second is a repeat of a request already made.
  // `force` exists because these double as refresh-after-save callbacks, which MUST refetch
  // the same key. Never drop it: without it, saving a cooperation stops updating the list.
  const lastCoopsKey = useRef('')
  const lastContactsPid = useRef('')
  const lastSharesPid = useRef('')
  const coopsSeq = useRef(0)      // review 2026-09-23: stale-response guards
  const contactsSeq = useRef(0)
  function reloadCoops(propertyId, extraIds = [], { force = false } = {}) {
    const ids = [...new Set([propertyId, ...extraIds].filter(Boolean))].sort()
    if (!ids.length) { setCoopsSettled(true); return }
    const key = ids.join(',')
    if (!force && key === lastCoopsKey.current) return
    lastCoopsKey.current = key
    // review 2026-09-23: only the NEWEST request may land — a slow seed fetch (one property) used
    // to arrive after the sibling-wide one and replace the fuller list with the narrower one.
    const seq = ++coopsSeq.current
    listCooperations({ propertyId: ids })
      .then(cs => { if (seq !== coopsSeq.current) return; setCoops(cs); setCoopsSettled(true) })
      .catch(() => { if (seq === coopsSeq.current) setCoopsSettled(true) })   // failure settles too — see the restore effect
  }
  function reloadContacts(propertyId, { force = false } = {}) {
    if (!propertyId) return
    if (!force && propertyId === lastContactsPid.current) return
    lastContactsPid.current = propertyId
    const seq = ++contactsSeq.current   // review 2026-09-23: newest request wins (see reloadCoops)
    getPropertyContacts(propertyId).then(r => { if (seq === contactsSeq.current) setPcontacts(r) }).catch(() => {})
  }
  // 🔗 The links already sent to clients for THIS property.
  //
  // ⚠️ FETCHED HERE rather than handed down from IntakeTab's sharesByProperty,
  // even though that index exists and is already passed to three other children.
  // That index only holds the properties of the page the agent is currently
  // looking at, and this modal is increasingly opened from places that have no
  // page behind them — the notification bell, and (2026-08-04) resolving a share
  // link an agent pasted back. Reading the index there yields "no entry", which
  // renders as no chip, precisely in the case where the agent is asking about a
  // link. One request for one property is cheap; being wrong is not.
  //
  // Stays null until it lands — ShareChip renders nothing on null, so the footer
  // never claims "no links sent" before it has looked.
  function reloadShares(propertyId, { force = false } = {}) {
    if (!propertyId) return
    if (!force && propertyId === lastSharesPid.current) return
    lastSharesPid.current = propertyId
    getPropertyShares({ propertyId }).then(setShares).catch(() => {})
  }

  // Modal-open latency (2026-07-17, user report "opens too slow"): the CARD already knows
  // this listing's property_id — cooperations/contacts/my-version never needed the lineage
  // response to start, they were just sequenced behind it. Fired at mount in PARALLEL with
  // the lineage fetch; the lineage-driven reloads below still run after (they add sibling
  // property ids the seed can't know) and overwrite with a superset. Two hops → one.
  useEffect(() => {
    const pid = seed?.property_id
    if (!pid) return
    reloadCoops(pid)
    reloadContacts(pid)
    reloadShares(pid)
    // ⚠️ NOT gated on agentId. An admin has no roster row, so this never asked and the card
    // reported "you have recorded nothing" over facts they had recorded themselves — measured
    // 2026-09-04 against a real ceiling height. The server resolves the viewer when no id is sent.
    getMyPropertyVersion(pid, agentId).then(setMyVersion).catch(() => {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // A link was minted or revoked in the ShareModal stacked over this one — force past
  // reloadShares' same-property guard, which exists to stop redundant fetches and would
  // otherwise swallow exactly the refetch that matters.
  useEffect(() => {
    if (!sharesEpoch) return
    reloadShares(data?.listing?.property_id || seed?.property_id, { force: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sharesEpoch])

  useEffect(() => {
    let cancelled = false
    // A REFETCH of the posting already on screen must not flash the modal into a spinner —
    // the agent is looking at it, and they just acted. Only a genuine anchor change
    // (re-anchoring onto a sibling) loads visibly.
    if (loadedViewRef.current !== view.id) setLoading(true)
    // Non-privileged viewers send their agent id so the server can withhold another agent's
    // scraped origin (owner identity swapped for the capturing agent's — 2026-07-12 rule).
    getListingLineage(view.id, { agentId: privileged ? null : agentId }).then(d => {
      if (cancelled) return
      setData(d); setDispute(d.district_dispute || null); setLoading(false)
      loadedViewRef.current = view.id
      // View log (2026-07-15): opening ANOTHER agent's listing detail is a logged,
      // attributable act (anti-silent-poaching) — the rep sees 👁 who/when on their
      // cards. Own postings are excluded (your own opens aren't news to you);
      // fire-and-forget and silent by design (no notice to the viewer).
      if (!view.isOwn && d.listing?.property_id) {
        logListingView({ propertyId: d.listing.property_id, listingId: view.id, context: 'lineage_modal' })
      }
      reloadCoops(d.listing?.property_id, (d.siblings || []).map(s => s.property_id))
      reloadContacts(d.listing?.property_id)
      reloadShares(d.listing?.property_id)
      { const pids = [...new Set([d.listing?.property_id, ...(d.siblings || []).map(s => s.property_id)].filter(Boolean))].slice(0, 20)
        if (pids.length) getBlockedAds(pids).then(v => { if (!cancelled) setBlockedAds(new Map((v?.blocked || []).filter(b => b.listing_id).map(b => [b.listing_id, b]))) }).catch(() => {})
        else setBlockedAds(null) }
      // FB publication counts (owner 2026-08-20: one row per spree, counts on it) — only
      // fetched when the family actually holds a facebook row, so 99% of opens skip it.
      const hasFb = [d.listing, d.origin, ...(d.siblings || [])].some(r => r && platformKey(r.source) === 'facebook')
      if (hasFb && d.listing?.property_id) {
        getFbShareStats(d.listing.property_id)
          .then(v => { if (!cancelled) setFbStats(new Map((v?.rows || []).map(r => [r.listing_id, r]))) })
          .catch(() => {})
      } else setFbStats(null)
      // "Which of these facts did I record myself?" — own-state facts render as the MY VERSION
      // card below, so an agent always sees their edits called out against the scraped facts.
      if (d.listing?.property_id) {          // agentId optional — see the seed fetch above
        getMyPropertyVersion(d.listing.property_id, agentId).then(v => { if (!cancelled) setMyVersion(v) }).catch(() => {})
      }
      // Viewings touching this property — matched by ad URL, the only key viewings carry for
      // core-picked properties (property_draft_id is nulled for them). Origin + platform
      // siblings included: a viewing booked off the myhome posting is still THIS property.
      const urls = [d.listing?.source_url, d.origin?.source_url, ...(d.siblings || []).map(s => s.source_url)]
      const coreIds = [d.listing?.listing_id, d.origin?.listing_id, ...(d.siblings || []).map(s => s.id)]
      getPropertyViewings(urls, coreIds).then(v => { if (!cancelled) setViewingsSummary(v) }).catch(() => {})
    })
      .catch(e => { if (!cancelled) { setErr(e.message); setLoading(false) } })
    return () => { cancelled = true }
  }, [view.id, lineageSeq])  // eslint-disable-line react-hooks/exhaustive-deps

  // Header now carries the address + freshness badge + facts line (area/rooms/floor/baths) —
  // was a bare "Listing" label with all of this repeated again inside the body. Computed here
  // since Modal's title/subtitle render before the loaded-data branch below does its own
  // destructuring.
  // While the lineage loads, the SEED (the card row the user just clicked) stands in for the
  // header — the address/facts they were looking at must not vanish into a spinner. Guarded
  // to the ORIGINAL anchor only: re-anchoring on a POSTED ON sibling is a different posting,
  // where the old card's data standing in would be wrong, not helpful.
  const seedActive = seed && view.id === listingId ? seed : null
  const hMerged = data?.merged || seedActive
  const modalTitle = hMerged ? (addressForDisplay(hMerged.address) || t('desk_lineage_fallback_title')) : t('desk_lineage_fallback_title')
  const hAgeBadge = hMerged ? listingAgeBadge(hMerged) : null
  // 🔴 BUILT HERE, AND THAT IS THE WHOLE FIX (Desk down 2026-09-10, owner-reported).
  // The first cut read `listing.property_id` inside the <Modal> tag below. `listing` is
  // destructured ~60 lines further on, INSIDE the loaded branch — so at the tag it is not in
  // scope at all and every open threw ReferenceError straight into the tab's error boundary.
  // `vite build` passed, the gate passed, 1469 tests passed: none of them RENDER this modal.
  // Only opening it does, which is the rule this repo already carries and I did not follow.
  // ⚠️ Keep every binding on this line one that exists ABOVE it. `data` is state and `hMerged`
  // is the header's own merged row, so both are safe; anything destructured out of `data` later
  // is not. A null property_id is fine — ShelfToggles renders nothing, which is correct while
  // the fetch is still in flight.
  const shelfRow = {
    property_id: data?.listing?.property_id ?? hMerged?.property_id ?? null,
    address: hMerged?.address ?? null,
    price: hMerged?.price ?? null,
    currency: hMerged?.currency ?? null,
  }
  // §9 sticky identity header (designer board 2026-07-23): price + last-move ticker +
  // freshness ride the title line — deep links (#datadesk/listing/<id>) land exactly here,
  // so the header alone must answer "what am I looking at". Ticker = newest price_history
  // row (server-normalized, trivial moves already dropped); freshness = last_seen_at chip.
  const hTicker = data?.price_history?.[0] || null
  const hLastSeen = data ? (data.origin || data.listing)?.last_seen_at : hMerged?.last_seen_at
  // The top row carries only what is SHORT and fixed-width — price, ticker, freshness, badge.
  // The address is neither, so it moved to its own full-width row (headerBelow) below.
  const modalTitleNode = hMerged ? (
    <span className="inline-flex items-center gap-2 flex-wrap">
      {hMerged.price != null && (
        <span className="text-lg font-bold tabular-nums whitespace-nowrap leading-none">
          <Money amount={hMerged.price} currency={hMerged.currency} />
        </span>
      )}
      {/* ⚠️ THE BADGE SITS SECOND, DIRECTLY AFTER THE PRICE, and the order is the fix rather than
          decoration (owner 2026-09-10: "still takes too much space — make it two lines"). Last in
          the row it was the item that wrapped, and a ~35px badge alone on its own line costs a
          full row for almost no ink. Short-and-fixed first, variable-width last. */}
      {hAgeBadge
        ? <Badge label={hAgeBadge.label} variant={hAgeBadge.variant} title={hAgeBadge.title} />
        : <Badge label={hMerged.status === 'active' ? t('desk_status_on_market') : t('desk_status_off_market')} variant={hMerged.status === 'active' ? 'green' : 'default'} />}
      {hTicker && (
        <span className={`text-xs font-semibold tabular-nums whitespace-nowrap ${hTicker.direction === 'down' ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-600 dark:text-amber-400'}`}
          title={`${hTicker.from ?? '—'} → ${hTicker.to ?? '—'} ${hTicker.currency || hMerged.currency || ''}`}>
          {/* comparand inline (designer 2026-07-23): this delta is vs the ad's OWN previous price */}
          {t('desk_delta_vs_prev')} {hTicker.direction === 'down' ? '▼' : '▲'}{hTicker.pct != null ? `${Math.abs(hTicker.pct)}%` : ''}
        </span>
      )}
    </span>
  ) : modalTitle
  // ⚠️ NOT `truncate`. Truncating is what the old one-row header did to it, and an ellipsised
  // address is the one thing this header exists to state. Two lines of address beat one line of
  // "…ის ქ. 32" — it wraps, and the row it is on has the whole sheet width to wrap into.
  // 🔑 MEASURED AT A 375px SHEET, and the measurement changed the design: THE HEADER'S HEIGHT IS
  // FLOOR-BOUND BY THE 44px BUTTONS, not by the text. A second chip line therefore costs NOTHING
  // (two 20px lines still fit inside 44), while every wrapped line of the address row costs a
  // real 20px. Three arrangements were rendered and read back before choosing:
  //   price·badge·time / address·spec   → chips 2 lines, address 2, header 104px
  //   price·badge / address·spec·time   → chips 1 line,  address 2, header 104px  ← this one
  //   price·badge·time·spec / address   → chips 3 lines, address 1, header 108px  (worse)
  // The third is the interesting failure: pushing everything up looks like it should help and is
  // the worst of the three, because the left column is only ~155px wide beside the buttons.
  // ⚠️ So the header cannot go far below ~100px at this width while carrying price, badge, time,
  // spec, address AND three 44px targets. Shrinking it further means dropping a fact or shrinking
  // the targets, and the targets were just made large on purpose.
  //
  // TWO BLOCKS ON THE LEFT, NOT THREE. The spec line used to ride `subtitle`, which renders as
  // its OWN <p> inside the narrow top column — so the header read price/time, then badge+spec,
  // then address: three rows on a phone. It joins the address row instead, muted and after a
  // separator, because both answer the same question ("what am I looking at") and neither needs
  // a row to itself.
  // 📌 THE SPEC GETS ITS OWN LINE (owner 2026-09-10: "why not move 108m to the lower line?").
  // Running it inline after the address was measurably fine and READ badly: the address wrapped
  // mid-way and the two facts interleaved across the break — "…ქ. 8 · 108მ², 3ოთ," then
  // "5/22ს, 2 სვ.წ · 👁 31 წთ". One line held the tail of an address and the head of a spec, so
  // neither could be scanned. Splitting them costs nothing in height (an address that wrapped to
  // two 20px lines becomes one 20px line plus one 18px muted line) and each line is now one fact.
  const modalAddressNode = hMerged ? (
    <>
      <p className="text-sm font-semibold leading-snug break-words">{modalTitle}</p>
      {(formatListingSummary(hMerged) || hLastSeen) && (
        <p className="text-xs text-muted-foreground leading-snug">
          {formatListingSummary(hMerged)}
          {formatListingSummary(hMerged) && hLastSeen ? ' · ' : ''}
          {hLastSeen && (
            <span className="whitespace-nowrap" title={fmtTbilisi(hLastSeen)}>👁 {timeAgo(hLastSeen)}</span>
          )}
        </p>
      )}
    </>
  ) : null
  return (
    <Modal onClose={onClose} title={modalTitleNode} wide
      headerBelow={modalAddressNode}
      /* 📌 TOP-RIGHT CORNER, NOT THE FOOTER — owner ruling 2026-09-10, made after seeing BOTH
         live. They first landed in the action row, on the reading that this modal's header is
         for identity and its footer for verbs (the 2026-07-25 rule that moved the share
         affordance off the subtitle line). The owner looked and moved them: on the CARD these
         two sit in the top-right corner, and a control that changes corner between the card and
         the card's own modal reads as a different control. Consistency of PLACE beat the
         identity/action split for these two. The share affordance's ruling is untouched.
         ⚠️ The row is handed over WHOLE, never {property_id}: toggleFavorite/toggleArchive
         SNAPSHOT address/price/currency onto the shelf entry when adding, and a bare id makes a
         shelf card with no address — indistinguishable from a broken save. `hMerged` is the
         header's own merged row, so the shelf records what the modal is actually showing. */
      headerRight={<ShelfToggles row={shelfRow}
        favorites={favorites} archived={archived}
        onToggleFavorite={onToggleFavorite} onToggleArchive={onToggleArchive} />}
      /* ⚠️ The share 🔗 used to ride this subtitle line. It moved to the sticky FOOTER, beside
         the contact pill (owner ruling 2026-07-25): contact and share are both "reach someone
         about this property" verbs, so they belong together and in ONE place across every
         modal — the header is for identity (what am I looking at), the footer for actions. */
      /* No `subtitle`: it now rides the address row above. Leaving it here too would print the
         spec twice and put back the third line this change removes. */>
      {loading ? (
        // Seeded skeleton (2026-07-17): first paint comes from the card's own data — photo,
        // price, facts — so opening a card never blanks what the user was already reading.
        // Only the lineage-dependent sections wait for the network.
        seedActive ? (
          <div className="space-y-3">
            {/* `desk-cq`: the second consumer of the card-form photo, which sizes itself
                against a query container and has none of its own here (index.css §1). Without
                it the photo silently renders full-width — an @container with no matching
                container matches nothing rather than falling back. */}
            <div className="desk-cq flex gap-3 items-start">
              <ListingGallery listing={seedActive} size="card" />
              <div className="flex-1 min-w-0">
                {seedActive.price != null && (
                  <p className="text-lg font-bold leading-6 tabular-nums">
                    {`${Number(seedActive.price).toLocaleString('en-US')} ${seedActive.currency || ''}`.trim()}
                  </p>
                )}
                <p className="text-sm text-muted-foreground mt-0.5">
                  {[seedActive.kind,
                    seedActive.rooms != null ? tLog('desk_entry_rooms_abbrev', { rooms: seedActive.rooms }) : null,
                    seedActive.area_sqm != null ? tLog('desk_entry_area_abbrev', { area: seedActive.area_sqm }) : null,
                    seedActive.floor != null ? (seedActive.floors_total != null ? tLog('desk_entry_floor_position', { floor: seedActive.floor, total: seedActive.floors_total }) : tLog('desk_entry_floor_only', { floor: seedActive.floor })) : null,
                  ].filter(Boolean).join(' · ')}
                </p>
              </div>
            </div>
            <p className="text-xs text-muted-foreground animate-pulse">{t('desk_loading')}</p>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground py-6 text-center">{t('desk_loading')}</p>
        )
      ) : err ? (
        <p className="text-sm text-destructive py-6 text-center">{tLog('desk_lineage_couldnt_load', { err })}</p>
      ) : (() => {
        const { listing, origin, origin_contact, merged: mergedRaw, siblings = [], price_history = [] } = data
        // T24 (owner 2026-09-03): "we need my version to be listing not list in listing".
        // MY VERSION stops being a list of fields inside the card and becomes a POSTING you can
        // open like any other row in POSTED ON — the same card, with the agent's own recorded
        // values standing in wherever they have one.
        // ⚠️ An OVERLAY, never a substitute: a fact the agent has not recorded keeps the ad's
        // value, so opening my version never blanks a field the property genuinely has. That is
        // also what makes it honest — "my version" is the ad plus my corrections, which is what
        // the agent actually believes, not a separate parallel listing they never filled in.
        const merged = view.mine ? myVersionOf(mergedRaw, myVersion?.facts) : mergedRaw
        // The active image set drives the whole mosaic. No set (fetch failed, older server,
        // nothing stored) → `merged.images`, i.e. exactly the pre-switcher screen.
        // T24 (owner 2026-09-03): "also images whatever agent edits in gallery is their version."
        // With MY VERSION open the photographs are not a tab choice — they are the agent's own
        // uploaded set, or it is not their version. Falls back to whatever is active when they
        // have uploaded nothing: an agent who never touched the gallery has asserted nothing
        // about the photographs, and an empty "my version" would state an absence they never
        // claimed. Same overlay rule as the facts.
        // ⚠️ The TABS are deliberately left alone — they still show every set and still switch.
        // Overriding the hero is a statement about which version you asked for; disabling the
        // switcher would take away the ability to compare, which is the reason it exists.
        const heroSet = (view.mine ? myVersionImageSet(imgSets.sets) : null) || imgSets.active
        const heroImages = heroSet ? heroSet.urls : (merged.images || [])
        const heroListing = heroSet ? { ...merged, images: heroSet.urls } : merged
        // PROPERTY-LEVEL CLOSED STATE — a UI projection across the listing FAMILY (owner
        // ruling 2026-07-30: "derive it in the UI, don't mark siblings"). record_deal_closure
        // writes the lifecycle on the ONE ad the agent chose; the other 12 postings of the same
        // flat keep their own honest per-ad state. But "is this flat still available" is a
        // property question, so every property-level surface in this modal (status card, the
        // deal-close door, the market-presence dot) reads THIS projection: closed if ANY family
        // ad is recorded sold/rented. Each sibling row still shows its own per-ad state.
        // ⚠️ SCOPED TO (PROPERTY × INTENT CLASS), not property (audit fix 2026-07-30). The first
        // version asked "is anything on this property closed?", which is not a question anyone
        // has: renting a flat out says nothing about whether it was sold. 19 real properties
        // carry both a sale and a tenancy listing today, and on every one of them the
        // property-wide version would have suppressed the sale-side actions the moment the rent
        // deal closed — hiding live work behind a settled fact about a different deal.
        // The classes mirror record_deal_closure's own outcome→intent mapping, so the projection
        // and the writer partition the world identically.
        const familyClosure = (() => {
          const INTENT_CLASS = { sale: 'sale', rent: 'tenancy', girao: 'tenancy', daily_rent: 'tenancy', ijara: 'tenancy' }
          const TERMINAL_CLASS = { status_sold: 'sale', status_rented_out: 'tenancy' }
          const anchorClass = INTENT_CLASS[listing.intent] || null
          if (!anchorClass) return null      // unknown intent → claim nothing, never guess
          const fam = [
            { lc: merged.lifecycle_status, at: merged.lifecycle_observed_at, id: listing.listing_id, intent: listing.intent },
            { lc: listing.lifecycle_status, at: listing.lifecycle_observed_at, id: listing.listing_id, intent: listing.intent },
            ...(origin ? [{ lc: origin.lifecycle_status, at: origin.lifecycle_observed_at, id: origin.listing_id, intent: origin.intent }] : []),
            ...siblings.map(s => ({ lc: s.lifecycle_status, at: s.lifecycle_observed_at, id: s.id, intent: s.intent })),
          ].filter(f => TERMINAL_CLASS[f.lc] === anchorClass
            // The ad's OWN intent must also belong to the anchor's class. Guards the case where a
            // sibling's lifecycle and its intent disagree (a sale ad carrying status_rented_out
            // would otherwise be read as closing the tenancy side of a property it never had).
            && (INTENT_CLASS[f.intent] || anchorClass) === anchorClass)
          // Newest terminal observation wins when several exist (e.g. an old close plus a fresh one).
          fam.sort((a, b) => new Date(b.at || 0) - new Date(a.at || 0))
          return fam[0] ? { ...fam[0], cls: anchorClass } : null
        })()
        // Non-representative viewer (2026-07-14, owner ruling): their path to this property
        // is the representative in the banner — cooperation with the owner, meetings, and
        // recording "owner's stated conditions" are all REPRESENTATIVE actions, so the add
        // affordances disappear; recorded knowledge stays readable.
        const notRep = data.viewer_is_representative === false
        // Freshness reads off whichever listing the market actually moves on — for a reshare
        // that's the origin ad (the agent's own posting is rarely re-scraped).
        const freshSrc = origin || listing
        const firstSeen = freshSrc.created_at || freshSrc.listed_at
        const lastSeen = freshSrc.last_seen_at
        const diff = k => !!origin && listing[k] != null && listing[k] !== origin[k]
        // Step 5 display honesty (PUBLICATION_AND_OWN_LISTINGS §5, 2026-08-03): a claim the
        // gateway stored as 'estimated' (confidence 0.40) must not render at the same weight
        // as a verified fact — "estimated/0.40 renders as a bare 45 მ²" was the complaint.
        // merged.fact_status ships each fact's non-confirmed claim status (server lineage
        // payload); ≈ is the platform's existing near/approximate mark (CHECK_MARK.partial).
        const estMark = k => (merged.fact_status && merged.fact_status[k]) ? '≈ ' : ''
        // Meeting seed (property + ranked contact + agreed cooperation terms) — powers
        // the 📅＋ chip in this modal's own MEETING section (below COOPERATION).
        // ── Meetings, computed ONCE (PILL_SYSTEM_SPEC §2) ─────────────────────────────
        // The header state pill and the row list are two views of the same ordered set, so
        // they must not each derive it — that is how a header says "no meetings" over a list
        // of three. Ordering: soonest UPCOMING first, then most recent past.
        //
        // CORE meetings merged in (owner report 2026-07-30): viewingsSummary is the LEGACY
        // viewings endpoint (URL/core_listing_id-matched) and never sees property-anchored
        // core meetings — the card face showed a meeting this card denied. Same source as the
        // face (meetingsIndex.byProperty, the merged feed), so the two can't disagree;
        // backfilled twins dedupe on legacy_viewing_id.
        const meetingRows = (() => {
          const now = Date.now()
          const propIds = [...new Set([listing.property_id, ...siblings.map(s => s.property_id)].filter(Boolean))]
          const coreRows = meetingsIndex ? propIds.flatMap(pid => meetingsIndex.byProperty?.[pid] || []) : []
          const seenIds = new Set((viewingsSummary || []).map(v => String(v.id)))
          const merged2 = [...(viewingsSummary || []), ...coreRows.filter(v =>
            !seenIds.has(String(v.id)) && !(v.legacy_viewing_id && seenIds.has(String(v.legacy_viewing_id))))]
          return merged2
            .map(v => ({ v, at: viewingSchedAt(v) }))
            .sort((a, b) => {
              const aUp = a.at != null && a.at >= now, bUp = b.at != null && b.at >= now
              if (aUp !== bUp) return aUp ? -1 : 1
              return aUp ? a.at - b.at : (b.at || 0) - (a.at || 0)
            })
        })()
        // The header pill names the NEXT meeting when one is upcoming, else the last outcome —
        // "when is it" before "what happened", the same question the ordering answers.
        const nextMeetingSummary = () => {
          if (!meetingRows.length) return null
          const now = Date.now()
          const up = meetingRows.find(r => r.at != null && r.at >= now)
          if (up) return { tone: 'info', text: viewingSchedText(up.at) }
          const last = meetingRows[0]
          const tone = last.v.status === 'closed' ? 'ok' : last.v.status === 'failed' ? 'warn' : 'neutral'
          return { tone, text: t(`dv_status_${last.v.status}`) || last.v.status }
        }
        const buildMeetingSeed = () => {
          const DEAL = { sale: 'trade', rent: 'lease' }
          const activeCoop = coops.find(c => c.status !== 'rejected' && c.status !== 'ended') || null
          const dflt = pcontacts?.contacts?.find(c => c.party_id === pcontacts.default_party_id) || null
          return {
            __source: 'core', id: listing.listing_id, property_id: listing.property_id || merged.property_id || null,
            address: merged.address, rooms_count: merged.rooms, apt_floor: merged.floor,
            total_floors: merged.floors_total, area_sq_m: merged.area_sqm,
            deal_type: DEAL[merged.intent] || merged.intent, price: merged.price, price_unit: merged.currency,
            source_url: merged.source_url,
            contact_name: dflt ? dflt.name : ((origin ? (origin_contact?.name || origin.author_name) : listing.author_name) || null),
            contact_phone: dflt ? dflt.phone : ((origin ? origin_contact?.phone : listing.author_phone) || null),
            contacts_ranked: pcontacts?.contacts?.length ? pcontacts.contacts : null,
            default_contact_party_id: pcontacts?.default_party_id || null,
            cooperation: coopMeetingSeed(activeCoop),
            // Property-rep PREVIEW for the meeting form (owner 2026-07-29: "my own
            // property says no representative on record" — counterintuitive). The
            // modal already knows who represents: the viewer themselves, or the
            // representative the masking banner names. Preview only — the server
            // still snapshots the authoritative rep at create (MEETINGS_MODEL §3).
            property_rep_name: data.viewer_is_representative === false
              ? (data.representative?.name || null) : (agentName || null),
            needs_property_access: data.viewer_is_representative === false,
          }
        }
        // ── Combined deal editor entry (2026-07-29): opening EITHER side seeds BOTH tabs.
        // Terms default = this card's primary listing in the shape termsModeFor picks;
        // coop default = the live grouped cooperation, else a fresh one against the
        // owner contact (same seed the 🤝＋ pill used).
        const defaultTermsFor = () => {
          const liveCoop = (coops || []).find(c => !['ended', 'rejected'].includes(c.status))
          return { listingId: listing.listing_id || listingId, mode: termsModeFor(listing.intent, liveCoop?.transaction_intent || null) }
        }
        const defaultCoopFor = () => {
          const live = (coops || []).find(c => !['ended', 'rejected'].includes(c.status)) || coops[0]
          if (live) return { propertyId: live.property_id, cooperationId: live.id, propertyLabel: merged.address || null }
          const resolverOwner = pcontacts?.contacts?.find(c => !c.is_agent && !c.via_agent) || null
          const cp = resolverOwner?.party_id || origin?.author_party_id || listing.author_party_id
          if (!cp || !listing.property_id) return null
          return { propertyId: listing.property_id, propertyLabel: merged.address || null,
            contactPartyId: cp, contactName: resolverOwner?.name || (origin ? (origin_contact?.name || origin.author_name) : listing.author_name) || null,
            seedIntent: coopIntentOf(merged.intent), seedCurrency: merged.currency || null, seedPrice: merged.price ?? null }
        }
        const openDealEditor = (tab, { coop = null, termsListingId = null, termsMode = null } = {}) => {
          setCoopModal(coop || defaultCoopFor())
          setTermsEdit(termsListingId ? { listingId: termsListingId, mode: termsMode || defaultTermsFor().mode } : defaultTermsFor())
          setDealTab(tab)
        }
        // The URL-restore effect's only way in (see its comment): this door is defined inside
        // the render closure that owns the seeds, so it cannot be called from an effect
        // without a handle. Assigning a ref during render is safe here — it stores a plain
        // function and triggers nothing; the effect reads it in the commit that follows.
        openDealEditorRef.current = openDealEditor
        return (
          <div className="space-y-4">
            {/* Representation banner (2026-07-14): this viewer is NOT the representing
                agent — the deal routes through them (owner links/contacts are already
                withheld server-side; this says WHO to cooperate with, up front). */}
            {/* C1 (2026-07-14): the phone is never printed and the Cooperate dial
                button is gone — its open number defeated its own audit log (agents
                read it and called untracked). ContactReveal IS the logged action now;
                the reveal event carries the cooperate intent. */}
            {data.viewer_is_representative === false && data.representative?.name && (
              <div className="rounded-lg border border-primary/40 bg-primary/10 px-3 py-2 text-sm flex items-center justify-between gap-3 flex-wrap">
                <span>
                  <Icon name="handshake" /> {tLog('desk_lineage_rep_banner', { name: data.representative.name })}
                </span>
                {data.representative.party_id && (
                  <ContactReveal targetPartyId={data.representative.party_id}
                    propertyId={listing.property_id} listingId={listing.listing_id}
                    context="cooperate via representative"
                    shareListingId={listing.listing_id} shareAddress={merged.address} />
                )}
                {/* C3: escalation when the rep doesn't answer — full-width row below */}
                <div className="w-full">
                  <AccessRequestPanel propertyId={listing.property_id} listingId={listing.listing_id} />
                </div>
              </div>
            )}
            {/* MarketReportPanel moved to the RIGHT column above the cooperation card
                (owner 2026-07-30) — it renders as the სტატუსი card there. */}
            {/* THE DEAL read-only face removed 2026-07-28 (owner ruling): the full
                COOPERATION card below now renders for EVERY viewer — a non-rep may
                contact the owner and open their OWN cooperation; what stays earned
                is other agents' numbers (blind-first projection masks them until
                the viewer records their own participation — "does his part"). */}
            {/* §9 two-column board (designer 2026-07-23): LEFT = "what is this?" (photos,
                facts, location, my version, price history), RIGHT = "our relationship"
                (contact, cooperation, meetings, terms, notes, POSTED ON). lg breakpoint —
                below it the columns unstack into one scroll in this same order (mobile
                answer). min-w-0 on both: POSTED ON rows carry long URLs/addresses that
                otherwise blow the grid track open. */}
            <div className="grid gap-4 lg:grid-cols-2 lg:items-start">
            <div className="space-y-4 min-w-0">
            {/* §9 photo mosaic (designer ruling 2026-07-23: photos are the hero, the map is
                ONE TILE in the mosaic — location is a glance among the visual set, not the
                headline). The hero stays the FULL interactive carousel (map slide, dead-photo
                pruning — all existing ListingGallery behavior); the tile strip below previews
                the next photos + a 📍 map tile, and clicking any tile jumps the hero to that
                slide ("viewing happens in the hero"). With zero photos the map already IS the
                hero (ListingGallery's only slide) — same fallback rule as the card. */}
            {/* Image-set switcher (2026-08-17, design/Image set switcher.dc.html). The hero and
                the tile strip below both read `heroImages` — the ACTIVE set — so switching a tab
                moves the whole mosaic, not just the big photo. With no sets loaded (fetch failed,
                older server, property with nothing stored) `heroImages` is `merged.images` and
                every line below behaves exactly as it did before the switcher existed: the
                feature is additive and its failure mode is the previous screen, never an error.
                ⚠️ The tab bar is the one place in the app that renders watermarked photos — see
                the header of ImageSetTabs.jsx for why that exception is deliberate. */}
            <div>
              <div className="relative"
                onMouseMove={e => {
                  const r = e.currentTarget.getBoundingClientRect()
                  const inBand = r.height > 0 && (e.clientY - r.top) / r.height >= PILL_BAND
                  setPillsOn(v => v === inBand ? v : inBand)
                }}
                onMouseLeave={() => setPillsOn(false)}>
                <ListingGallery listing={heroListing} size="large" index={heroSlide} onIndexChange={setHeroSlide} zoomable />
                {imgSets.sets.length > 0 && (
                  <div className={`absolute left-0 right-0 bottom-0 z-10 px-2 pt-3.5 pb-2 pointer-events-none
                                  bg-gradient-to-t from-black/90 via-black/60 to-transparent transition-opacity
                                  ${!canHover || pillsOn ? '' : 'opacity-0'} focus-within:opacity-100`}>
                    <div className="pointer-events-auto">
                      {/* Switching a set KEEPS YOUR PLACE (owner 2026-08-17: "when switching a
                          set make next front image from that set"). Resetting to 0 threw away
                          the comparison the tabs exist for — you pick photo 5, switch to the
                          watermarked rendition, and want photo 5 stamped, not photo 1 again.
                          heroSlide can be a number, a URL (clicked from the tile strip) or
                          'map', so the index is resolved against the OUTGOING set and clamped
                          to the incoming one, which may be shorter. */}
                      <ImageSetTabs sets={imgSets.sets} activeId={imgSets.activeId}
                        onPick={id => {
                          const cur = imgSets.active?.urls || []
                          const at = typeof heroSlide === 'number' ? heroSlide
                            : (typeof heroSlide === 'string' && heroSlide !== 'map') ? Math.max(cur.indexOf(heroSlide), 0)
                            : 0
                          const next = imgSets.sets.find(s => s.id === id)
                          imgSets.setActiveId(id)
                          setHeroSlide(Math.min(at, Math.max((next?.urls?.length || 1) - 1, 0)))
                        }} />
                    </div>
                  </div>
                )}
              </div>
              {(heroImages || []).length > 0 && (() => {
                /* EVERY photo gets a tile (owner 2026-08-09: "when they open modal there also
                   allow them to see all"). It was `slice(1, 6)` with a `+N` veil on the last
                   tile — and the veil was a dead end, not a door: it named a number of photos
                   and gave no way to reach them, so the only way to see photo 8 was to click
                   the hero seven times past a map slide. The grid wraps, so a 20-photo listing
                   is three or four rows of tiles rather than a lie about there being six.
                   ⚠️ THIS WAS `slice(1)` AND IS NOW `slice(0)` — owner 2026-08-17: "also show
                   selected image in below image set; if there is only one image show that image
                   and map". The old comment justified skipping tile 1 because it duplicated the
                   hero's opening slide and "read as an off-by-one". That reasoning died with the
                   set switcher: the hero is no longer necessarily photo 1 (a tab switch carries
                   your position into the new set), so a strip starting at photo 2 hid whichever
                   photo you were actually looking at, and a ONE-photo set rendered a strip with
                   no photo in it at all. The duplicate it warned about is now the SELECTED tile,
                   ringed below — which is an orientation cue, not an off-by-one. */
                const tiles = heroImages
                // Which tile is the hero? heroSlide is a number, a URL, or 'map'.
                const at = typeof heroSlide === 'number' ? heroSlide % Math.max(tiles.length + 1, 1)
                  : typeof heroSlide === 'string' && heroSlide !== 'map' ? tiles.indexOf(heroSlide)
                  : -1
                return (
                  <div className="grid grid-cols-6 gap-1 -mt-1">
                    {tiles.map((u, i) => (
                      <button key={`${i}:${u}`} type="button"
                        className={`relative aspect-[4/3] rounded overflow-hidden bg-muted ${i === at ? 'ring-2 ring-primary' : ''}`}
                        onClick={() => setHeroSlide(i)}>
                        <img src={u} alt="" loading="lazy" className="w-full h-full object-cover" />
                      </button>
                    ))}
                    <button type="button" className="relative aspect-[4/3] rounded overflow-hidden bg-muted"
                      onClick={() => setHeroSlide('map')}>
                      {merged.lat != null && merged.lng != null && (
                        <img src={osmTileUrl(merged.lat, merged.lng)} alt="" loading="lazy" className="w-full h-full object-cover" />
                      )}
                      <span className="absolute inset-0 bg-black/40 text-white text-xs font-semibold flex items-center justify-center gap-0.5">
                        📍 {t('desk_lineage_map_tile')}
                      </span>
                      {/* Tiphys placed this pin (owner 2026-09-07): the AI mark sits ON the map
                          tile, replacing the retired agent-activity list. street_linked comes from
                          merged.agent_activity, which the lineage endpoint fails soft to null. */}
                      {merged.agent_activity?.tiphys?.street_linked && (
                        // ⚠️ pointer-events-none (owner 2026-10-28, phone): on a ~60px tile the mark took the tap and
                        // opened its sheet instead of the map. A tap on the tile is a tap on the map; the mark only shows.
                        <span className="absolute top-1 right-1 pointer-events-none">
                          <AiMark mark={{ tier: 'ai', agent: 'tiphys', action: 'placed',
                                          version: merged.agent_activity.tiphys.house_number ? t('desk_agent_place_with_number') : t('desk_agent_place_street') }} />
                        </span>
                      )}
                    </button>
                  </div>
                )
              })()}
              {imgSets.sets.length > 0 && (
                <div className="mt-2 space-y-2">
                  <div className="flex items-baseline justify-between gap-2">
                    <p className={SECTION_LABEL}>{t('desk_imgset_section')}</p>
                    <ImageSetTotal sets={imgSets.sets} distinctPhotos={imgSets.distinctPhotos} />
                  </div>
                  {/* One set = nothing to switch to; say so instead of leaving a lone pill to
                      imply the others are still loading (owner call 3 keeps the pill AND the
                      sentence). ⚠️ NOTHING renders for the multi-set case any more — the per-set
                      description and the mark caveat were removed at the owner's request
                      2026-08-17; see the note in ImageSetTabs.jsx before adding copy back. */}
                  {imgSets.sets.length === 1 && (
                    <p className="text-xs leading-relaxed text-muted-foreground">{t('desk_imgset_only_one')}</p>
                  )}
                </div>
              )}
            </div>
            <div className={card}>
              {/* isOwn = true only for an agent's own reshare (listing_readonly) — "your posting"
                  is factually wrong for a plain scraped ad (scraped_listing_readonly), which was
                  authored by someone else and merely captured by one of our agents. */}
              {/* T24: the card is now three things depending on which POSTED ON row is open —
                  someone else's ad, our posting, or MY VERSION. The label is the only thing that
                  says which, so it must never be left saying "THE AD" over the agent's own
                  values (rule 6: a surface that names something the reader cannot verify). */}
              <p className={`${SECTION_LABEL} mb-2`}>
                {view.mine ? t('desk_lineage_my_version_section')
                  : view.isOwn ? t('desk_lineage_your_posting_section') : t('desk_lineage_the_ad_section')}
              </p>
              {view.mine && (
                <div className="flex items-start justify-between gap-3 mb-2">
                  <p className="text-[11px] text-muted-foreground/80">{t('desk_my_version_overlay_note')}</p>
                  {/* The edit door followed my-version here from the old list card. It is the
                      same call — what changed is that it now sits on the thing it edits. */}
                  {onEditProperty && listing.property_id && !notRep && (
                    <button type="button" className="text-xs text-primary shrink-0" data-my-version-edit
                      onClick={() => onEditProperty({ propertyId: listing.property_id, listingId: listing.listing_id || listingId })}>
                      {t('desk_lineage_edit_property_button')}
                    </button>
                  )}
                </div>
              )}
              {lineageFieldRow(t('desk_lineage_field_price'), merged.price != null ? `${merged.price} ${merged.currency || ''}`.trim() : null, diff('price'))}
              {/* labels.js (designer vocab rule 2026-07-23): raw enum values never render */}
              {lineageFieldRow(t('desk_lineage_field_intent'), intentLabel(merged.intent), diff('intent'))}
              {lineageFieldRow(t('desk_lineage_field_kind'), kindLabel(merged.kind), diff('kind'))}
              {lineageFieldRow(t('desk_lineage_field_rooms'), merged.rooms != null ? `${estMark('rooms')}${merged.rooms}` : null, diff('rooms'))}
              {/* Bedrooms is captured (overlay field, server lineage merge) and filterable,
                  but had no row here — so the one number a client actually states was the one
                  the fact sheet omitted (owner 2026-08-03). */}
              {lineageFieldRow(t('desk_lineage_field_bedrooms'), merged.bedrooms != null ? `${estMark('bedrooms')}${merged.bedrooms}` : null, diff('bedrooms'))}
              {lineageFieldRow(t('desk_lineage_field_area'), merged.area_sqm ? `${estMark('area_sqm')}${merged.area_sqm} m²` : null, diff('area_sqm'))}
              {lineageFieldRow(t('desk_lineage_field_floor'), merged.floor != null ? `${estMark('floor')}${merged.floors_total != null ? `${merged.floor}/${merged.floors_total}` : merged.floor}` : null, diff('floor'))}
              {/* Condition + project (2026-07-21): empty ≠ hidden for the rep — an absent
                  value renders a "? fill in" pill whose popover answers IN PLACE (owner
                  request: same UX as the match panel's stance popovers, no modal). The pick
                  writes a feature flag via setPropertyFeature; filledFacts echoes it into
                  the row because merged.* (scrape claims) won't reflect a feature write.
                  `lineageFieldRow(...) ?? pill` works because it returns exactly null on an
                  empty value. */}
              {(() => {
                const canFill = agentId && listing.property_id && !notRep
                const condOptions = [...PROP_STATE, ...APT_CLASS].map(([code, label]) => ({ key: code, label }))
                // proj_* features (0148/0150) — vocab-driven so DB renames propagate; the
                // subcategory filter is primary, the prefix a fallback for a stale /features.
                const projOptions = featVocab
                  .filter(f => f.subcategory === 'project' || String(f.code).startsWith('proj_'))
                  .map(f => ({ key: f.code, label: f.name_ka || f.name_en || f.code }))
                // What the scrape DID record, as features: scrape_myhome emits condition and
                // project only inside tagKeys → commit_feature, so for most captures the
                // claim (merged.condition/.project) is NULL while the property carries
                // frame_white / proj_chekhuri rows. merged.feature_keys ships those codes
                // (building-axis only); labels resolve from the cached /features vocab, with
                // the PROP_STATE/APT_CLASS constants as fallback for the ladder codes. A
                // fill-in pill is only honest AFTER this lookup comes back empty — asking
                // the agent to pick what myhome already told us fabricates a gap
                // (user-caught 2026-07-21).
                const fk = merged.feature_keys || []
                const featLabel = code => featVocab.find(f => f.code === code)?.name_ka
                  || [...PROP_STATE, ...APT_CLASS].find(([c]) => c === code)?.[1] || code
                // CONDITION vs BUILDING STATUS are DIFFERENT axes (owner-caught 2026-07-24:
                // "ძველი აშენებული is building status, condition is ახალი რემონტით"). The source
                // portals list them as separate fields (მდგომარეობა vs სტატუსი), so they must
                // NOT be merged. Condition = the finish/renovation state (reno_*/frame_* +
                // the 'condition'/'renovation'/'frame' vocab categories). Building status = the
                // building's AGE (cls_* = ახალი/ძველი აშენებული / მშენებარე). The old filter
                // dumped cls_* into condition, so a bare "ძველი აშენებული" read as a renovation.
                // AXIS MEMBERSHIP, not prefix (2026-07-24, designer ruling + the live vocab
                // dump): `/^(cls_|class_)/` used to mean "building status", which filed the
                // apartment-class ladder (class_1…8) under building age even though the
                // fill-in below offers those codes as CONDITION and the match scorer counts
                // them as the condition ladder. Building status = the cls_ family only; the
                // class_ ladder renders with condition, where it is written and scored.
                // ⚠️ Keep asking featureAxes — the CONFLICT row (designed 2026-07-24) keys off
                // the same module's isExclusiveAxis, and a second, private notion of "which
                // axis is this" is exactly how the two drift apart.
                const isCondition = c => [AXIS.CONDITION, AXIS.APT_CLASS].includes(axisOf(c, featVocab))
                const isBuildStatus = c => axisOf(c, featVocab) === AXIS.BUILD_STATUS
                // ── CONFLICT resolution (board §9) ──────────────────────────
                // `features` carries per-row provenance (source_kind/confidence/last_seen_at);
                // `feature_keys` is the old flat list. Against a server that hasn't shipped
                // the new field, conflicts simply don't render — the row falls back to the
                // join it always was, rather than pretending nothing disagrees.
                const conflicts = axisConflicts(merged.features || [], featVocab)
                const conflictOf = a => conflicts.find(c => c.axis === a) || null
                // An override the agent JUST made — optimistic, same echo as filledFacts.
                const winnerFor = a => resolvedAxes[a] || conflictOf(a)?.winner?.feature_key || null
                // Codes to render for one host row: an exclusive axis contributes ONE value
                // (its winner), everything else contributes itself. Two axes can share a row —
                // apartment class rides with condition (host-row ruling 2026-07-24).
                const codesFor = pred => {
                  const shown = []
                  for (const c of fk.filter(pred)) {
                    const a = axisOf(c, featVocab)
                    const w = isExclusiveAxis(c, featVocab) ? winnerFor(a) : null
                    if (!w) { shown.push(c); continue }
                    if (!shown.includes(w)) shown.push(w)     // the winner replaces every peer
                  }
                  return shown
                }
                // Flags belonging to a host row, minus any axis the agent just resolved.
                const flagsFor = pred => conflicts.filter(c => c.flagged && !resolvedAxes[c.axis]
                  && fk.some(code => pred(code) && axisOf(code, featVocab) === c.axis))
                const featCondition = codesFor(isCondition).map(featLabel).join(' · ') || null
                const featBuildStatus = codesFor(isBuildStatus).map(featLabel).join(' · ') || null
                const featProject = codesFor(c => axisOf(c, featVocab) === AXIS.PROJECT).map(featLabel).join(' · ') || null
                // The value cell = text + any flags for that row. Kept separate from the plain
                // string above because factInfo()'s ⓘ lookup resolves a DISPLAY STRING, not a node.
                const withFlags = (text, pred) => {
                  const flags = flagsFor(pred)
                  if (!text || !flags.length) return text
                  return (<span className="inline-flex items-center gap-1.5">{text}
                    {flags.map(c => (
                      <ConflictFlag key={c.axis} conflict={c} labelOf={featLabel}
                        propertyId={listing.property_id} agentId={agentId}
                        onResolved={code => setResolvedAxes(m => ({ ...m, [c.axis]: code }))} />
                    ))}</span>)
                }
                // 'feature_keys' in merged = this response came from a build that actually
                // looked — the gate that keeps pills honest on an un-deployed server, where
                // "we can't see it" must not render as "nobody recorded it".
                const serverLooked = 'feature_keys' in merged
                // ⓘ (0162): the read-only rows are where agents actually stare at these
                // terms (user-caught 2026-07-21 — "ქალაქური" showed no dot: only the edit
                // ladders were wired). Values are display strings; featureDefOf resolves
                // them via name/alias. A ' · ' multi-part join resolves to nothing → no dot.
                const condVal = merged.condition || featCondition || filledFacts.condition
                const buildStatusVal = featBuildStatus
                const projVal = merged.project || featProject || filledFacts.project
                // §9 3-step, step 3: a value the agent JUST recorded through the pill carries
                // ✎ author provenance in place — the dashed vocabulary retires for that fact.
                const condFromFill = !merged.condition && !featCondition && !!filledFacts.condition
                const projFromFill = !merged.project && !featProject && !!filledFacts.project
                const withProv = (val, fromFill) => !val ? val : !fromFill ? val
                  : <span>{val} <span className="text-xs text-muted-foreground">✎ {agentName || t('desk_lineage_my_version_section')}</span></span>
                const factInfo = val => featureDefOf(featVocab, val)
                  ? <InfoDot active={factDefKey === val} onTap={() => setFactDefKey(k => k === val ? null : val)} />
                  : null
                return (<>
                  {lineageFieldRow(t('desk_lineage_field_condition'), withFlags(withProv(condVal, condFromFill), isCondition), diff('condition'), factInfo(condVal))
                    ?? (canFill && serverLooked ? <FillFactPill label={t('desk_lineage_field_condition')} options={condOptions}
                          propertyId={listing.property_id} agentId={agentId}
                          onRecorded={o => setFilledFacts(f => ({ ...f, condition: o.label }))} /> : null)}
                  {/* Building status (სტატუსი) — the building's AGE (ახალი/ძველი აშენებული),
                      a SEPARATE axis from the renovation condition above (2026-07-24). */}
                  {buildStatusVal && lineageFieldRow(t('desk_lineage_field_build_status'), withFlags(buildStatusVal, isBuildStatus), false, factInfo(buildStatusVal))}
                  {lineageFieldRow(t('desk_lineage_field_project'), withFlags(withProv(projVal, projFromFill), c => axisOf(c, featVocab) === AXIS.PROJECT), diff('project'), factInfo(projVal))
                    ?? (canFill && serverLooked && projOptions.length > 0
                        ? <FillFactPill label={t('desk_lineage_field_project')} options={projOptions}
                            propertyId={listing.property_id} agentId={agentId}
                            onRecorded={o => setFilledFacts(f => ({ ...f, project: o.label }))} /> : null)}
                  <FeatureDefPanel def={featureDefOf(featVocab, factDefKey)} onClose={() => setFactDefKey(null)} />
                  {/* ── AGENT-READ AMENITIES (owner, 2026-08-27): "if source fields are empty
                      and idmon extracts data it takes over, and we see clearly these fields
                      are created by idmon." Every amenity a named agent read out of the
                      advert, each wearing its agent's ✦ — hover shows WHO, the verbatim
                      quote, version and date. aiMarkFor silences the mark when a HUMAN row
                      answers the same key, which is the takeover rule made visual: the agent
                      fills what nobody recorded, and steps back the moment somebody does.
                      Condition/project keys are excluded — they render in their own rows
                      above and a second appearance would break the same-fact-never-twice
                      rule. */}
                  {(() => {
                    const rows = (merged.features || []).filter(r => r.source_agent && !/^(proj_|class_|cls_|frame_|reno_)/.test(r.feature_key))
                    if (!rows.length) return null
                    // Observability audit 2026-08-27: grouped by KEY, keeping every agent —
                    // the old first-row-wins map silently dropped the second agent, so
                    // "Idmon and Lynceus both saw the balcony" rendered as one arbitrary
                    // author and a text-vs-photo disagreement rendered as no disagreement.
                    // Three visual states per chip: AGREEMENT = one label wearing every
                    // supporting agent's ✦ side by side; CONFLICT = the label plus ⚡ and
                    // both marks, never a silent winner; stated ABSENCE (present:false,
                    // no positive anywhere) = struck-through label — "checked and said no",
                    // which must never look like "never checked".
                    const byKey = new Map()
                    for (const r of rows) { if (!byKey.has(r.feature_key)) byKey.set(r.feature_key, []); byKey.get(r.feature_key).push(r) }
                    return lineageFieldRow(t('desk_lineage_field_agent_read'), (
                      <span className="inline-flex flex-wrap items-center gap-x-2 gap-y-1">
                        {[...byKey.entries()].map(([key, group]) => {
                          const pos = group.filter(x => x.present !== false)
                          const neg = group.filter(x => x.present === false)
                          const conflict = pos.length > 0 && neg.length > 0
                          const absent = !pos.length && neg.length > 0
                          const shown = conflict ? [pos[0], neg[0]] : (absent ? neg : pos)
                          const sibs = (merged.features || []).filter(x => x.feature_key === key)
                          return (
                            // 2026-08-27 usability test: a strikethrough is CSS-only, and it
                            // sat inside a row headed "Equipment" — a list of what the flat
                            // HAS. At a glance that reads as "removed", not as "the advert
                            // says there is none", so the blank-field rule ("must never make
                            // an agent guess") was being broken by the one state that exists
                            // to prevent guessing. The word now carries the meaning; the
                            // strikethrough only reinforces it.
                            <span key={key} className="inline-flex items-center gap-1" title={conflict ? t('desk_agent_conflict_hint') : (absent ? t('desk_agent_absent_hint') : undefined)}>
                              {conflict && <span aria-hidden className="text-amber-500">⚡</span>}
                              <span className={absent ? 'line-through opacity-60' : undefined}>{featLabel(key)}</span>
                              {absent && <span className="text-[10px] rounded px-1 bg-zinc-500/15 text-zinc-600 dark:text-zinc-300">{t('desk_agent_absent_tag')}</span>}
                              {conflict && <span className="text-[10px] rounded px-1 bg-amber-500/15 text-amber-700 dark:text-amber-300">{t('desk_agent_conflict_tag')}</span>}
                              {shown.map((r, i) => <AiMark key={i} mark={aiMarkFor(r, sibs)} />)}
                            </span>
                          )
                        })}
                      </span>), false)
                  })()}
                  {/* ORPHEUS NOTE (owner 2026-09-07: "remove the agent-activity section and put
                      that comment instead"). The 2026-08-27 activity band (Idmon / Lynceus /
                      Tiphys / Orpheus chips) is gone: each agent's work is already marked where
                      it lands — the AI mark on features, the Tiphys mark on the map pin (below),
                      the writer label on this note. The note is the same card sentence the
                      Leads feed renders (DistinctNote, property_distinctiveness.text_ka). */}
                  {/* ქონების შეფასება door (owner 2026-09-12): opens the deal editor on its FIRST
                      tab — the deterministic statistics / traits / most-alike / questions box.
                      Sits with the Orpheus note because it is the numbers behind that sentence. */}
                  {listing.property_id ? (
                    <div className="py-1.5 border-b border-border/40 last:border-0 flex items-center justify-between gap-2">
                      <div className="text-xs text-muted-foreground">{t('desk_lineage_field_orpheus_note')}</div>
                      <button type="button" data-valuation-door
                        onClick={() => { openDealEditor('valuation'); setValuationOpen(true) }}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-border px-2.5 py-1 text-xs font-medium hover:bg-muted">
                        <Icon name="chart" /> {t('desk_val_open')}
                      </button>
                    </div>
                  ) : null}
                  {distinctRow && (distinctRow.text_ka || distinctRow.text_en) ? (
                    <div className="py-1.5 border-b border-border/40 last:border-0">
                      {!listing.property_id && <div className="text-xs text-muted-foreground mb-0.5">{t('desk_lineage_field_orpheus_note')}</div>}
                      <DistinctNote row={distinctRow} />
                    </div>
                  ) : null}
                  {/* AGENT SIGNALS (B1075/B1076/B1077, 2026-08-27): three claim-backed facts
                      that were commercially orphaned. Each renders the owner's OWN WORDS —
                      the quote is the evidence, not decoration. The exclusive is an alert
                      (resharing this flat is the act the fact warns against); the co-broke
                      offer is a working signal, deliberately NOT a filter; the role conflict
                      names the contradiction and arbitrates nothing. */}
                  {merged.agent_signals?.third_party_exclusive && (
                    <div className="mt-2 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm">
                      <p className="text-xs font-semibold"><Icon name="alert-triangle" /> {t('desk_signal_tpe_title')}</p>
                      <p className="text-xs opacity-80 mt-0.5">„{merged.agent_signals.third_party_exclusive.quote}“</p>
                    </div>
                  )}
                  {merged.agent_signals?.coop_offer && lineageFieldRow(t('desk_signal_coop_title'), (
                    <span title={merged.agent_signals.coop_offer.quote ? `„${merged.agent_signals.coop_offer.quote}“` : undefined}>
                      {merged.agent_signals.coop_offer.percent}% <span className="opacity-60 text-xs">({t('desk_signal_coop_declared')})</span>
                    </span>), false)}
                  {merged.agent_signals?.author_role_conflict && (
                    <div className="mt-2 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm">
                      <p className="text-xs font-semibold"><Icon name="alert-triangle" /> {t('desk_signal_role_conflict_title')}</p>
                      <p className="text-xs opacity-80 mt-0.5">
                        {tLog('desk_signal_role_conflict_body', {
                          prose: t(merged.agent_signals.author_role_conflict.prose === 'agent' ? 'desk_role_agent' : 'desk_role_owner'),
                          portal: t(merged.agent_signals.author_role_conflict.portal === 'agent' ? 'desk_role_agent' : 'desk_role_owner'),
                        })} „{merged.agent_signals.author_role_conflict.quote}“
                      </p>
                    </div>
                  )}
                </>)
              })()}
              {/* District dispute (0101): display priority = winner-by-votes, declared on
                  tie (server-computed `display`) — NOT the stored polygon district. */}
              {lineageFieldRow(t('desk_lineage_field_district'),
                dispute ? <span><Icon name="alert-triangle" /> {dispute[dispute.display].name}</span> : merged.district,
                diff('district'))}
              {dispute && (
                <div className="mt-2 rounded-lg border border-yellow-500/40 bg-yellow-500/10 px-3 py-2 text-sm space-y-2">
                  <p className="text-xs font-semibold"><Icon name="alert-triangle" /> {t('desk_district_dispute_title')}</p>
                  {dispute.status === 'resolved' ? (
                    <p>{tLog('desk_district_dispute_resolved', {
                      name: dispute[dispute.votes.declared >= dispute.votes.polygon ? 'declared' : 'polygon'].name,
                      d: dispute.votes.declared, p: dispute.votes.polygon,
                    })}</p>
                  ) : (
                    <>
                      <p>{tLog('desk_district_dispute_question', { declared: dispute.declared.name, polygon: dispute.polygon.name })}</p>
                      {/* ⚠️ B277 (owner ruling 2026-08-02): the buttons no longer require an
                          agents row. An admin saw only the tally and no way to answer — the
                          owner included, on his own login. The server resolves a manager/admin
                          through the party bridged to the verified session. */}
                      <div className="flex items-center gap-2 flex-wrap">
                        <button disabled={voteBusy} onClick={() => castDistrictVote('declared')}
                          className={`rounded-md border px-3 py-1 text-sm ${dispute.my_vote === 'declared' ? 'border-primary bg-primary/20 font-semibold' : 'border-border hover:bg-muted'}`}>
                          {tLog('desk_district_dispute_yes', { declared: dispute.declared.name })}
                        </button>
                        <button disabled={voteBusy} onClick={() => castDistrictVote('polygon')}
                          className={`rounded-md border px-3 py-1 text-sm ${dispute.my_vote === 'polygon' ? 'border-primary bg-primary/20 font-semibold' : 'border-border hover:bg-muted'}`}>
                          {tLog('desk_district_dispute_no', { polygon: dispute.polygon.name })}
                        </button>
                        <span className="text-xs text-muted-foreground">
                          {t('desk_district_dispute_votes')}: {dispute.votes.declared} : {dispute.votes.polygon}
                        </span>
                      </div>
                      {/* ⚠️ The tally-only fallback that used to live here is GONE, not disabled.
                          It rendered when `agentId` was absent, i.e. for admins — the one branch
                          that made the question unanswerable for the people who asked for it.
                          If a caller genuinely cannot vote, the SERVER says so (404 with a
                          message the modal logs); guessing client-side is what hid it before. */}
                    </>
                  )}
                </div>
              )}
              {/* Freshness — "am I early or late to this?" in one line. created_at = when our
                  pipeline first captured it, last_seen_at = the latest re-scrape (0022). */}
              {(firstSeen || lastSeen) && (
                <p className="text-xs text-muted-foreground pt-2">
                  {firstSeen && <>{tLog('desk_lineage_captured', { time: timeAgo(firstSeen) })}</>}
                  {firstSeen && lastSeen && ' '}
                  {lastSeen && <>{tLog('desk_lineage_last_seen', { time: timeAgo(lastSeen) })}</>}
                  {firstSeen && price_history.length === 0 && <> {t('desk_lineage_price_unchanged')}</>}
                </p>
              )}
            </div>
            {/* ── 🔻 WITHDRAWN 2026-08-27: THE MODAL'S AGENT ROW IS THE OTHER BRANCH'S ────
                This branch briefly rendered its <AgentFacts> band here as well as on the card
                face. The Idmon session was building a modal row at the same time, reading
                `merged.features` — and its SERVER half is already live (source_agent /
                source_version / instances now ship for every agent row), so two renderings of
                the same fact would have landed in one modal.

                Agreed split: the CARD FACE shows the takeover chips (this branch), the MODAL
                shows the complete agent-read row with quote evidence (theirs). The owner's
                reason decides it — *"so I can see everything and verify myself"*: completeness
                and evidence belong where there is room for them.

                ⚠️ TWO THINGS THIS COSTS, BOTH REAL, NEITHER SILENT:
                • `merged.features` is ROW-scoped. This band was CLUSTER-scoped, so a fact
                  Idmon read on the flat's other advert appeared here and will not appear in
                  the replacement. That is diagnosis rule 5 in miniature and it should be
                  raised with the other session rather than rediscovered as a bug.
                • If that branch never lands, the modal has NO agent row at all. The fetch
                  above was removed with it; restoring both is a revert of one commit.

                The state and its fetch stay; only the render is withdrawn. */}
            {/* LOCATION (2026-07-16): nearby amenities + complex-membership question.
                Sits right under THE AD because it's sell-side property facts, and MY
                VERSION below only renders sometimes. One question at a time modal-wide:
                the complex question stays hidden while the district dispute above is
                still unanswered by this agent. */}
            <NearbyLocationsCard
              propertyId={listing.property_id}
              lat={merged.lat} lng={merged.lng} address={merged.address}
              agentId={agentId}
              canEdit={!notRep}
              onSetPin={onEditProperty && listing.property_id && !notRep
                ? () => onEditProperty({ propertyId: listing.property_id, listingId: listing.listing_id || listingId })
                : null}
              suppressQuestion={!!(dispute && dispute.status !== 'resolved' && !dispute.my_vote)} />
            {/* WHAT THE STATE SAYS THIS PLACE IS (owner 2026-09-20). Directly under the location
                card, because it answers the same question from the one source that is not a portal:
                the Public Registry's own parcel record, keyed by the cadastral code. It renders
                NOTHING unless the parcel has been resolved, so most cards are unchanged. Its most
                useful row is the last one — whether our own pin falls inside the registry's outline.
                It never shows who owns the parcel; see CadastralCard's header. */}
            {/* The micro-district / neighbourhood: the parent of the resolved address (core 1654-1660). */}
            {/* A street no witness forces: ask the person who knows (core 1723/1724). */}
            <StreetQuestion propertyId={listing.property_id} agentId={agentId}
              suppress={!!(dispute && dispute.status !== 'resolved' && !dispute.my_vote)} />
            <AreaCard propertyId={listing.property_id} />
            <CadastralCard propertyId={listing.property_id} />
            {/* THE OWNER'S OWN WORDS (owner 2026-07-30) — the ad's scraped description,
                under the location card. Sell-side context like the card above it, and it
                renders nothing at all when the owner wrote no text. */}
            {(() => { const ad = adDescriptionOf(data); return ad ? <AdDescriptionCard {...ad} /> : null })()}
            {/* MY VERSION IS NO LONGER A LIST HERE (owner 2026-09-03: "we need my version to
                be listing not list in listing"). It is a row in POSTED ON that OPENS, and the
                card at the top of the modal then renders the ad overlaid with the agent's own
                values — one place that shows a property, whichever version you asked for,
                instead of the property in one shape and "my facts" in another shape below it.
                ⚠️ The card component survives and is still exported: it remains the only place
                that renders ⚠ drift per field, and it is kept for the differences view (T24
                point 3). Do not delete it on the strength of this call site going away.
                ⚠️ The EDIT door moved with it — it now sits on the opened my-version card, so it
                is reachable from the thing it edits rather than from a list beside it. */}
            {/* ❌ "რას წერს თითოეული წყარო" (0444's PortalFactsCard) REMOVED FROM THE MODAL —
                owner, 2026-09-10, pointing at a live card. Only the render is gone: the
                component is still exported (the harness mounts it at cases I1–I5) and
                `property_portal_facts` still populates, so restoring it is one line.
                📌 WHAT HE WAS LOOKING AT, because it explains the call and it is not a
                presentation quibble. The card listed FOUR myhome.ge rows — one origin and
                three ↻ reshares — reading 80 / 80 / 80 / 88, and warned „არ ემთხვევა" off the
                88. That 88 came from a RESHARE, and the standing owner ruling of 2026-08-15 is
                absolute: *a reshare is a copy, and a copy decides nothing* — never a vote,
                never corroboration. So the card raised a disagreement that, by ruling, does not
                exist, on a property where every row is the same portal.
                ⚠️ The card's own header already says it returns CHANNELS, not portals
                (`commit_claim` supersedes by (source_party_id, source_kind)), which is why one
                portal can appear four times. Removing the card does NOT fix the underlying
                thing: any future surface built on `property_portal_facts` inherits both the
                repeated-portal shape and the reshare rows. Fix those at the source before
                bringing this back. */}
            {/* F4 — directly below PortalFactsCard: it shows the disagreement, this resolves it.
                Renders only when a machine-owned value actually conflicts (else null). */}
            <ClaimConflictsPanel propertyId={listing.property_id} />
            {/* Workbench-only as its own card — the shop window folds price moves into
                MARKET PRESENCE below (2026-07-14). */}
            {!notRep && price_history.length > 0 && (
              <div className={card}>
                <p className={`${SECTION_LABEL} mb-2`}>{t('desk_lineage_price_history_section')}</p>
                <div className="space-y-1">
                  {price_history.map((h, i) => (
                    <div key={i} className="flex items-center justify-between text-sm py-0.5">
                      {/* Server normalized currency toggles → today's-rate same-currency compare,
                          and dropped trivial moves; direction/pct come from it (classifyPriceChange). */}
                      <span className={h.direction === 'down' ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-600 dark:text-amber-400'}>
                        {h.direction === 'down' ? '↓' : '↑'}{' '}
                        {h.from ?? '—'} {h.currency || merged.currency || ''} → {h.to ?? '—'} {h.currency || merged.currency || ''}
                        {h.pct != null && <span className="ml-1 tabular-nums">({h.pct > 0 ? '+' : ''}{h.pct}%)</span>}
                      </span>
                      <span className="text-xs text-muted-foreground">{timeAgo(h.observed_at)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {/* ── MARKET PRESENCE (shop window, 2026-07-14; §9: lives in the left column —
                availability + price moves are "what is this" facts). A buyer-side agent
                needs exactly two market facts: is it still AVAILABLE, and how the PRICE
                has moved. */}
            {notRep && (() => {
              // A recorded close outranks raw platform activity: 12 sibling ads can still say
              // "active" on their platforms the day after the deal — the crawler hasn't caught
              // up, but WE know. "Still available" over a closed deal would be the modal
              // contradicting its own status card.
              const anyActive = !familyClosure && [listing, ...(origin ? [origin] : []), ...siblings].some(r => r?.status === 'active')
              return (
                <div className={card}>
                  <p className={`${SECTION_LABEL} mb-2`}>{t('desk_shop_market_section')}</p>
                  <p className="text-sm">
                    <span className={`inline-block w-2 h-2 rounded-full mr-1.5 ${anyActive ? 'bg-green-500' : 'bg-muted-foreground/40'}`} />
                    {anyActive ? t('desk_shop_still_available') : t('desk_shop_off_market')}
                    {lastSeen && <span className="text-xs text-muted-foreground"> · {tLog('desk_shop_last_confirmed', { time: timeAgo(lastSeen) })}</span>}
                  </p>
                  {price_history.length > 0 && (
                    <div className="space-y-0.5 mt-1.5">
                      {price_history.map((h, i) => (
                        <div key={i} className="flex items-center justify-between text-sm py-0.5">
                          <span className={h.direction === 'down' ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-600 dark:text-amber-400'}>
                            {h.direction === 'down' ? '↓' : '↑'}{' '}
                            {h.from ?? '—'} {h.currency || merged.currency || ''} → {h.to ?? '—'} {h.currency || merged.currency || ''}
                            {h.pct != null && <span className="ml-1 tabular-nums">({h.pct > 0 ? '+' : ''}{h.pct}%)</span>}
                          </span>
                          <span className="text-xs text-muted-foreground">{timeAgo(h.observed_at)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )
            })()}
            </div>
            {/* ── RIGHT COLUMN: "our relationship" ── */}
            <div className="space-y-4 min-w-0">
            {/* Property-contact resolution (cooperation → non-agent author → origin → manual
                edges) no longer gets its own card here — it's all in the footer's 📞 Contact
                popover now (2026-07-05), including add-contact. listing.author_email has no
                home in that popover (contacts are phone-first), so it's the one thing kept
                visible here when there's no richer resolver data yet. */}
            {!notRep && !pcontacts?.contacts?.length && !view.isOwn && listing.author_email && (
              <div className={card}>
                <p className={`${SECTION_LABEL} mb-2`}>{t('desk_lineage_contact_section')}</p>
                <p className="text-sm text-muted-foreground"><Icon name="mail" /> {listing.author_email}</p>
              </div>
            )}
{/* Viewings summary merged into the MEETING section below (2026-07-11) so existing
                meetings show as chips next to 📅＋ — same "show the actual values" pattern as
                COOPERATION and RENTAL RESTRICTIONS, not a separate counts-only card. */}
            {/* Origin-ad contact used to get its own card here, duplicating the origin's row
                (with its own ↗ link) already listed in POSTED ON below — dropped 2026-07-05,
                the property-level CONTACTS card above already surfaces the same person when
                the resolver ranks them (rank 3, origin-ad author). */}
            {/* The SAME property is almost always posted to all three platforms (myhome/ss/korter),
                each its own listing row with its own derived_from edge back to this same origin
                (0041 — never the same ad as its source). ONE list for every platform posting,
                THIS one included (not just the siblings) — it used to sit as a separate link up
                in the posting/ad box above, split from its own siblings list, so an agent had to
                look in two different places to see everywhere a property is live. */}
            {/* სტატუსი (owner 2026-07-30): what the owner said on the phone, one row per
                agent who called — the FIRST thing an agent learns, so it leads the
                relationship column, above the cooperation card. */}
            <MarketReportPanel propertyId={listing.property_id} agentId={agentId}
              listingId={listing.listing_id || listingId}
              agentName={agentName}
              lifecycle={familyClosure?.lc || null}
              // WHICH ad the closure was recorded against — the reopen must target that one, not
              // whichever posting this modal happens to be anchored on (AD-1).
              closedListingId={familyClosure?.id || null}
              // The LOG belongs to this ad, not the family — a projection has no history of its
              // own, and showing a sibling's transitions here would misattribute them.
              lifecycleHistory={listing.lifecycle_history || null}
              contested={listing.lifecycle_contested === true}
              onDealClosed={() => {
                reloadCoops(data?.listing?.property_id, (data?.siblings || []).map(s => s.property_id), { force: true })
                // The lifecycle this modal projects lives in the lineage payload, so the close
                // is invisible here until it is re-read (see lineageSeq).
                setLineageSeq(s => s + 1)
              }} />
            {/* Cooperations of this listing's PROPERTY — same renderer as the card-face chips
                and the person view (one interpretation, many anchors). The counterparty for a
                NEW cooperation is the ORIGIN ad's contact when this is a reshare (the person
                whose number is on the source ad — same rule as the extension's reshare sync),
                falling back to this listing's own author for a plain capture. */}
            {/* Every viewer (2026-07-28, was rep-only): a non-rep opens their OWN
                cooperation against the owner; others' numbers stay projection-masked
                until they contribute. Contact seed prefers the RESOLVER's owner row —
                for a non-rep the lineage payload rewrites author_party_id to the
                representing agent (2026-07-14 masking), which would silently open the
                cooperation against the AGENT as counterparty. The resolver's rank-2/3
                public-evidence rows carry the real owner for every viewer. */}
            {listing.property_id && (() => {
              const resolverOwner = pcontacts?.contacts?.find(c => !c.is_agent && !c.via_agent) || null
              const contactPartyId = resolverOwner?.party_id || origin?.author_party_id || listing.author_party_id
              const contactName = resolverOwner?.name
                || (origin ? (origin_contact?.name || origin.author_name) : listing.author_name)
              return (
                <div className={card}>
                  <p className={`${SECTION_LABEL} mb-2`}>{t('desk_lineage_cooperation_section')}</p>
                  {coops.length === 0 && !contactPartyId ? (
                    <p className="text-sm text-muted-foreground">{t('desk_lineage_no_coop')}</p>
                  ) : (
                    /* list view (2026-07-20): names + values per participant, like the
                       sale-terms card; the compact chip stays on card faces. candidates =
                       agents with a POSTING here (same roster the sale-terms card lists) —
                       shown muted as "not set yet" when absent from the cooperation */
                    <CooperationList cooperations={coops} addContactName={contactName || null} viewerName={agentName}
                      candidates={[listing, ...siblings].filter(l => l?.author_is_agent && l.author_name).map(l => l.author_name)}
                      onOpen={c => openDealEditor('coop', { coop: { propertyId: c.property_id, cooperationId: c.id, propertyLabel: merged.address || null } })}
                      onAdd={contactPartyId
                        ? () => openDealEditor('coop', { coop: { propertyId: listing.property_id, propertyLabel: merged.address || null, contactPartyId, contactName: contactName || null,
                            seedIntent: coopIntentOf(merged.intent), seedCurrency: merged.currency || null, seedPrice: merged.price ?? null } })
                        : null} />
                  )}
                </div>
              )
            })()}
            {/* RENTAL RESTRICTIONS / SALE CONDITIONS — owner's stated conditions, PER AGENT
                (2026-07-11): like POSTED ON, each agent's reshare is its own listing row, so
                each agent may have heard/recorded different conditions from the owner. One
                row per agent; 🏠/🏠＋ opens the editor on THAT agent's listing. Sales get the
                sale-shaped panel (0080: cash-only, occupancy, handover, appraisal...) with
                the same buyer-restriction stance chips. */}
            {/* Every intent that has an editor shape. girao joined 0219; ijara has no
                shape of its own and opens the rent panel (termsModeFor) rather than
                being denied a terms card entirely. */}
            {['rent', 'daily_rent', 'sale', 'girao', 'ijara'].includes(listing.intent) && (listing.listing_id || listingId) && (() => {
              // THE AD IS NOT THE DEAL (2026-07-27). An ACTIVE cooperation states what
              // this agency is actually working on this property; it outranks the ad's
              // own label, which may describe a different transaction entirely (the case
              // that found this: rent cooperation opened against a girao ad). Ended and
              // rejected cooperations are not engagements and must not steer the panel.
              const liveCoop = (coops || []).find(c => !['ended', 'rejected'].includes(c.status))
              const termsMode = termsModeFor(listing.intent, liveCoop?.transaction_intent || null)
              // Same ownerName the row-bucketing below uses — the header names WHOSE
              // requirements these are (owner ruling 2026-07-29: every row here is an
              // agent's record of the OWNER's conditions, never an agent↔agent fact).
              const termsOwner = origin ? (origin_contact?.contact_via_agent ? null : (origin_contact?.name || origin.author_name)) : (!view.isOwn ? listing.author_name : null)
              return (
              <div className={card}>
                {/* TITLE ONLY (owner 2026-07-30, §3b.2). This read
                    "გაქირავების პირობები — მესაკუთრის მოთხოვნები (მხოლოდ აგენტებისთვის) — ნატალი (მესაკუთრე)":
                    a 2-word title plus three qualifiers. None of them earn the space — the card
                    sits inside a property modal, the whole surface is agents-only, and WHOSE
                    terms each row holds is the row's own left column. `_short` keys are used
                    here; the long ones stay for the terms MODAL's own header, where a standalone
                    dialog does need to say what it is. */}
                <p className={`${SECTION_LABEL} mb-2`}>
                  {t(termsMode === 'sale' ? 'desk_sale_terms_title_short' : termsMode === 'girao' ? 'desk_girao_terms_title_short' : 'desk_terms_facet_title_short')}
                </p>
                {/* Siblings are OTHER agents' reposts — each its own deal, so same-intent
                    filtering still applies to them.
                    ⚠️ The ORIGIN is NOT filtered by intent any more (2026-07-27). It used to
                    be, on the reasoning that "a rent modal must not fold a sale sibling's
                    row in". That reasoning does not hold for the origin, because the origin
                    is where an agent's OWN gallery-recorded terms physically land (the
                    gallery save resolves the source ad) — so when the origin's intent
                    differed from the card's, this filter deleted the agent's own work from
                    the only panel that shows it. Exactly the 2026-07-21 Ani/Tolstoi-13 bug,
                    recurring through a different door. Showing an extra row of the owner's
                    stated conditions costs a line; hiding an agent's record costs the work. */}
                <RentalTermsByAgent primaryListing={listing} primaryIdFallback={listingId}
                  siblings={siblings.filter(s => (s.intent || listing.intent) === listing.intent)}
                  origin={origin}
                  viewerName={agentName} refreshKey={termsRefresh} canRecord={!notRep}
                  ownerName={termsOwner}
                  onOpen={id => openDealEditor('terms', { termsListingId: id, termsMode })} />
                {/* "Available from" (2026-07-21): DISPLAYED here, EDITED in the terms editor
                    (🏠 above) alongside deposit/min-lease — it is an owner-stated condition,
                    so it is recorded where the other conditions are. Read-only here for the
                    same reason every other row in this card is: this panel summarises, the
                    editor writes. Shown to everyone who can see the card (no !notRep gate) —
                    seeing WHEN a property frees up is exactly what a buyer-side agent needs;
                    only writing is representation-gated, and that gate lives on the server. */}
                {listing.property_id && (
                  <AvailabilityLine propertyId={listing.property_id} refreshKey={termsRefresh} />
                )}
              </div>
              )
            })()}
            {/* MEETING — 📅＋ to add, plus existing meetings shown as chips (2026-07-11):
                client + status, colored like the cooperation/rental chips. Read-only here
                (the viewing pipeline lives in the Deals tab); matched to this property by ad
                URL / core_listing_id, so an empty result can't tell "none" from "saved with
                no source_url" — hence "no meetings yet" only shows once the fetch resolved. */}
            {/* Every viewer (2026-07-28, was rep-only): a non-rep books their own
                viewing — they hold the owner contact now. Other agents' meetings
                stay facts-only (client_name projected null server-side, Track 2). */}
            {onAddMeeting && listing.property_id && (
              <div className={card}>
                <div className="flex items-center justify-between gap-3 mb-1.5">
                  <p className={SECTION_LABEL}>{t('desk_lineage_meeting_section')}</p>
                  {/* §2: the card's ONE state pill — the NEXT meeting, which is the only
                      thing an agent scanning this column needs off the header. Everything
                      else is a row below. */}
                  {(() => {
                    const nx = nextMeetingSummary()
                    return nx ? <span className={pill(nx.tone, { sm: true })}>
                      <Icon name="calendar" size={12} /> {nx.text}
                    </span> : null
                  })()}
                </div>
                <div className="space-y-0">
                  {/* Only meetings with an actual client — a client-less viewing is an
                      empty/incomplete record ("nothing selected yet") and carries no info
                      worth a chip. Closest upcoming first (then most recent past) — the list
                      answers "when is the next one" before "what happened before". Agent
                      names are admin-grade (same rule as cooperation chips): an agent sees
                      time + client + status, an admin also sees whose meeting it is. */}
                  {/* §2 ROWS, not a stack of tinted capsules. Five meetings used to be five
                      coloured pills wrapping across the card; now the STATUS is a 7px dot and
                      a word at the right edge, the time is the row's value, and the who sits
                      under it. Every viewing shows as a FACT — a non-hosting agent gets the
                      date/status with client_name projected to null server-side (Track 2),
                      not dropped.
                      Rows are DOORS, same grammar as every other card in this column (owner
                      2026-07-30): tap a meeting to open its detail/editor — core rows →
                      CoreMeetingDetail, legacy rows → ViewingForm, routed by the same handler
                      the card face uses. */}
                  {meetingRows.map(({ v, at }) => {
                    const who = [representingSeg(v, privileged), v.client_name].filter(Boolean).join(' · ')
                    const body = (
                      <>
                        <div className="min-w-0 flex items-center gap-2">
                          <span className={`w-[7px] h-[7px] rounded-full shrink-0 ${VIEWING_DOT[v.status] || 'bg-muted-foreground/50'}`} />
                          <div className="min-w-0">
                            <div className={`${ROW_LEFT} tabular-nums`}>{viewingSchedText(at)}</div>
                            {who && <div className="text-xs leading-[1.45] text-muted-foreground truncate">{who}</div>}
                          </div>
                        </div>
                        <span className={ROW_VAL}>{t(`dv_status_${v.status}`) || v.status}</span>
                      </>
                    )
                    return onOpenMeeting ? (
                      <button key={v.id} type="button" title={`${timeAgo(v.created_at)} · ${t('edit')}`}
                        onClick={() => onOpenMeeting(v)}
                        className={`${ROW} w-full text-left rounded-md hover:bg-accent/40`}>{body}</button>
                    ) : (
                      <div key={v.id} title={timeAgo(v.created_at)} className={ROW}>{body}</div>
                    )
                  })}
                </div>
                {/* The ONE action, last, named by its verb (§2). */}
                <button title={t('desk_meeting_add_tooltip')}
                  onClick={() => onAddMeeting(buildMeetingSeed())}
                  className={pill('add', { door: true, extra: 'mt-2' })}>
                  <Icon name="calendar" size={12} /> {t('desk_meeting_add_action')}
                </button>
              </div>
            )}
            {/* ბირჟა (PublishToPoolFacet) was removed from this modal 2026-07-21 — the
                offer-to-other-agents flow is being redesigned, so the modal drops it now
                rather than carrying a form for a mechanism that is going away. The Pool
                TAB, PoolFeed, PoolCardModal, auto-publish settings and every server
                endpoint are deliberately UNTOUCHED; this is a modal-only removal. */}
            {/* Availability moved INTO the rental-terms card above (same date, one fewer
                card). It renders only when that card renders — i.e. intent is
                sale/rent/daily_rent. listing_intent_check was dropped in 0008, so a
                legacy 'lease'/'off_market' row has no terms card and therefore no
                availability control; that fallback lives right below. */}
            {/* Workbench position — the shop window renders private notes LAST instead
                (after matching clients), per the spec's reading order. */}
            {!notRep && agentId && listing.property_id && (
              <>
                {/* Public page opt-in (B187) — representative-only, same gate the server
                    enforces; a notRep viewer never sees a control that would 403. */}
                <PublicPageFacet propertyId={listing.property_id} agentId={agentId} />
                <PrivateNotesPanel propertyId={listing.property_id} agentId={agentId} />
                {!(listing.intent === 'rent' || listing.intent === 'daily_rent' || listing.intent === 'sale') && (
                  <AvailabilityFacet propertyId={listing.property_id} agentId={agentId} />
                )}
              </>
            )}
            {/* NO !notRep GATE (removed 2026-08-05, owner-reported). This section used to be
                hidden wholesale from non-representatives, which threw away the rows the server
                had deliberately KEPT: an agent's own reshare link survives the mask on purpose
                ("an agent's posting carries the agent's own contact" — server.js's sibling
                shape), so Luka, holding two reposts of this very flat, could not see links to
                ads he posted himself. Masking is per-ROW and it is the SERVER's: owner ads
                arrive with source_url / posted_address / author_name already null (and
                editUrlFor returns null for author_is_agent === false), so every row below
                degrades itself. A second gate here could only ever be wrong in one direction —
                it hid what the projection allowed, and it would not have caught a projection
                that leaked. One gate, server-side (COOPERATION_VISIBILITY_SPEC §4).
                ⚠️ Everything in this block is READ-ONLY — re-check that before adding a
                control here; write affordances stay behind !notRep, further down.
                ⚠️ POLICY — owner ruling 2026-08-05 (B327, resolved). Showing this section to a
                non-representative is INTENDED, and the rule is a trust boundary, not a field
                list: COVERAGE METADATA describes the market state of a PROPERTY and is
                agency-visible — which portals it appears on, asking prices, listing age,
                status, and the existence of sibling listings. OWNER DATA describes a PERSON and
                stays masked for non-representatives — exact address, precise pin, owner
                identity, owner phone/contact, links to the owner's ads, and any other direct
                path that bypasses the responsible agent. Classify a NEW field by which side of
                that boundary it falls on; do not pattern-match against the list. Mixing the two
                is what produces authorization rules that drift.
                The masking itself is still the SERVER's and stays there (§4). */}
            {(listing.source_url || siblings.length > 0) && (() => {
              // Every row in this list gets compared against ONE reference posting — the origin
              // ad when there is one (every reshare descends from it); when there's no separate
              // origin, THIS listing itself is what every reshare descended from, so it becomes
              // the reference instead of leaving every row unhighlighted.
              const refRow = origin || listing
              // Price delta helpers hoisted section-wide (§11): the anchor row and the
              // agent-group headers need the same computation the sibling rows use.
              // 2026-07-20: both sides normalised to the DISPLAY currency first (GEL repost
              // of a USD origin gets a real delta); no rate loaded → mixed pairs refused.
              // §11 (designer round 4): a delta is INFO (gray) unless it crosses the
              // divergence threshold (~2% of the reference) — red on every row meant
              // nothing read as an alert.
              const deltaInfo = (m, ref) => {
                if (!ref || m?.price == null || ref.price == null || m === ref) return null
                const mc = m.currency || 'USD', rc = ref.currency || 'USD'
                const convertible = money.display && money.rate > 0
                  && CONVERTIBLE.has(mc) && CONVERTIBLE.has(rc)
                if (!convertible && mc !== rc) return null
                const a = comparableAmount(m.price, mc, money.display, money.rate)
                const b = comparableAmount(ref.price, rc, money.display, money.rate)
                const d = Math.round(a - b)
                if (!d) return null
                return { d, divergent: Math.abs(d) > Math.max(1, 0.02 * Math.abs(b)) }
              }
              // Per-AD lifecycle badge: the ONE ad a closure was recorded against says so on
              // its own row (გაიყიდა/გაქირავებულია) — that is the evidence behind the
              // property-level familyClosure projection, made visible where the ad lives.
              // Siblings keep their own per-ad state; nothing here writes anything.
              const lifecycleBadge = r => {
                const lc = r?.lifecycle_status
                if (lc !== 'status_sold' && lc !== 'status_rented_out') return null
                return <Badge label={t(lc === 'status_sold' ? 'desk_entry_closed_sold' : 'desk_entry_closed_rented')}
                  variant="green" title={r.lifecycle_observed_at ? fmtTbilisi(r.lifecycle_observed_at) : undefined} />
              }
              // Comparand INLINE (designer vocab rule 2026-07-23): a bare "+46" is noise —
              // the anchor is the posting this modal is opened on.
              const priceDeltaToken = (m, ref) => {
                const di = deltaInfo(m, ref)
                if (!di) return null
                const cls = di.divergent ? (di.d > 0 ? 'text-red-500' : 'text-green-600') : 'text-muted-foreground'
                // Bare arrow+number on the row (owner 2026-07-24: the repeated anchor phrase
                // was line-noise in Georgian); the comparand survives in the tooltip.
                return <span className={`ml-1 text-xs font-semibold ${cls}`}
                  title={t('desk_delta_vs_post')}>
                  {di.d > 0 ? `▲+${di.d.toLocaleString('en-US')}` : `▼${di.d.toLocaleString('en-US')}`}</span>
              }
              return (
              <div className={card}>
                <p className={`${SECTION_LABEL} mb-2`}>{t('desk_lineage_posted_on_section')}</p>
                {/* §11 CoverageStrip: answers "do I need to repost, where, at what price"
                    before a single row is read — platform coverage, price range, origin age. */}
                {(() => {
                  // Same-intent rows only — a sale sibling's price inside a rent modal's
                  // coverage range is nonsense (live-caught on first render, 2026-07-24).
                  // 🔴 THE LISTING COUNTS EVEN WITHOUT A URL (owner, 2026-09-07). This read
                  // `listing.source_url ? listing : null`, so a posting with no stored URL was
                  // dropped from its OWN coverage row: the owner opened a Facebook listing and
                  // the strip said SS.ge ✓3 · MyHome ✓2 · Korter ✗ with no Facebook at all,
                  // while the card face showed the FB post. The cluster really holds 6 ads —
                  // ss×3, myhome×2, facebook×1 — and the sixth was invisible in the one panel
                  // that exists to say where a flat is posted.
                  // ⚠️ It is NOT a reshare and was not mistaken for one: no derived_from edge in
                  // either direction. It is its own observed source.
                  // The URL was standing in for "is this a real portal posting", which
                  // platformKey already answers exactly — 'manual' returns null and is skipped
                  // by the `if (!k) return` below, which is what the proxy was protecting.
                  // Measured: 12 of 51 facebook listings carry no source_url, plus all 34 manual
                  // ones — the manual rows were never the problem, the FB ones always were.
                  const fam = [listing, origin,
                    ...siblings.filter(s => s.id !== (origin?.listing_id || null) && s.id !== listing.listing_id)]
                    .filter(Boolean).filter(r => (r.intent || listing.intent) === listing.intent)
                  // Per platform: ACTIVE (still on-market) vs TOTAL history — a ✓7 that
                  // counted dead ads overstated real coverage (owner 2026-07-24).
                  const counts = new Map()
                  fam.forEach(r => {
                    const k = platformKey(r.source); if (!k) return
                    const c = counts.get(k) || { total: 0, active: 0 }
                    c.total++; if (r.status === 'active') c.active++
                    counts.set(k, c)
                  })
                  const conv = r => comparableAmount(r.price, r.currency || 'USD', money.display, money.rate)
                  let lo = null, hi = null
                  fam.filter(r => r.price != null).forEach(r => {
                    if (lo == null || conv(r) < conv(lo)) lo = r
                    if (hi == null || conv(r) > conv(hi)) hi = r
                  })
                  const CORE3 = ['ss', 'myhome', 'korter']
                  const extras = [...counts.keys()].filter(k => !CORE3.includes(k))
                  return (
                    <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1 text-xs pb-2 mb-2 border-b border-border/40">
                      {[...CORE3, ...extras].map(k => {
                        const c = counts.get(k)
                        return (
                        <span key={k} className="whitespace-nowrap text-muted-foreground" title={c ? t('desk_lineage_cov_active_total') : undefined}>
                          <b className="text-foreground font-semibold">{PLATFORM_LABEL[k] || k}</b>{' '}
                          {c
                            /* active in green (the live coverage), /total muted (the history) —
                               collapses to one number only when every posting is still live. */
                            ? <span className="font-semibold">
                                <span className={c.active ? 'text-green-600 dark:text-green-500' : 'text-muted-foreground/60'}>✓{c.active}</span>
                                {c.active !== c.total && <span className="text-muted-foreground/60">/{c.total}</span>}
                              </span>
                            : <span className="text-muted-foreground/60 font-semibold">✗</span>}
                        </span>
                      )})}
                      <span className="flex-1" />
                      {lo && <span className="whitespace-nowrap text-muted-foreground">{t('desk_lineage_cov_price')}{' '}
                        <Money amount={lo.price} currency={lo.currency} />{hi !== lo && <>–<Money amount={hi.price} currency={hi.currency} /></>}</span>}
                      {origin?.created_at && <span className="whitespace-nowrap text-muted-foreground">{t('desk_lineage_cov_origin')} {timeAgo(origin.created_at)}</span>}
                    </div>
                  )
                })()}
                <div className="space-y-1">
                  {/* Each posting shows ITS OWN posted address (listing.meta.posted_address via
                      the API) — a reshare may be posted under a different address than its origin;
                      the row must reflect exactly what that platform's ad says. */}
                  {listing.source_url && (() => { const edit = editUrlFor(listing.source, listing.source_url); return (
                    /* §9/§11: the CURRENT ANCHOR is ringed + badged — ONE line (a closed row
                       answers "should I care?"); its address/facts live in the cards above,
                       not repeated here. Provenance wording preserved (owner-caught
                       2026-07-14: a repost must SAY it's a repost, not just "viewing"). */
                    <div className="flex items-center gap-2 text-sm py-1.5 px-2 -mx-1 rounded ring-1 ring-primary/50">
                      <span className="rounded px-1.5 py-px text-xs font-bold bg-primary text-primary-foreground whitespace-nowrap">{t('desk_lineage_viewing_badge')}</span>
                      <PlatformPill source={listing.source} />
                      <span className="text-muted-foreground text-xs truncate min-w-0"
                        title={(listing.listed_at || listing.created_at) ? fmtTbilisi(listing.listed_at || listing.created_at) : undefined}>
                        {/* Step 5 display honesty (PUBLICATION_AND_OWN_LISTINGS, 2026-08-03):
                            the provenance word must not outrun the evidence. An ad WE created
                            (listing.provenance === 'published', 0385) says so — calling our
                            own publication "scraped" was the doc's §5 complaint. An observed
                            origin that other postings derive FROM says "source of N reposts"
                            (the siblings list holds exactly those derived rows). Only a plain
                            observed ad with no reposts keeps the bare scraped badge. */}
                        {origin
                          ? (listing.listed_at ? tLog('desk_entry_reposted', { datetime: timeAgo(listing.listed_at) }) : t('desk_entry_reposted_no_date'))
                          : listing.provenance === 'published'
                            ? <><IconText>{t('desk_lineage_published_badge')}</IconText>{listing.created_at && <> · {timeAgo(listing.created_at)}</>}</>
                          : (() => {
                              const selfId = listing.listing_id || listing.id
                              const derivedN = (siblings || []).filter(s => s.derived_from_id === selfId).length
                              return derivedN > 0
                                ? <><IconText>{tLog('desk_lineage_source_of_reposts', { n: derivedN })}</IconText>{listing.created_at && <> · {timeAgo(listing.created_at)}</>}</>
                                : <><IconText>{t('desk_lineage_scraped_badge')}</IconText>{listing.created_at && <> · {timeAgo(listing.created_at)}</>}</>
                            })()}
                      </span>
                      {/* No scraped-by names on the anchor/source row (designer ruling
                          2026-07-24) — external platform only; our-side identity is OUR REPOSTS. */}
                      <span className="flex-1" />
                      {merged.price != null
                        ? <span className="text-xs font-bold whitespace-nowrap"><Money amount={merged.price} currency={merged.currency} />{priceDeltaToken(merged, origin)}</span>
                        /* The ad SAYS "ფასი შეთანხმებით" (server flag) — different from having
                           no price, and the card says so too, so the modal must not contradict
                           it with a blank. Gated on the flag ONLY, never on price == null. */
                        : merged.price_on_request ? <span className="text-xs font-bold whitespace-nowrap text-muted-foreground">{t('desk_price_on_request')}</span>
                        : null}
                      {(() => { const lb = lifecycleBadge(merged); if (lb) return lb
                        const ab = listingAgeBadge(merged); return ab
                        ? <Badge label={ab.label} variant={ab.variant} title={ab.title} />
                        : <Badge label={merged.status === 'active' ? t('desk_status_on_market') : t('desk_status_off_market')} variant={merged.status === 'active' ? 'green' : 'default'} /> })()}
                      {/* hideText (owner 2026-07-24): this row carries badge + platform +
                          provenance + price + age BEFORE the links, and on a phone the word
                          "გახსნა" is what pushes it over. IconText drops the label under
                          sm: and keeps the ↗ — the arrow already says "opens the ad", and
                          title= keeps it named for anyone hovering or using a reader. */}
                      <a href={listing.source_url} target="_blank" rel="noreferrer" title={t('desk_lineage_open_link')} className="text-xs text-primary whitespace-nowrap"><IconText hideText>{t('desk_lineage_open_link')}</IconText></a>
                      {/* isOwn only — this row can BE the scraped origin itself (viewing it
                          directly, not via a reshare), and someone else's ad has no edit page
                          we own. See the isOwn comment above ("YOUR POSTING" vs "THE AD"). */}
                      {view.isOwn && edit && <a href={edit} target="_blank" rel="noreferrer" title={t('desk_lineage_edit_link')} className="text-xs text-primary whitespace-nowrap"><IconText hideText>{t('desk_lineage_edit_link')}</IconText></a>}
                    </div>
                  ) })()}
                  {/* The ORIGIN (scraped source ad) as its own row — the server's siblings list
                      only holds other REPOSTS of it (derived_from edges, source != id), so
                      without this the source ad had no row at all once the ORIGIN AD card was
                      dropped (2026-07-05: its Open link silently vanished for reshares). No
                      Edit link: it's someone else's ad, the edit pages are for OUR postings. */}
                  {origin && (
                    /* §9/§11 OriginCard: DASHED border + 🌐 (the dashed vocabulary marks
                       external everywhere on the Desk, so a platform author never reads as a
                       roster agent) — and deliberately BORING: it is the baseline every delta
                       compares against, so nothing inside it is ever red/highlighted, and it
                       carries the section's largest price. Click still re-anchors (load-bearing). */
                    <div className="border border-dashed border-muted-foreground/40 bg-muted/30 rounded-lg px-3 py-2.5 cursor-pointer hover:bg-accent/50"
                      title={t('desk_lineage_view_posting_tooltip')}
                      onClick={() => setView({ id: origin.listing_id, isOwn: false, mine: false })}>
                      <div className="flex items-center gap-2 mb-1.5">
                        <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground whitespace-nowrap">
                          <IconText>{t('desk_lineage_origin_card_title')}</IconText>
                        </span>
                        <PlatformPill source={origin.source} />
                        <span className="flex-1" />
                        <span className="text-xs text-muted-foreground whitespace-nowrap"
                          title={origin.created_at ? fmtTbilisi(origin.created_at) : undefined}>
                          <IconText>{t('desk_lineage_scraped_badge')}</IconText>{origin.created_at && <> · {timeAgo(origin.created_at)}</>}
                        </span>
                        {(() => { const lb = lifecycleBadge(origin); if (lb) return lb
                          const ab = listingAgeBadge(origin); return ab
                          ? <Badge label={ab.label} variant={ab.variant} title={ab.title} />
                          : <Badge label={origin.status === 'active' ? t('desk_status_on_market') : t('desk_status_off_market')} variant={origin.status === 'active' ? 'green' : 'default'} /> })()}
                      </div>
                      <div className="flex items-end justify-between gap-3">
                        <span className="min-w-0">
                          {origin.address && <span className="block text-xs text-muted-foreground truncate">{origin.address}</span>}
                          {formatListingSummary(origin) && <span className="block text-xs text-muted-foreground/80">{formatListingSummary(origin)}</span>}
                          {/* No scraped-by names on the source row (designer ruling 2026-07-24):
                              the origin row names the EXTERNAL platform only; who captured/
                              reposted it on our side lives in the OUR REPOSTS groups below. */}
                        </span>
                        <span className="flex items-center gap-2.5 shrink-0">
                          {origin.price != null && <Money amount={origin.price} currency={origin.currency} className="text-xl font-extrabold tracking-tight" />}
                          {origin.source_url && <a href={origin.source_url} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()} className="text-xs text-primary"><IconText>{t('desk_lineage_open_link')}</IconText></a>}
                        </span>
                      </div>
                    </div>
                  )}
                  {/* MY VERSION IS A POSTING NOW (owner 2026-09-03: "we need my version to be
                      listing not list in listing"). It used to be a thin row that SCROLLED to a
                      list of fields further up the modal — so the one view built from the
                      agent's own knowledge was the only one you could not open. It re-anchors
                      like every other row: same shape, same click, and the card above renders the
                      ad overlaid with the agent's recorded values.
                      ⚠️ It carries no listing id and no platform, and that is not a defect to
                      paper over — it is not published anywhere. `mine` rides ALONGSIDE the id
                      rather than replacing it, so the modal keeps a real listing underneath to
                      draw photos, contacts and history from.
                      Shown only once the agent has recorded something: an empty my-version row
                      would be a door onto the ad you are already looking at. */}
                  {hasMyVersion(myVersion?.facts, imgSets.sets) && (() => {
                    void ownValuesOf
                    const openMine = view.mine
                    return (
                      <div data-my-version-row className={`flex items-center justify-between text-sm py-1 border-b border-border/40 last:border-0 cursor-pointer rounded px-1 ${openMine ? 'bg-accent' : 'hover:bg-accent/50'}`}
                        title={t('desk_lineage_view_my_version_tooltip')}
                        aria-current={openMine ? 'true' : undefined}
                        onClick={() => setView(v => ({ ...v, mine: true }))}>
                        <span className="font-medium min-w-0">
                          <span className={`${COUNTER} font-semibold`}>
                            {t('desk_lineage_my_version_section')}
                          </span>{' '}
                          {/* NOT FactsDiffLine: that is the card-face summary and knows six
                              facts. This row must name whatever the agent actually recorded,
                              or it promises "facts you recorded" and shows none of them. */}
                          <MyFactsLine facts={myVersion?.facts} />
                        </span>
                        {/* The other rows all say where they are published. This one says the
                            opposite, because the difference is the point: these are the agent's
                            own facts, not an advert anyone can read. */}
                        <span className={`${COUNTER} shrink-0 ml-2`}>{t('desk_my_version_not_published')}</span>
                      </div>
                    )
                  })()}
                  {(() => {
                    // Reshare-of-a-reshare attribution (2026-07-11, admin-directed: "if they
                    // reshared from Gvantsa it should be seen"): each sibling carries its DIRECT
                    // parent (derived_from_id, full-tree BFS server-side). A depth-1 reshare's
                    // parent is the origin ad — that's the implied default, no marker. Anything
                    // deeper gets an explicit ↳ token naming whose posting it was taken from.
                    const originId = origin?.listing_id || null
                    const familyById = {}
                    if (origin) familyById[origin.listing_id] = origin
                    if (listing.listing_id) familyById[listing.listing_id] = listing
                    siblings.forEach(x => { familyById[x.id] = x })
                    const resharedFromToken = s => {
                      if (!s.derived_from_id || s.derived_from_id === originId) return null
                      const p = familyById[s.derived_from_id]
                      if (!p) return null
                      const whoRaw = p.author_name || (PLATFORM_LABEL[platformKey(p.source)] || p.source)
                      const who = p.author_name === agentName ? t('desk_lineage_your_reposts_section') : whoRaw
                      return (
                        <span className={`${COUNTER} ml-1`}
                          title={tLog('desk_lineage_reshared_from', { who: `${whoRaw}${p.source ? ` · ${p.source}` : ''}` })}>
                          ↳ {who}
                        </span>
                      )
                    }
                    // priceDeltaToken/deltaInfo hoisted above the CoverageStrip (§11) — the
                    // anchor row and group headers share them now.
                    // §4i ONE-LINE RULE (designer 2026-07-23): one posting = one row —
                    // platform chip · freshness · current price + SINGLE delta vs anchor ·
                    // status · chevron. Everything else (address/facts diffs, reshared-from,
                    // captured-by, open/edit links) lives BEHIND the chevron: four-plus
                    // numbers on one row buried the delta and let outliers shout. Agent
                    // identity stays on the SECTION header (2026-07-09 owner ruling), not
                    // repeated per row.
                    const togglePosting = key => setOpenPostings(prev => {
                      const next = new Set(prev); next.has(key) ? next.delete(key) : next.add(key); return next
                    })
                    const renderSibling = (s, ref) => { const edit = s.author_is_agent === false ? null : editUrlFor(s.source, s.source_url); const open = openPostings.has(s.id); return (
                      <div key={s.id} className="border-b border-border/40 last:border-0">
                        <div className="flex items-center justify-between gap-2 text-sm py-1 cursor-pointer rounded hover:bg-accent/50"
                          onClick={() => togglePosting(s.id)}>
                          <span className="font-medium min-w-0 truncate"><PlatformPill source={s.source} />{' '}
                            <span className="text-muted-foreground font-normal" title={s.listed_at ? fmtTbilisi(s.listed_at) : undefined}>
                              {/* A family row with NO derived_from edge is an ORIGIN ad (შოთა case,
                                  2026-07-12) — label it scraped; only real lineage edges say რეპოსტი. */}
                              {s.derived_from_id
                                ? (s.listed_at && tLog('desk_entry_reposted', { datetime: timeAgo(s.listed_at) }))
                                : <><IconText>{t('desk_lineage_scraped_badge')}</IconText>{s.listed_at && <> · {timeAgo(s.listed_at)}</>}</>}
                            </span>
                          </span>
                          <span className="flex items-center gap-2 shrink-0">
                            {/* One FB row = a whole posting spree — say how far it reached.
                                The row's link opens the canonical post (our own group when
                                we have one); the rest are findable on FB by the #id hashtag. */}
                            {(() => { const fs = platformKey(s.source) === 'facebook' ? fbStats?.get(s.id) : null
                              return fs ? <span className="text-xs text-muted-foreground whitespace-nowrap">
                                {fs.post_count} {t('desk_lineage_fb_posts')}{fs.group_count > 0 && <> · {fs.group_count} {t('desk_lineage_fb_groups')}</>}
                              </span> : null })()}
                            {s.price != null && <span className="text-muted-foreground text-xs whitespace-nowrap"><Money amount={s.price} currency={s.currency} />{priceDeltaToken(s, ref)}</span>}
                            {(() => { const lb = lifecycleBadge(s); if (lb) return lb
                              const ab = listingAgeBadge(s); return ab
                              ? <Badge label={ab.label} variant={ab.variant} title={ab.title} />
                              : <Badge label={s.status === 'active' ? t('desk_status_on_market') : t('desk_status_off_market')} variant={s.status === 'active' ? 'green' : 'default'} /> })()}
                            <span className="text-muted-foreground/60 text-xs">{open ? '▴' : '▾'}</span>
                          </span>
                        </div>
                        {/* BLOCKED by the portal (owner 2026-10-29): the portal's own reason, and whether our PC removed it */}
                        {blockedAds?.get(s.id) && (() => { const b = blockedAds.get(s.id); return (
                          <p className="pl-3 pb-1 text-xs text-red-600">
                            🔒 {tLog('desk_blocked_line', { portal: PLATFORM_LABEL[b.platform] || b.platform })} {b.reason || '—'}
                            {b.deleted_at && <span className="text-muted-foreground"> · {t('desk_blocked_deleted')}</span>}
                          </p>) })()}
                        {open && (
                          <div className="pl-3 pb-1.5 text-xs space-y-0.5">
                            {resharedFromToken(s)}
                            <CapturedByToken row={s} />
                            <AddressDiffLine value={s.posted_address || merged.address} refValue={ref?.address} />
                            {/* This reshare's OWN facts (0041): its scraped rooms/area/floor can
                                genuinely differ, vs its OWN intent's reference row (2026-07-11);
                                §11 collapses disagreements into one explicit sentence. */}
                            <FactsDifferSentence m={s} refM={ref} />
                            <span className="flex items-center gap-3">
                              <button type="button" className="text-primary" onClick={() => setView({ id: s.id, isOwn: true, mine: false })}>{t('desk_lineage_view_posting_tooltip')}</button>
                              {s.source_url && <a href={s.source_url} target="_blank" rel="noreferrer" className="text-primary"><IconText>{t('desk_lineage_open_link')}</IconText></a>}
                              {edit && <a href={edit} target="_blank" rel="noreferrer" className="text-primary"><IconText>{t('desk_lineage_edit_link')}</IconText></a>}
                              {/* AD REMOVER (owner 2026-10-23 "From here manager and admin must have an option to send a delete request.
                                  Agent may send request for their own listings"): the PC of the agent who posted it deletes it from their
                                  own portal account; the server checks the same rule (managers / admins: any of ours; an agent: own). */}
                              {edit && s.status === 'active' && /ss\.ge|myhome|korter/i.test(s.source || '') && (privileged || (agentName && s.author_name === agentName)) && (
                                rmSent.has(s.id)
                                  ? <span className="text-muted-foreground">{t('desk_adrm_requested')}</span>
                                  : <button type="button" className="text-red-600" onClick={async () => {
                                      const why = await askRemovalReason(1); if (!why) return   // owner 2026-10-28: every removal says why
                                      try { const r = await requestAdTakedown([s.id], why.reason, why.note); const x = r?.results?.[0]
                                        if (x && !x.ok) window.alert(x.why); else setRmSent(v => new Set(v).add(s.id)) }
                                      catch (e) { window.alert(e.message) }
                                    }}>{t('desk_adrm_request')}</button>
                              )}
                            </span>
                          </div>
                        )}
                      </div>
                    ) }
                    // ALWAYS grouped into one section per reposting agent (2026-07-09 — was a
                    // flat list with an inline name when only one agent reshared; a property with
                    // many agents' reshares needs each agent's postings gathered under their own
                    // heading, not scattered). The section for the agent VIEWING this modal
                    // (agentName === author_name) gets no name in its header — "you already know
                    // these are yours" — everyone else's section is labeled with their name.
                    const perAgent = (sibs, ref, capScope = 'main') => {
                      const order = []
                      const byAgent = new Map()
                      sibs.forEach(s => {
                        const key = s.author_name || '—'
                        if (!byAgent.has(key)) { byAgent.set(key, []); order.push(key) }
                        byAgent.get(key).push(s)
                      })
                      // §4i v2 cap: long agent lists fold past 5 groups behind "+N კიდევ აგენტი".
                      // capScope (B577, 2026-08-17): the opened intent now renders in TWO calls
                      // (owner ads, then our reposts) against the SAME ref row, so the key must
                      // carry which call it belongs to — without it both sections shared one
                      // toggle and expanding either one expanded both.
                      const capKey = `agents:${capScope}:${ref?.id || 'main'}`
                      const capOpen = openPostings.has(capKey)
                      const shownNames = capOpen ? order : order.slice(0, 5)
                      const capBtn = !capOpen && order.length > 5 && (
                        <button key="__cap" type="button" className="text-xs text-primary py-0.5"
                          onClick={() => togglePosting(capKey)}>
                          {tLog('desk_lineage_more_agents', { n: order.length - 5 })}
                        </button>
                      )
                      return [...shownNames.map(name => {
                        const rows = byAgent.get(name)
                        // A bare platform-author name as a section header read like one of OUR
                        // people ("who is კახა?" — 2026-07-13, owner-caught). Non-agent authors
                        // are owner/advertiser identities from the platform ad — label the
                        // section as such instead of presenting them like roster agents.
                        // 🔴 A KNOWN AGENCY AGENT IS NOT AN OWNER (owner, 2026-09-07). This asked only
                        // author_is_agent, which means OUR ROSTER — so an outside agency agent fell
                        // into the owner branch and their post was headed "🏠 owner ad — <name>".
                        // author_agency is the dictionary answer (0204), kept separate from ours.
                        const isKnownAgent = rows.some(s => s.author_is_agent === true || s.author_agency)
                        const isOwnerGroup = !isKnownAgent && rows.some(s => s.author_is_agent === false)
                        const header = name === agentName ? t('desk_lineage_your_reposts_section')
                          // Masked/unknown owner author → no name to show: plain "owner ad",
                          // never "owner ad — —" (the dash placeholder leaked through).
                          : isOwnerGroup ? (name && name !== '—' ? tLog('desk_lineage_owner_section', { name }) : t('desk_lineage_owner_section_anon'))
                          // Somebody else's agency: named as an agent, with the agency when the
                          // dictionary carries one, so the heading says WHY it is not an owner ad.
                          : (() => { const ag = rows.map(r => r.author_agency).find(x => x && x !== true)
                              return ag ? tLog('desk_lineage_agency_section', { name: name || '—', agency: ag })
                                        : (name || t('desk_lineage_owner_section_anon')) })()
                        // §4i v2 (owner + designer 2026-07-23): GROUP BY PERSON, collapsed →
                        // expand. One row per agent: name · deduped platform pills (×count) ·
                        // post count · the FRESHEST posting's price + ONE delta vs anchor · age ·
                        // chevron. Expand reveals the per-platform one-line posting rows (each
                        // with its own detail chevron). Folds ~15 duplicated rows into ~6 — the
                        // repeats (same agent × 3 platforms echoing one price) were the noise.
                        const grpKey = `grp:${name}`
                        // §11: the VIEWER's own group opens by default ("your postings are
                        // today's work"); set-membership INVERTS the default so the existing
                        // togglePosting keeps working for both cases.
                        const defaultOpen = name === agentName
                        const grpOpen = openPostings.has(grpKey) ? !defaultOpen : defaultOpen
                        const freshest = rows.slice().sort((a, b) => new Date(b.listed_at || 0) - new Date(a.listed_at || 0))[0]
                        const plats = []
                        for (const s of rows) {
                          const k = platformKey(s.source) || s.source || '?'
                          const hit = plats.find(p => p.key === k)
                          if (hit) hit.count++; else plats.push({ key: k, source: s.source, count: 1 })
                        }
                        // §11 admin affordance: a small red dot on a COLLAPSED header when the
                        // group hides a real problem — price divergence beyond the threshold,
                        // or a stale (>30d) still-active posting. Privileged viewers only.
                        const di = deltaInfo(freshest, ref)
                        const staleDays = freshest?.listed_at ? (Date.now() - new Date(freshest.listed_at)) / 864e5 : null
                        const alarm = privileged && (di?.divergent
                          || (staleDays != null && staleDays > 30 && rows.some(r => r.status === 'active')))
                        return (
                          <div key={name}>
                            {/* §11 header, third pass (owner-caught live 2026-07-24): the NAME
                                is the row's identity and must never flex-collapse — it gets a
                                bounded truncate; the "latest" summary is the flexible part and
                                WRAPS to its own right-aligned line when Georgian runs long.
                                Compact S/M/K pills (full names in titles + the expand); the
                                "latest" word lives in the summary's tooltip, not the row. */}
                            <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-sm py-1 cursor-pointer rounded hover:bg-accent/50"
                              onClick={() => togglePosting(grpKey)}>
                              <span className="flex items-center gap-1.5 min-w-0">
                                <span className="text-xs font-semibold text-muted-foreground truncate max-w-[11rem]">{header}</span>
                                {alarm && !grpOpen && <span title={t('desk_lineage_admin_dot')} className="shrink-0 inline-block w-1.5 h-1.5 rounded-full bg-red-500" />}
                                <span className="shrink-0 inline-flex items-center gap-1">
                                  {plats.map(p => <PlatformPill key={p.key} source={p.source} count={p.count} compact />)}
                                </span>
                              </span>
                              <span className="ml-auto flex items-center gap-2 text-xs text-muted-foreground">
                                {/* Platform name dropped (owner 2026-07-24): the S/M/K pills
                                    already carry it; the summary is just freshness + price. */}
                                {freshest && <span className="whitespace-nowrap"
                                  title={`${t('desk_lineage_latest')} · ${PLATFORM_LABEL[platformKey(freshest.source)] || freshest.source}${freshest.listed_at ? ` · ${fmtTbilisi(freshest.listed_at)}` : ''}`}>
                                  {freshest.listed_at && timeAgo(freshest.listed_at)}
                                  {freshest.price != null && <> · <Money amount={freshest.price} currency={freshest.currency} />{priceDeltaToken(freshest, ref)}</>}
                                </span>}
                                {rows.length > 1 && !grpOpen && <span className="whitespace-nowrap text-muted-foreground/70">{tLog('desk_lineage_older_postings', { n: rows.length - 1 })}</span>}
                                <span className="text-muted-foreground/60">{grpOpen ? '▴' : '▾'}</span>
                              </span>
                            </div>
                            {grpOpen && <div className="pl-2 border-l border-border/40">{rows.map(s => renderSibling(s, ref))}</div>}
                          </div>
                        )
                      }), capBtn]
                    }
                    // Intent grouping (2026-07-11): one physical unit can be posted for BOTH rent
                    // AND sale (two resolve_property rows the resolver same_as-linked, 0063) — the
                    // server now returns the whole family, so a rent modal also carries the sale
                    // postings. Split by intent: the OPENED intent's reshares flow right under the
                    // opened + origin rows above (no extra header — they're already that intent);
                    // every OTHER intent gets a labeled divider and its OWN reference row (its
                    // oldest posting), so its facts/price aren't diffed against the wrong intent.
                    // The server's siblings list is meant to hold only OTHER postings, but the
                    // 2026-07-11 "whole family" widening (comment above) stopped excluding the
                    // origin/opened-listing row itself — without this filter the same source ad
                    // rendered twice: once here (line ~6030) and again inside perAgent's owner
                    // section below (2026-07-16, owner-caught: "we have twice, same link").
                    const byIntent = new Map()
                    siblings.filter(s => s.id !== originId && s.id !== listing.listing_id)
                      .forEach(s => { const k = s.intent || listing.intent || '—'; if (!byIntent.has(k)) byIntent.set(k, []); byIntent.get(k).push(s) })
                    const openedIntent = listing.intent || '—'
                    const otherIntents = [...byIntent.keys()].filter(i => i !== openedIntent)
                    const oldestOf = sibs => sibs.slice().sort((a, b) => new Date(a.listed_at || 0) - new Date(b.listed_at || 0))[0] || null
                    // B577 (owner-reported 2026-08-17): the "our reposts · N agents" header was
                    // printed over EVERY sibling group of the opened intent, so a flat nobody on
                    // our side had touched — the owner's own ss.ge + myhome + korter ads — was
                    // announced as ours, and `n` counted distinct AUTHOR NAMES whether or not the
                    // author was one of our agents. perAgent already knew the difference one level
                    // down (isOwnerGroup → desk_lineage_owner_section, "🏠 owner ad"), so the row
                    // said "owner ad" while the heading above it claimed the opposite. A heading is
                    // an ASSERTION about the list under it and must be computed from the same rule
                    // (B273's disease, one layer up; PHILOSOPHY's promises-must-match-reality).
                    // ⚠️ Owner ads render FIRST and with NO section header of their own: each group
                    // header already names itself "🏠 owner ad — <name>", and a bare section after
                    // a headed one would read as still belonging to it. Do NOT "fix" this instead
                    // by softening the header to wording true of both kinds — which ads are OURS is
                    // the question the agent opened this card to answer.
                    // `author_is_agent` is a strict boolean from the server (server.js wraps it in
                    // `!!`), so there is no third unknown state to bucket.
                    const openedSibs = byIntent.get(openedIntent) || []
                    const ourSibs = openedSibs.filter(s => s.author_is_agent !== false)
                    const ownerSibs = openedSibs.filter(s => s.author_is_agent === false)
                    return (
                      <>
                        {ownerSibs.length > 0 && perAgent(ownerSibs, refRow, 'owner')}
                        {/* §11: "our reposts · N agents" — the count that used to overflow a
                            tooltip; the named groups themselves are the list right below. */}
                        {ourSibs.length > 0 && (() => {
                          // AD REMOVER "remove all" (owner 2026-10-23 "we need remove all at once button too"): every live
                          // repost of ours listed here, same rule as the per-row button - managers / admins only
                          const rmIds = privileged ? ourSibs.filter(s => s.status === 'active' && s.author_is_agent !== false
                            && /ss\.ge|myhome|korter/i.test(s.source || '') && !rmSent.has(s.id)).map(s => s.id) : []
                          return (
                            <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground pt-2">
                              <span>{tLog('desk_lineage_our_reposts', { n: new Set(ourSibs.map(s => s.author_name || '—')).size })}</span>
                              {rmIds.length > 0 && (
                                <button type="button" className="ml-auto normal-case tracking-normal font-normal text-red-600" onClick={async () => {
                                  const why = await askRemovalReason(rmIds.length); if (!why) return
                                  try { const r = await requestAdTakedown(rmIds, why.reason, why.note)
                                    const bad = (r?.results || []).filter(x => !x.ok)
                                    setRmSent(v => { const n = new Set(v); (r?.results || []).filter(x => x.ok).forEach(x => n.add(x.listing_id)); return n })
                                    if (bad.length) window.alert(bad.map(x => x.why).join('\n')) }
                                  catch (e) { window.alert(e.message) }
                                }}>{t('desk_adrm_remove_all')} ({rmIds.length})</button>
                              )}
                            </p>
                          )
                        })()}
                        {ourSibs.length > 0 && perAgent(ourSibs, refRow, 'ours')}
                        {otherIntents.map(i => (
                          <div key={i}>
                            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground pt-3 mt-1 border-t border-border/40">
                              {intentLabel(i)}
                            </p>
                            {perAgent(byIntent.get(i), oldestOf(byIntent.get(i)), `intent:${i}`)}
                          </div>
                        ))}
                      </>
                    )
                  })()}
                </div>
              </div>
              )
            })()}
            </div>
            </div>
            {/* ── FULL WIDTH below the columns: matching demands span both concerns. ── */}
            {/* Scorer suggestions: active client demands this property could fit — the
                supply-side mirror of the client modal's matching-properties card. In the
                shop window this is server-scoped to the VIEWER's own clients (decision 3):
                "your reasons to cooperate". */}
            {/* B568 (2026-08-08): the property side now renders the v2 MATRIX — conditions ×
                clients, one row per client, expandable into the full evidence card. Same
                endpoint, same checks, same strip and same five row actions as the card list
                it replaces; what changed is the shape, because with 20 clients and 21
                distinct blockers a stack of cards cannot answer "who is closest, and to
                what". MatchSuggestions still owns the CLIENT direction (kind="requirement"),
                which is a different question and keeps its card list.
                ⚠️ This panel draws NO footer — it is a section inside this modal, and the
                footer below is this modal's own. */}
            {listing.property_id && (
              <MatchMatrix propertyId={listing.property_id} onOpenDemand={onOpenDemand}
                listingId={listing.listing_id || listingId} agentId={agentId} canRecord={!notRep}
                refreshKey={termsRefresh} address={merged.address || null}
                completeness={{ label: t('desk_completeness_property'), ...listingCompleteness(listing) }} />
            )}
            {/* Every live price conversation about this property (MEETINGS_MODEL
                §11.4 — the same threads the client modal shows from the other
                direction). Renders nothing when there are none; a thread is
                STARTED from a match row above, not here, because opening one
                needs both sides and this modal knows only the property. */}
            {listing.property_id && (
              <NegotiationsPanel propertyId={listing.property_id}
                propertyLabel={merged.address || null} className="mt-4" />
            )}
            {/* Everything this property has been observed to do, newest first. Admin-only —
                see the component header for why the gate is a masking boundary, not a flag. */}
            {/* T27/§8 — ONE placement, and the condition is the lesson. It was two: beside the
                private notes for a representative, and again for a non-representative under
                management. Opened on a real card as ADMIN, NEITHER fired — the first needs
                `agentId` (an admin has no roster row, §7 OPEN-17, so it is null) and the second
                needed `notRep`, which was false. Two conditions, both plausible, and the union of
                them still missed the viewer the management half exists for.
                ⚠️ `showSelf` is separate from `canManage` on purpose: telling a viewer who does not
                represent this flat "you can be listed as a contact" is a claim that may be false,
                and a control over it would be diagnosis rule 6's lock with nothing behind it. */}
            {/* T37 (0838): the owner's notes to whoever works this flat next. Admin only — the
                panel itself returns null for anyone else, so there is no empty section and no dead
                control; the server refuses them as well. */}
            {listing.property_id && isAdmin && (
              <AdminCommentsPanel propertyId={listing.property_id} isAdmin />
            )}
            {listing.property_id && (agentId || privileged) && (
              <ContactOptOutPanel propertyId={listing.property_id} agentId={agentId}
                showSelf={!notRep && !!agentId} canManage={privileged} />
            )}
            {/* AD REMOVER (owner 2026-10-23): managers / admins delete our agents' reposts of this flat from the portals */}
            {privileged && listing.property_id && <AdRemoverPanel propertyId={listing.property_id} />}
            {privileged && (
              <PropertyHistoryBand
                propertyIds={[...new Set([listing.property_id, origin?.property_id,
                  ...siblings.map(s => s.property_id)].filter(Boolean))]}
                refreshKey={termsRefresh} />
            )}
            {/* Shop window closes with the viewer's own notebook — the one writable thing
                on another agent's card ("called the rep 7/14, waiting on price"). */}
            {notRep && agentId && listing.property_id && (
              <PrivateNotesPanel propertyId={listing.property_id} agentId={agentId} />
            )}
            {/* §8 OPEN-22 on a card the viewer does NOT represent — management only.
                ⚠️ The personal half is deliberately absent here. Telling a non-representative
                "you can be listed as a contact for this property" is a claim that may simply be
                false, and offering them a control over it is diagnosis rule 6's lock with nothing
                behind it. Management, by contrast, does the management job on any flat — an agent
                who has left is exactly the case, and their card is one nobody represents. */}
            {/* Locked to the bottom of the modal's scroll area (was inline, so it scrolled away
                on long listings) — negative margin cancels the modal's own p-5 so it spans edge
                to edge like the modal's own sticky title bar. */}
            {/* Above the sticky bar rather than inside it: the footer is a single flex row of
                fixed-width verbs, and a 200px panel in it would push the actions off the
                right edge on a phone. */}
            {sharePick && (
              <div className="mt-2">
                {shareErr && <p className="text-xs text-destructive mb-1">{shareErr}</p>}
                <AgentPicker agents={agents} busy={shareBusy}
                  exclude={(propReps || []).map(r => r.agent_id).filter(Boolean)}
                  note={t('desk_property_share_note')}
                  onCommit={async list => {
                    setShareBusy(true); setShareErr(null); setShareOk(null)
                    const failed = []
                    for (const a of list) {
                      try { await sharePropertyWith(listing.property_id, { toAgentId: a.id }) }
                      catch { failed.push(a.name || a.id) }
                    }
                    // Never stop at the first failure — with several picked, aborting leaves a
                    // half-shared flat behind an error naming only the one that broke.
                    if (failed.length) {
                      setShareErr(tLog('desk_client_share_partial',
                        { ok: list.length - failed.length, bad: failed.length, names: failed.join(', ') }))
                    } else { setShareOk(list.length); setSharePick(false) }
                    setShareBusy(false)
                    getPropertyRepresentatives(listing.property_id).then(d => setPropReps(d.representatives || [])).catch(() => {})
                  }}
                  onCancel={() => { setSharePick(false); setShareErr(null) }} />
              </div>
            )}
            {shareOk ? (
              <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-1">
                {tLog('desk_property_shared_n', { n: shareOk })}
              </p>
            ) : null}
            <div className="sticky bottom-0 -mx-5 -mb-5 px-5 py-2 mt-2 bg-card border-t border-border flex justify-between items-center gap-2">
              {/* Left group = the "reach someone" verbs, same shape PersonModal already uses:
                  contact, then share. Kept as one span so the actions on the right stay
                  pinned regardless of how many verbs land here later. */}
              <span className="flex items-center gap-3 min-w-0">
              <ContactPill
                contacts={pcontacts?.contacts?.length ? pcontacts.contacts.filter(c => c.phone)
                  : (listing.author_phone ? [{ party_id: 'author', name: listing.author_name, phone: listing.author_phone, role: 'agent listing author' }] : [])}
                defaultPartyId={pcontacts?.default_party_id || null}
                // `listing` (not just address) — the modal is re-anchorable to any one posting
                // (POSTED ON rows), so whichever one is currently in view is exactly "which
                // version got this interest" (2026-07-10); the log should say so, not just the
                // shared address every posting has in common.
                listing={listing} label={t('desk_coop_chip_default')}
                /* 0376: the modal's 📞 says exactly what the card's does — same read, same
                   badge, and the popover inside it carries the same question. */
                agentMark={authorRoleMark}
                propertyId={listing.property_id} onAdded={() => reloadContacts(listing.property_id, { force: true })} />
              {/* Moved here from the subtitle line (see the Modal's subtitle comment), and it
                  is the SAME ShareMenu the card footer's rail renders — one share affordance
                  across every surface (owner ruling 2026-07-25), not a bare 🔗 here and a menu
                  there. view.id still follows the ANCHORED posting, so re-anchoring the modal
                  to a POSTED-ON row keeps sharing the version you are actually looking at. */}
              <ShareMenu
                /* Only where there is a flat to share and a roster to share it with — the
                   menu row is omitted, never disabled, when either is missing. */
                onShareOthers={listing.property_id && agents.length ? () => {
                  setSharePick(true); setShareOk(null); setShareErr(null)
                  getPropertyRepresentatives(listing.property_id)
                    .then(d => setPropReps(d.representatives || []))
                    .catch(() => setPropReps([]))   // unknown → exclude nobody, never block the share
                } : null}
                onRepost={/* ⚠️ Seed the OTHER two tabs, like every other door into this editor.
                    This one set repostOpen + the tab and nothing else, so `coop` and `terms`
                    reached DealEditor as null: switching away from გაზიარება showed only the
                    "no cooperation" / "no conditions" fallbacks, on properties that had both.
                    IntakeTab's repost door had the identical bug and was fixed 2026-07-31;
                    this one was missed because it hangs off ShareMenu rather than sitting
                    beside the other two. repostOpen stays — it is the render gate for when
                    defaultTermsFor() has nothing to hand over. */
                  listing.listing_id
                    ? () => { openDealEditor('share'); setRepostOpen(true) }
                    : null} />
              {/* 🔗 links already sent to clients — the SAME chip the card footer's rail
                  renders (ShareChip, exported from CooperationChips), in the same place
                  relative to ↗ and for the same reason: it answers the question ↗ raises,
                  "have I already sent this, and to whom?". Owner-reported 2026-08-04: the
                  card had it and the modal you open FROM that card did not, so the answer
                  disappeared exactly when an agent had drilled in to look for it.
                  Renders nothing until the fetch lands — never a false "none sent". */}
              <ShareChip shares={shares} listingId={view.id}
                location={shareableLocation(hMerged?.lat, hMerged?.lng, data?.viewer_is_representative === true)}
                onShare={onShare && listing.property_id ? () => onShare({
                propertyId: listing.property_id,
                propertyLabel: hMerged?.address || null,
                seedPrice: listing.price ?? null,
                seedCurrency: listing.currency || null,
              }) : null} />
              {/* 💬 the team's thread about THIS flat (messaging phase 2): one thread per flat, whichever row the
                  agent opened it from — the server keys it on the cluster. Any active agent may read and write it. */}
              <DiscussWithTeamButton propertyId={listing.property_id || null} />
              </span>
              {/* ✎ Edit → the unified editable property workspace (ManualPropertyModal in
                  existing mode). This modal is the info surface; changing facts is an explicit
                  second step (2026-07-09 — one click opens info, Edit opens the editor). */}
              <span className="flex items-center gap-2">
                {/* Fact editing is REPRESENTATIVE work (2026-07-14) — a non-rep's surface
                    is read-only + their own private notes; changes go through the rep. */}
                {onEditProperty && listing.property_id && !notRep && (
                  <button className={`${btn('primary')} whitespace-nowrap`} title={t('desk_lineage_edit_property_button')}
                    onClick={() => onEditProperty({ propertyId: listing.property_id, listingId: listing.listing_id || listingId })}>
                    <BtnLabel icon="pencil" label={t('desk_lineage_edit_property_button')} />
                  </button>
                )}
                <button className={`${btn('ghost')} whitespace-nowrap`} onClick={onClose}
                  title={t('desk_lineage_close_button')}><BtnLabel icon="x" label={t('desk_lineage_close_button')} /></button>
              </span>
            </div>
          </div>
        )
      })()}
      {/* Combined deal editor (owner 2026-07-29/30) — shared component, same editor the
          card-face chips open (DealEditor.jsx owns the chrome + tab logic).
          `share` = the repost tab (B244). Offered on any card with a listing — no cooperation
          required (owner ruling 2026-07-31: "let agent repost without editing anything").
          The lead-suppression gate is unaffected: it keys on property_id, and a repost creates
          a new listing on the SAME property, so the reshare inherits the suppression. */}
      {(coopModal || termsEdit || repostOpen || valuationOpen) && (
        <DealEditor coop={coopModal} propertyId={data?.listing?.property_id || null}
          terms={termsEdit ? { listingId: termsEdit.listingId, mode: termsEdit.mode, propertyId: data?.listing?.property_id || null } : null}
          share={/* build from the ORIGIN when the anchored row is a reshare — its raw rows
                    carry no images (the post-publish ingest sends none). See ShareTab. */
            data?.listing?.listing_id
              ? { listingId: data.listing.listing_id, originListingId: data?.origin?.listing_id || null }
              : null}
          initialTab={dealTab} onTabChange={setDealTab} agentId={agentId}
          sourceUrl={data?.listing?.source_url || null}
          authorListing={data?.listing || null}
          onCoopSaved={() => reloadCoops(data?.listing?.property_id, (data?.siblings || []).map(s => s.property_id), { force: true })}
          onTermsRecorded={() => setTermsRefresh(n => n + 1)}
          onClose={() => { setCoopModal(null); setTermsEdit(null); setRepostOpen(false); setValuationOpen(false); setTermsRefresh(n => n + 1) }} />
      )}
    </Modal>
  )
}

// ── Exports — everything IntakeTab.jsx still references ──
export { locDistM, NEAR_SEARCH_RADIUS_M, lineageFieldRow, ListingLineageModal }
